import * as client_request from './alan_api';
import * as application from '../application/alan_api';

type SymbolsFile = [string, SymbolsList];
type SymbolsList = Array<[string, string, string, [number, number, number], [number, number, number]]>;

const debug = false;
function isFunction(p: any): p is Function {
	return (typeof p === 'function');
}
function assert(predicate:boolean) {
	if (predicate === false) throw new Error(`Assertion error`);
}
function resolve<T>(context:T) {
	return {
		then: <RT>(callback: (context: T) => RT) => resolve(callback(context)),
		result: context
	};
}
export type dictionary_type<T> = {[key:string]:T};


enum ResolutionStatus {
	Resolved,
	Resolving,
	Unresolved,
	Detached,
}
function cache<T extends IAlanObject>(callback:(detach:boolean) => T) {
	let cached_value:T;
	let status:ResolutionStatus = ResolutionStatus.Unresolved;
	return (detach = false) => {
		switch (status) {
			case ResolutionStatus.Resolving:
				throw new Error(`Cyclic dependency detected!`);
			case ResolutionStatus.Detached: {
				if (detach) break;
				(cached_value as any) = undefined;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
			case ResolutionStatus.Resolved: {
				if (!detach) break;
				callback(detach);
				status = ResolutionStatus.Detached;
			} break;
			case ResolutionStatus.Unresolved: {
				if (detach) break;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
		}
		return cached_value;
	}
}

function maybe_cache<T extends IAlanObject>(callback:(detach:boolean) => T|undefined) {
	let cached_value:T|undefined;
	let status:ResolutionStatus = ResolutionStatus.Unresolved;
	return (detach = false) => {
		switch (status) {
			case ResolutionStatus.Resolving:
				throw new Error(`Cyclic dependency detected!`);
			case ResolutionStatus.Detached: {
				if (detach) break;
				cached_value = undefined;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
			case ResolutionStatus.Resolved: {
				if (!detach) break;
				callback(detach);
				status = ResolutionStatus.Detached;
			} break;
			case ResolutionStatus.Unresolved: {
				if (detach) break;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
		}
		return cached_value;
	}
}

/* number validation */
function number__is_positive(val:number) { assert(val > 0); }
function number__is_negative(val:number) { assert(val < 0); }
function number__is_zero(val:number) { assert(val === 0); }
function number__is_non_positive(val:number) { assert(val <= 0); }
function number__is_non_negative(val:number) { assert(val >= 0); }
function number__is_non_zero(val:number) { assert(val !== 0); }

/* complex value wrappers */
export abstract class AlanObject {
	public abstract get path(): string;
	private _destroyed?: true;
	get destroyed(): boolean {
		return this._destroyed ? true : false;
	}
}
export interface IAlanObject {
	readonly path: string;
	readonly destroyed: boolean;
};

export abstract class AlanStruct extends AlanObject {
	public abstract is(other:AlanStruct):boolean;
}
export interface Tree<T> {
	types: {[name:string]:T};
	subtrees: {[name:string]:Tree<T>};
}
export abstract class Reference<T extends IAlanObject, V extends (string|string[])> extends AlanObject {
	constructor(public readonly entry:V, private readonly resolve:() => T, protected readonly parent: AlanNode) { super(); }
	public get ref() { return this.resolve(); }

}
export abstract class AlanInteger extends AlanObject {
	constructor(public readonly value:number, protected readonly parent: AlanNode) { super(); }

}
export abstract class AlanDictionary<T extends {node: AlanDictionaryEntry, init: any }, P extends AlanNode> extends AlanObject {
	protected _entries:Map<string, () => T['node']>;

	constructor (entries:{[key:string]:T['init']}, protected parent:P) {
		super();
		this._entries = new Map(Object.keys(entries).map(entry_key => [entry_key, () => {
			const loaded_entry = this.initialize(this.parent, entry_key, entries[entry_key])
			this._entries.set(entry_key, () => loaded_entry);
			return loaded_entry;
		}]));
	}

	protected abstract initialize(parent: P, key: string, obj: T['init']): T['node'];
	protected abstract finalize(obj: T['node'], detach?: boolean): void;

	get size() { return this._entries.size; }
	[Symbol.iterator](): IterableIterator<[string, T['node']]> {
		const iterator = this._entries.entries();
		return {
			next() {
				const next = iterator.next();
				if (next.done) {
					return next;
				} else {
					return {
						value: [next.value[0], next.value[1]()]
					}
				}
			},
			[Symbol.iterator]() {
				return this;
			}
		};
	}
	entries(): IterableIterator<[string, T['node']]> {
		return this[Symbol.iterator]();
	}
	values(): IterableIterator<T['node']> {
		const iterator = this._entries.values();
		return {
			next() {
				const next = iterator.next();
				if (next.done) {
					return next;
				} else {
					return {
						value: next.value()
					}
				}
			},
			[Symbol.iterator]() {
				return this;
			}
		};
	}
	keys(): IterableIterator<string> {
		return this._entries.keys();
	}
	forEach(walk_function: ($:T['node']) => void) {
		for (const entry of this.values()) {
			walk_function(entry);
		}
	}
	toArray(): [string, T['node']][] {
		return Array.from(this.entries());
	}
	map<RT>(callback: (value: T['node']) => RT): Record<string, RT> {
		const result:Record<string, RT> = {};
		for (const [key, entry] of this.entries()) {
			result[key] = callback(entry);
		}
		return result;
	}
	get(key:string): T['node'] | undefined {
		return this._entries.get(key)?.();
	}
	has(key: string): boolean { return this._entries.has(key); }
	switchOnEntryExists<RT>(key: string, onExists: (($: T['node']) => RT) | Exclude<RT, Function>, onNotExists: (() => RT) | Exclude<RT, Function>): RT {
		const entry = this._entries.get(key);
		if (entry === undefined) {
			return isFunction(onNotExists) ? onNotExists() : onNotExists;
		} else {
			return isFunction(onExists) ? onExists(entry()) : onExists;
		}
	}
}

export abstract class AlanSet<T extends {node: AlanNode, init: any }, P extends AlanNode> extends AlanObject {
	private _entries:Set<T['node']>;
	constructor (
		entries:Array<T['init']>,
		protected parent:P) {
		super();
		this._entries = new Set(entries.map(entry => this.initialize(parent, entry)));
	}

	protected abstract initialize(parent:P, obj:T['init']):T['node'];
	protected abstract finalize(obj:T['node'],detach?:boolean):void;

	get size() { return this._entries.size; }
	[Symbol.iterator]() {
		return this._entries[Symbol.iterator]();
	}
	entries() {
		return this._entries.entries();
	}
	forEach(walk_function: ($:T['node']) => void) {
		this._entries.forEach(walk_function);
	}
	has(key:T['node']):boolean { return this._entries.has(key); }
}
type DistributiveOmit<T, K extends keyof any> = T extends any ? Omit<T, K> : never;
export abstract class StateGroup<T extends {name:string, node:AlanNode & {parent:AlanNode}, init:any}> extends AlanObject {
	public state: DistributiveOmit<T,'init'>;
	public readonly root:Cclient_request
	private init(state_name:T['name'], init:T['init'], parent:AlanNode) {
		this.state = {
			name: state_name,
			node: this.initializer(state_name)(init, parent),
		} as DistributiveOmit<T,'init'>;
	}
	constructor (s:[T['name'],T['init']]|T['name'], protected readonly parent: AlanNode, readonly name: string) {
		super();
		this.root = parent.root
		const state_name:T['name'] = (typeof s === 'string') ? s : s[0];
		this.init(state_name, typeof s === 'string' ? {} : s[1], parent);
	}

	protected abstract initializer(state_name:T['name']): ($:T['init'], parent:AlanNode) => T['node'];
	protected abstract finalizer(state:T['name']): ($:T['node'], detach?:boolean) => void;

	switch<TS> (cases:{[K in T['name']]:(($:Extract<T, {name:K}>['node']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		const handler = cases[this.state.name as T['name']];
		if (isFunction(handler)) {
			return handler(this.state.node);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	cast<S extends T['name']>(state:S):Extract<T, {name:S}>['node'] {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		if (this.state.name === state) {
			return this.state.node;
		} else {
			throw new Error(`Invalid cast to state '${state}'; actual state is '${this.state.name}' at ${this.state.node.path} .`);
		}
	}
}

export abstract class AlanCombinator extends AlanObject {}
export abstract class AlanNode extends AlanStruct {
	protected _root:Cclient_request|undefined;
	public abstract get root():Cclient_request;
	public abstract get entity():AlanNode;
	public is(other:AlanNode):boolean {
		return this === other;
	}
}

export abstract class AlanDictionaryEntry extends AlanNode {
	public abstract key:(string|Reference<IAlanObject,string>);
	public abstract get key_value():string;
};
export abstract class AlanTopology<T extends { node:AlanDictionaryEntry, init:any }, P extends AlanNode, G extends string> extends AlanDictionary<T,P> {
	protected abstract graph_iterator(key:string):(entry:T['node']) => T['node'];
	private findFirst(iterator:(entry:T['node']) => T['node']) {
		const key_of = (entry:T['node']) => typeof entry.key === 'string' ? entry.key : entry.key.entry;
		let done:Record<string, boolean> = {};
		let first:T['node']|undefined = undefined;
		for (let [entry_id, current] of this) {
			if (!done[entry_id]) {
				first = current;
				while (current && !done[entry_id]) {
					done[entry_id] = true;
					current = iterator(current);
					if (current !== undefined) {
						entry_id = key_of(current);
					}
				}
			}
		}
		return first;
	}
	topo_entries(graph:G, first?:T['node']):IterableIterator<T['node']> {
		let iterator = this.graph_iterator(graph);
		let current = first || this.findFirst(iterator);
		return {
			next() {
				if (current !== undefined) {
					const entry = current;
					current = iterator(current);
					return {
						value: entry
					};
				} else {
					return {
						done: true,
						value: undefined
					}
				}
			},
			[Symbol.iterator]() {
				return this;
			}
		};
	}
	topo_forEach(graph:G, walk_function: ($:T['node']) => void, first?:T['node']) {
		for (let entry of this.topo_entries(graph, first)) { walk_function(entry); }
	}
	topo_toArray(graph:G, first?:T['node']):T['node'][] {
		return Array.from(this.topo_entries(graph, first));
	}
}

/* alan objects */
export type Ttext_expression = {
	'type':['concatenation', Tconcatenation]|['singular', Tsingular];
};
export class Ctext_expression extends AlanNode {
	public readonly properties:{
		readonly type:Ctext_expression.Dtype
	};
	constructor(init:Ttext_expression, public location:AlanNode, public input: {
		context_node: () => application.Cnode__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			type: new Ctext_expression.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tconcatenation = {
	'parts':Tsingular_text_expression_list;
};
export class Cconcatenation extends AlanNode {
	public readonly properties:{
		readonly parts:Csingular_text_expression_list
	};
	constructor(init:Tconcatenation, public parent:Ctext_expression) {
		super();
		const $this = this;
		this.properties = {
			parts: new Cconcatenation.Dparts(init['parts'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'concatenation'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsingular = {
	'expression':Tsingular_text_expression;
};
export class Csingular extends AlanNode {
	public readonly properties:{
		readonly expression:Csingular_text_expression
	};
	constructor(init:Tsingular, public parent:Ctext_expression) {
		super();
		const $this = this;
		this.properties = {
			expression: new Csingular.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'singular'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsingular_text_expression_list = {
	'has element':'no'|['no', {}]|['yes', Tyes__has_element__singular_text_expression_list];
};
export class Csingular_text_expression_list extends AlanNode {
	public readonly properties:{
		readonly has_element:Csingular_text_expression_list.Dhas_element
	};
	constructor(init:Tsingular_text_expression_list, public location:AlanNode, public input: {
		context_node: () => application.Cnode__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_element: new Csingular_text_expression_list.Dhas_element(init['has element'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_element__singular_text_expression_list = {
};
export class Cno__has_element__singular_text_expression_list extends AlanNode {
	constructor(init:Tno__has_element__singular_text_expression_list, public parent:Csingular_text_expression_list) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has element'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_element__singular_text_expression_list = {
	'singular text expression':Tsingular_text_expression;
	'tail':Tsingular_text_expression_list;
};
export class Cyes__has_element__singular_text_expression_list extends AlanNode {
	public readonly properties:{
		readonly singular_text_expression:Csingular_text_expression,
		readonly tail:Csingular_text_expression_list
	};
	constructor(init:Tyes__has_element__singular_text_expression_list, public parent:Csingular_text_expression_list) {
		super();
		const $this = this;
		this.properties = {
			singular_text_expression: new Cyes__has_element__singular_text_expression_list.Dsingular_text_expression(init['singular text expression'], $this),
			tail: new Cyes__has_element__singular_text_expression_list.Dtail(init['tail'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has element'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsingular_text_expression = {
	'type':['dynamic', Tdynamic]|['static', Tstatic];
};
export class Csingular_text_expression extends AlanNode {
	public readonly properties:{
		readonly type:Csingular_text_expression.Dtype
	};
	constructor(init:Tsingular_text_expression, public location:AlanNode, public input: {
		context_node: () => application.Cnode__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			type: new Csingular_text_expression.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tdynamic = {
	'path':Tnode_path;
	'text':string;
};
export class Cdynamic extends AlanNode {
	public readonly properties:{
		readonly path:Cnode_path,
		readonly text:Cdynamic.Dtext
	};
	constructor(init:Tdynamic, public parent:Csingular_text_expression) {
		super();
		const $this = this;
		this.properties = {
			path: new Cdynamic.Dpath(init['path'], $this),
			text: new Cdynamic.Dtext(init['text'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'dynamic'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstatic = {
	'string':string;
};
export class Cstatic extends AlanNode {
	public readonly properties:{
		readonly string:string
	};
	constructor(init:Tstatic, public parent:Csingular_text_expression) {
		super();
		const $this = this;
		this.properties = {
			string: init['string']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'static'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsigned_number_property_list = {
	'has element':'no'|['no', {}]|['yes', Tyes__has_element__signed_number_property_list];
};
export class Csigned_number_property_list extends AlanNode {
	public readonly properties:{
		readonly has_element:Csigned_number_property_list.Dhas_element
	};
	constructor(init:Tsigned_number_property_list, public location:AlanNode, public input: {
		context_node: () => application.Cnode__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_element: new Csigned_number_property_list.Dhas_element(init['has element'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_element__signed_number_property_list = {
};
export class Cno__has_element__signed_number_property_list extends AlanNode {
	constructor(init:Tno__has_element__signed_number_property_list, public parent:Csigned_number_property_list) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has element'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_element__signed_number_property_list = {
	'signed number property':Tsigned_number_property;
	'tail':Tsigned_number_property_list;
};
export class Cyes__has_element__signed_number_property_list extends AlanNode {
	public readonly properties:{
		readonly signed_number_property:Csigned_number_property,
		readonly tail:Csigned_number_property_list
	};
	constructor(init:Tyes__has_element__signed_number_property_list, public parent:Csigned_number_property_list) {
		super();
		const $this = this;
		this.properties = {
			signed_number_property: new Cyes__has_element__signed_number_property_list.Dsigned_number_property(init['signed number property'], $this),
			tail: new Cyes__has_element__signed_number_property_list.Dtail(init['tail'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has element'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsigned_number_property = {
	'path':Tnode_path;
	'property name':string;
	'sign':'negative'|['negative', {}]|'positive'|['positive', {}];
};
export class Csigned_number_property extends AlanNode {
	public readonly properties:{
		readonly path:Cnode_path,
		readonly property_name:Csigned_number_property.Dproperty_name,
		readonly sign:Csigned_number_property.Dsign
	};
	constructor(init:Tsigned_number_property, public location:AlanNode, public input: {
		context_node: () => application.Cnode__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			path: new Csigned_number_property.Dpath(init['path'], $this),
			property_name: new Csigned_number_property.Dproperty_name(init['property name'], $this),
			sign: new Csigned_number_property.Dsign(init['sign'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tnegative = {
};
export class Cnegative extends AlanNode {
	constructor(init:Tnegative, public parent:Csigned_number_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'sign'?'negative'`; }
	public get entity() { return this.parent.entity; }
}
export type Tpositive = {
};
export class Cpositive extends AlanNode {
	constructor(init:Tpositive, public parent:Csigned_number_property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'sign'?'positive'`; }
	public get entity() { return this.parent.entity; }
}
export type Treference_filter_list = {
	'has alternative':'no'|['no', {}]|['yes', Tyes__has_alternative];
	'referenced node':Tid_path__client_request;
};
export class Creference_filter_list extends AlanNode {
	public readonly properties:{
		readonly has_alternative:Creference_filter_list.Dhas_alternative,
		readonly referenced_node:Cid_path__client_request & { readonly inferences: {
			entity_constraint: () => application.Centity__application;
		} }
	};
	constructor(init:Treference_filter_list, public location:AlanNode, public input: {
		entity_node: () => application.Centity__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_alternative: new Creference_filter_list.Dhas_alternative(init['has alternative'], $this),
			referenced_node: new Creference_filter_list.Dreferenced_node(init['referenced node'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_alternative = {
};
export class Cno__has_alternative extends AlanNode {
	constructor(init:Tno__has_alternative, public parent:Creference_filter_list) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has alternative'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_alternative = {
	'alternative':Treference_filter_list;
};
export class Cyes__has_alternative extends AlanNode {
	public readonly properties:{
		readonly alternative:Creference_filter_list
	};
	constructor(init:Tyes__has_alternative, public parent:Creference_filter_list) {
		super();
		const $this = this;
		this.properties = {
			alternative: new Cyes__has_alternative.Dalternative(init['alternative'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has alternative'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber_filter_expression = {
	'operator':['equals', Tequals]|['range', Trange];
};
export class Cnumber_filter_expression extends AlanNode {
	public readonly properties:{
		readonly operator:Cnumber_filter_expression.Doperator
	};
	constructor(init:Tnumber_filter_expression, public location:AlanNode, private key: string) {
		super();
		const $this = this;
		this.properties = {
			operator: new Cnumber_filter_expression.Doperator(init['operator'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tequals = {
	'criterium':number;
};
export class Cequals extends AlanNode {
	public readonly properties:{
		readonly criterium:Cequals.Dcriterium
	};
	constructor(init:Tequals, public parent:Cnumber_filter_expression) {
		super();
		const $this = this;
		this.properties = {
			criterium: new Cequals.Dcriterium(init['criterium'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operator'?'equals'`; }
	public get entity() { return this.parent.entity; }
}
export type Trange = {
	'greater than':'no'|['no', {}]|['yes', Tyes__greater_than];
	'less than':'no'|['no', {}]|['yes', Tyes__less_than];
};
export class Crange extends AlanNode {
	public readonly properties:{
		readonly greater_than:Crange.Dgreater_than,
		readonly less_than:Crange.Dless_than
	};
	constructor(init:Trange, public parent:Cnumber_filter_expression) {
		super();
		const $this = this;
		this.properties = {
			greater_than: new Crange.Dgreater_than(init['greater than'], $this),
			less_than: new Crange.Dless_than(init['less than'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operator'?'range'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__greater_than = {
};
export class Cno__greater_than extends AlanNode {
	constructor(init:Tno__greater_than, public parent:Crange) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'greater than'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__greater_than = {
	'criterium':number;
};
export class Cyes__greater_than extends AlanNode {
	public readonly properties:{
		readonly criterium:Cyes__greater_than.Dcriterium
	};
	constructor(init:Tyes__greater_than, public parent:Crange) {
		super();
		const $this = this;
		this.properties = {
			criterium: new Cyes__greater_than.Dcriterium(init['criterium'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'greater than'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__less_than = {
};
export class Cno__less_than extends AlanNode {
	public readonly inferences:{
		must_be_greater_than: () => client_request.Cyes__greater_than
	} = {
		must_be_greater_than: cache((detach:boolean) => {
			const client_request__number_filter_expression__operator__range__less_than__no_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => {
					if (context?.properties.greater_than.state.name === 'yes') {
						return resolve(context.properties.greater_than.state.node as client_request.Cyes__greater_than).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tno__less_than, public parent:Crange) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'less than'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__less_than = {
	'criterium':number;
};
export class Cyes__less_than extends AlanNode {
	public readonly properties:{
		readonly criterium:Cyes__less_than.Dcriterium
	};
	constructor(init:Tyes__less_than, public parent:Crange) {
		super();
		const $this = this;
		this.properties = {
			criterium: new Cyes__less_than.Dcriterium(init['criterium'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'less than'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber_expression = {
	'type':['count', Tcount]|['division', Tdivision]|['maximum', Tmaximum]|['minimum', Tminimum]|['number property', Tnumber_property]|['product', Tproduct]|['remainder', Tremainder]|['state based', Tstate_based]|['sum', Tsum]|['sum list', Tsum_list]|['value', Tvalue];
};
export class Cnumber_expression extends AlanNode {
	public readonly properties:{
		readonly type:Cnumber_expression.Dtype
	};
	constructor(init:Tnumber_expression, public location:AlanNode, public input: {
		context_node: () => application.Cnode__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			type: new Cnumber_expression.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tcount = {
	'aggregate':Taggregate;
};
export class Ccount extends AlanNode {
	public readonly properties:{
		readonly aggregate:Caggregate
	};
	constructor(init:Tcount, public parent:Cnumber_expression) {
		super();
		const $this = this;
		this.properties = {
			aggregate: new Ccount.Daggregate(init['aggregate'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'count'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdivision = {
	'denominator':Tsigned_number_property;
	'numerator':Tsigned_number_property;
	'rounding':'ceil'|['ceil', {}]|'floor'|['floor', {}]|'ordinary'|['ordinary', {}];
};
export class Cdivision extends AlanNode {
	public readonly properties:{
		readonly denominator:Csigned_number_property,
		readonly numerator:Csigned_number_property,
		readonly rounding:Cdivision.Drounding
	};
	constructor(init:Tdivision, public parent:Cnumber_expression) {
		super();
		const $this = this;
		this.properties = {
			denominator: new Cdivision.Ddenominator(init['denominator'], $this),
			numerator: new Cdivision.Dnumerator(init['numerator'], $this),
			rounding: new Cdivision.Drounding(init['rounding'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'division'`; }
	public get entity() { return this.parent.entity; }
}
export type Tceil = {
};
export class Cceil extends AlanNode {
	constructor(init:Tceil, public parent:Cdivision) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'rounding'?'ceil'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfloor = {
};
export class Cfloor extends AlanNode {
	constructor(init:Tfloor, public parent:Cdivision) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'rounding'?'floor'`; }
	public get entity() { return this.parent.entity; }
}
export type Tordinary = {
};
export class Cordinary extends AlanNode {
	constructor(init:Tordinary, public parent:Cdivision) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'rounding'?'ordinary'`; }
	public get entity() { return this.parent.entity; }
}
export type Tmaximum = {
	'left expression':Tnumber_expression;
	'right expression':Tnumber_expression;
};
export class Cmaximum extends AlanNode {
	public readonly properties:{
		readonly left_expression:Cnumber_expression,
		readonly right_expression:Cnumber_expression
	};
	constructor(init:Tmaximum, public parent:Cnumber_expression) {
		super();
		const $this = this;
		this.properties = {
			left_expression: new Cmaximum.Dleft_expression(init['left expression'], $this),
			right_expression: new Cmaximum.Dright_expression(init['right expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'maximum'`; }
	public get entity() { return this.parent.entity; }
}
export type Tminimum = {
	'left expression':Tnumber_expression;
	'right expression':Tnumber_expression;
};
export class Cminimum extends AlanNode {
	public readonly properties:{
		readonly left_expression:Cnumber_expression,
		readonly right_expression:Cnumber_expression
	};
	constructor(init:Tminimum, public parent:Cnumber_expression) {
		super();
		const $this = this;
		this.properties = {
			left_expression: new Cminimum.Dleft_expression(init['left expression'], $this),
			right_expression: new Cminimum.Dright_expression(init['right expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'minimum'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber_property = {
	'signed number property':Tsigned_number_property;
};
export class Cnumber_property extends AlanNode {
	public readonly properties:{
		readonly signed_number_property:Csigned_number_property
	};
	constructor(init:Tnumber_property, public parent:Cnumber_expression) {
		super();
		const $this = this;
		this.properties = {
			signed_number_property: new Cnumber_property.Dsigned_number_property(init['signed number property'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number property'`; }
	public get entity() { return this.parent.entity; }
}
export type Tproduct = {
	'numbers':Tsigned_number_property_list;
};
export class Cproduct extends AlanNode {
	public readonly properties:{
		readonly numbers:Csigned_number_property_list
	};
	constructor(init:Tproduct, public parent:Cnumber_expression) {
		super();
		const $this = this;
		this.properties = {
			numbers: new Cproduct.Dnumbers(init['numbers'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'product'`; }
	public get entity() { return this.parent.entity; }
}
export type Tremainder = {
	'modulus':Tsigned_number_property;
	'number':Tsigned_number_property;
};
export class Cremainder extends AlanNode {
	public readonly properties:{
		readonly modulus:Csigned_number_property,
		readonly number:Csigned_number_property
	};
	constructor(init:Tremainder, public parent:Cnumber_expression) {
		super();
		const $this = this;
		this.properties = {
			modulus: new Cremainder.Dmodulus(init['modulus'], $this),
			number: new Cremainder.Dnumber(init['number'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'remainder'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_based = {
	'path':Tnode_path;
	'state group':string;
	'states':Record<string, Tstates__state_based>;
};
export class Cstate_based extends AlanNode {
	public readonly properties:{
		readonly path:Cnode_path,
		readonly state_group:Cstate_based.Dstate_group,
		readonly states:Cstate_based.Dstates
	};
	constructor(init:Tstate_based, public parent:Cnumber_expression) {
		super();
		const $this = this;
		this.properties = {
			path: new Cstate_based.Dpath(init['path'], $this),
			state_group: new Cstate_based.Dstate_group(init['state group'], $this),
			states: new Cstate_based.Dstates(init['states'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state based'`; }
	public get entity() { return this.parent.entity; }
}
export class Kstates__state_based extends Reference<application.Cstates__state_group, string> {
	constructor(key:string, $this:Cstates__state_based) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.properties.state_group?.ref)
			.then(context => {
				const entry = context?.properties.states.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'::key`; }
}
export type Tstates__state_based = {
	'value':Tnumber_expression;
};
export class Cstates__state_based extends AlanDictionaryEntry {
	public key:Kstates__state_based;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly value:Cnumber_expression
	};
	constructor(key:string, init:Tstates__state_based, public parent:Cstate_based) {
		super();
		const $this = this;
		this.key = new Kstates__state_based(key, $this);
		this.properties = {
			value: new Cstates__state_based.Dvalue(init['value'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tsum = {
	'number aggregate':Tnumber_aggregate;
};
export class Csum extends AlanNode {
	public readonly properties:{
		readonly number_aggregate:Cnumber_aggregate
	};
	constructor(init:Tsum, public parent:Cnumber_expression) {
		super();
		const $this = this;
		this.properties = {
			number_aggregate: new Csum.Dnumber_aggregate(init['number aggregate'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'sum'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsum_list = {
	'numbers':Tsigned_number_property_list;
};
export class Csum_list extends AlanNode {
	public readonly properties:{
		readonly numbers:Csigned_number_property_list
	};
	constructor(init:Tsum_list, public parent:Cnumber_expression) {
		super();
		const $this = this;
		this.properties = {
			numbers: new Csum_list.Dnumbers(init['numbers'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'sum list'`; }
	public get entity() { return this.parent.entity; }
}
export type Tvalue = {
	'value':number;
};
export class Cvalue extends AlanNode {
	public readonly properties:{
		readonly value:Cvalue.Dvalue
	};
	constructor(init:Tvalue, public parent:Cnumber_expression) {
		super();
		const $this = this;
		this.properties = {
			value: new Cvalue.Dvalue(init['value'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'value'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber_aggregate = {
	'aggregate':Taggregate;
	'property name':string;
};
export class Cnumber_aggregate extends AlanNode {
	public readonly properties:{
		readonly aggregate:Caggregate,
		readonly property_name:Cnumber_aggregate.Dproperty_name
	};
	constructor(init:Tnumber_aggregate, public location:AlanNode, public input: {
		context_node: () => application.Cnode__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			aggregate: new Cnumber_aggregate.Daggregate(init['aggregate'], $this),
			property_name: new Cnumber_aggregate.Dproperty_name(init['property name'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tnode_update = {
	'properties':Record<string, Tproperties__node_update>;
};
export class Cnode_update extends AlanNode {
	public readonly properties:{
		readonly properties:Cnode_update.Dproperties
	};
	constructor(init:Tnode_update, public location:AlanNode, public input: {
		node_type: () => application.Cnode__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			properties: new Cnode_update.Dproperties(init['properties'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export class Kproperties__node_update extends Reference<application.Cproperty__type__attributes, string> {
	public readonly inferences:{
		value_source_is_user: () => application.Cuser__external_source
	}
	constructor(key:string, $this:Cproperties__node_update) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.component_root.input.node_type())
			.then(context => {
				const entry = context?.properties.attributes.get(this.entry)!;
				return resolve(entry)
				.then(context => {
					if (context?.properties.type.state.name === 'property') {
						return context.properties.type.state.node as application.Cproperty__type__attributes;
					} else {
						return undefined;
					}
				}).result;
			}).result!), $this.parent)
		this.inferences = {
			value_source_is_user: cache((detach:boolean) => {
				const client_request__node_update__properties_key_nval = $this.key.ref;
				return resolve($this.key.ref)
					.then(context => client_request__node_update__properties_key_nval)
					.then(context => context?.properties.type?.state.node.output.value_source())
					.then(context => context?.variant.name === 'base' ? context.variant.definition as application.Cbase_value_source : undefined)
					.then(context => context?.variant.name === 'external' ? context.variant.definition as application.Cexternal_source : undefined)
					.then(context => context?.variant.name === 'user' ? context.variant.definition as application.Cuser__external_source : undefined).result!;
			})

		}
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'properties'::key`; }
}
export type Tproperties__node_update = {
	'type':['collection', Tcollection__type__properties__node_update]|['file', Tfile__type__properties__node_update]|['group', Tgroup__type__properties__node_update]|['number', Tnumber__type__properties__node_update]|['state group', Tstate_group__type__properties__node_update]|['text', Ttext__type__properties__node_update];
};
export class Cproperties__node_update extends AlanDictionaryEntry {
	public key:Kproperties__node_update;
	public get key_value() { return this.key.entry; }
	public get inferences() { return this.key.inferences; }
	public readonly properties:{
		readonly type:Cproperties__node_update.Dtype
	};
	constructor(key:string, init:Tproperties__node_update, public parent:Cnode_update) {
		super();
		const $this = this;
		this.key = new Kproperties__node_update(key, $this);
		this.properties = {
			type: new Cproperties__node_update.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'properties'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tcollection__type__properties__node_update = {
	'entries':Record<string, Tentries__collection__type__properties__node_update>;
};
export class Ccollection__type__properties__node_update extends AlanNode {
	public readonly properties:{
		readonly entries:Ccollection__type__properties__node_update.Dentries
	};
	public readonly inferences:{
		collection: () => application.Ccollection__type__property
	} = {
		collection: cache((detach:boolean) => {
			const client_request__node_update__properties__type__collection_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'collection') {
						return resolve(context.properties.type.state.node as application.Ccollection__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tcollection__type__properties__node_update, public parent:Cproperties__node_update) {
		super();
		const $this = this;
		this.properties = {
			entries: new Ccollection__type__properties__node_update.Dentries(init['entries'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'collection'`; }
	public get entity() { return this.parent.entity; }
}
export type Tentries__collection__type__properties__node_update = {
	'type':['create', Tcreate]|'remove'|['remove', {}]|['update', Tupdate__type__entries];
};
export class Centries__collection__type__properties__node_update extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly type:Centries__collection__type__properties__node_update.Dtype
	};
	constructor(key:string, init:Tentries__collection__type__properties__node_update, public parent:Ccollection__type__properties__node_update) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			type: new Centries__collection__type__properties__node_update.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'entries'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tcreate = {
	'initialization':Tnode_initialization;
};
export class Ccreate extends AlanNode {
	public readonly properties:{
		readonly initialization:Cnode_initialization
	};
	constructor(init:Tcreate, public parent:Centries__collection__type__properties__node_update) {
		super();
		const $this = this;
		this.properties = {
			initialization: new Ccreate.Dinitialization(init['initialization'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'create'`; }
	public get entity() { return this.parent.entity; }
}
export type Tremove = {
};
export class Cremove extends AlanNode {
	constructor(init:Tremove, public parent:Centries__collection__type__properties__node_update) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'remove'`; }
	public get entity() { return this.parent.entity; }
}
export type Tupdate__type__entries = {
	'update':Tnode_update;
};
export class Cupdate__type__entries extends AlanNode {
	public readonly properties:{
		readonly update:Cnode_update
	};
	constructor(init:Tupdate__type__entries, public parent:Centries__collection__type__properties__node_update) {
		super();
		const $this = this;
		this.properties = {
			update: new Cupdate__type__entries.Dupdate(init['update'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'update'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfile__type__properties__node_update = {
	'new extension':string;
	'new token':string;
};
export class Cfile__type__properties__node_update extends AlanNode {
	public readonly properties:{
		readonly new_extension:string,
		readonly new_token:string
	};
	public readonly inferences:{
		file: () => application.Cfile
	} = {
		file: cache((detach:boolean) => {
			const client_request__node_update__properties__type__file_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'file') {
						return resolve(context.properties.type.state.node as application.Cfile).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tfile__type__properties__node_update, public parent:Cproperties__node_update) {
		super();
		const $this = this;
		this.properties = {
			new_extension: init['new extension'],
			new_token: init['new token']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'file'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgroup__type__properties__node_update = {
	'update':Tnode_update;
};
export class Cgroup__type__properties__node_update extends AlanNode {
	public readonly properties:{
		readonly update:Cnode_update
	};
	public readonly inferences:{
		group: () => application.Cgroup__type__property
	} = {
		group: cache((detach:boolean) => {
			const client_request__node_update__properties__type__group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'group') {
						return resolve(context.properties.type.state.node as application.Cgroup__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tgroup__type__properties__node_update, public parent:Cproperties__node_update) {
		super();
		const $this = this;
		this.properties = {
			update: new Cgroup__type__properties__node_update.Dupdate(init['update'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__properties__node_update = {
	'new value':number;
};
export class Cnumber__type__properties__node_update extends AlanNode {
	public readonly properties:{
		readonly new_value:Cnumber__type__properties__node_update.Dnew_value
	};
	public readonly inferences:{
		number: () => application.Cnumber__type__property,
		simple: () => application.Csimple__type__base
	} = {
		number: cache((detach:boolean) => {
			const client_request__node_update__properties__type__number_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'number') {
						return resolve(context.properties.type.state.node as application.Cnumber__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		simple: cache((detach:boolean) => {
			const client_request__node_update__properties__type__number_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.number())
				.then(context => {
					if (context?.properties.type.state.name === 'base') {
						return resolve(context.properties.type.state.node as application.Cbase__type__number)
						.then(context => {
							if (context?.properties.type.state.name === 'simple') {
								return context.properties.type.state.node as application.Csimple__type__base;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tnumber__type__properties__node_update, public parent:Cproperties__node_update) {
		super();
		const $this = this;
		this.properties = {
			new_value: new Cnumber__type__properties__node_update.Dnew_value(init['new value'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_group__type__properties__node_update = {
	'state':string;
	'type':['set', Tset]|['update', Tupdate__type__state_group];
};
export class Cstate_group__type__properties__node_update extends AlanNode {
	public readonly properties:{
		readonly state:Cstate_group__type__properties__node_update.Dstate,
		readonly type:Cstate_group__type__properties__node_update.Dtype
	};
	public readonly inferences:{
		state_group: () => application.Cstate_group
	} = {
		state_group: cache((detach:boolean) => {
			const client_request__node_update__properties__type__state_group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'state group') {
						return resolve(context.properties.type.state.node as application.Cstate_group).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tstate_group__type__properties__node_update, public parent:Cproperties__node_update) {
		super();
		const $this = this;
		this.properties = {
			state: new Cstate_group__type__properties__node_update.Dstate(init['state'], $this),
			type: new Cstate_group__type__properties__node_update.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tset = {
	'initialization':Tnode_initialization;
};
export class Cset extends AlanNode {
	public readonly properties:{
		readonly initialization:Cnode_initialization
	};
	constructor(init:Tset, public parent:Cstate_group__type__properties__node_update) {
		super();
		const $this = this;
		this.properties = {
			initialization: new Cset.Dinitialization(init['initialization'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'set'`; }
	public get entity() { return this.parent.entity; }
}
export type Tupdate__type__state_group = {
	'update':Tnode_update;
};
export class Cupdate__type__state_group extends AlanNode {
	public readonly properties:{
		readonly update:Cnode_update
	};
	constructor(init:Tupdate__type__state_group, public parent:Cstate_group__type__properties__node_update) {
		super();
		const $this = this;
		this.properties = {
			update: new Cupdate__type__state_group.Dupdate(init['update'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'update'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext__type__properties__node_update = {
	'new value':string;
};
export class Ctext__type__properties__node_update extends AlanNode {
	public readonly properties:{
		readonly new_value:string
	};
	public readonly inferences:{
		text: () => application.Ctext__type__property
	} = {
		text: cache((detach:boolean) => {
			const client_request__node_update__properties__type__text_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'text') {
						return resolve(context.properties.type.state.node as application.Ctext__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Ttext__type__properties__node_update, public parent:Cproperties__node_update) {
		super();
		const $this = this;
		this.properties = {
			new_value: init['new value']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode_subscription = {
	'attributes':Record<string, Tattributes>;
};
export class Cnode_subscription extends AlanNode {
	public readonly properties:{
		readonly attributes:Cnode_subscription.Dattributes
	};
	constructor(init:Tnode_subscription, public location:AlanNode, public input: {
		node_type: () => application.Cnode__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			attributes: new Cnode_subscription.Dattributes(init['attributes'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export class Kattributes extends Reference<application.Cattributes, string> {
	constructor(key:string, $this:Cattributes) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.component_root.input.node_type())
			.then(context => {
				const entry = context?.properties.attributes.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'attributes'::key`; }
}
export type Tattributes = {
	'type':'command'|['command', {}]|['property', Tproperty__type__attributes];
};
export class Cattributes extends AlanDictionaryEntry {
	public key:Kattributes;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly type:Cattributes.Dtype
	};
	constructor(key:string, init:Tattributes, public parent:Cnode_subscription) {
		super();
		const $this = this;
		this.key = new Kattributes(key, $this);
		this.properties = {
			type: new Cattributes.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'attributes'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tcommand = {
};
export class Ccommand extends AlanNode {
	public readonly inferences:{
		command: () => application.Ccommand__type
	} = {
		command: cache((detach:boolean) => {
			const client_request__node_subscription__attributes__type__command_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'command') {
						return resolve(context.properties.type.state.node as application.Ccommand__type).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tcommand, public parent:Cattributes) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'command'`; }
	public get entity() { return this.parent.entity; }
}
export type Tproperty__type__attributes = {
	'type':['collection', Tcollection__type__property__type__attributes]|'file'|['file', {}]|['group', Tgroup__type__property]|'number'|['number', {}]|['state group', Tstate_group__type__property__type__attributes]|['text', Ttext__type__property__type__attributes];
};
export class Cproperty__type__attributes extends AlanNode {
	public readonly properties:{
		readonly type:Cproperty__type__attributes.Dtype
	};
	public readonly inferences:{
		property: () => application.Cproperty__type__attributes
	} = {
		property: cache((detach:boolean) => {
			const client_request__node_subscription__attributes__type__property_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'property') {
						return resolve(context.properties.type.state.node as application.Cproperty__type__attributes).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tproperty__type__attributes, public parent:Cattributes) {
		super();
		const $this = this;
		this.properties = {
			type: new Cproperty__type__attributes.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'property'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection__type__property__type__attributes = {
	'include graph endpoints':'no'|['no', {}]|'yes'|['yes', {}];
	'subscribe on':['entries', Tentries__subscribe_on]|'updates'|['updates', {}];
};
export class Ccollection__type__property__type__attributes extends AlanNode {
	public readonly properties:{
		readonly include_graph_endpoints:Ccollection__type__property__type__attributes.Dinclude_graph_endpoints,
		readonly subscribe_on:Ccollection__type__property__type__attributes.Dsubscribe_on
	};
	public readonly inferences:{
		collection: () => application.Ccollection__type__property
	} = {
		collection: cache((detach:boolean) => {
			const client_request__node_subscription__attributes__type__property__type__collection_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.property())
				.then(context => {
					if (context?.properties.type.state.name === 'collection') {
						return resolve(context.properties.type.state.node as application.Ccollection__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tcollection__type__property__type__attributes, public parent:Cproperty__type__attributes) {
		super();
		const $this = this;
		this.properties = {
			include_graph_endpoints: new Ccollection__type__property__type__attributes.Dinclude_graph_endpoints(init['include graph endpoints'], $this),
			subscribe_on: new Ccollection__type__property__type__attributes.Dsubscribe_on(init['subscribe on'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'collection'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__include_graph_endpoints__collection__type__property = {
};
export class Cno__include_graph_endpoints__collection__type__property extends AlanNode {
	constructor(init:Tno__include_graph_endpoints__collection__type__property, public parent:Ccollection__type__property__type__attributes) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'include graph endpoints'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__include_graph_endpoints__collection__type__property = {
};
export class Cyes__include_graph_endpoints__collection__type__property extends AlanNode {
	constructor(init:Tyes__include_graph_endpoints__collection__type__property, public parent:Ccollection__type__property__type__attributes) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'include graph endpoints'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tentries__subscribe_on = {
	'key filter':'no'|['no', {}]|['yes', Tyes__key_filter];
	'subscription':Tnode_subscription;
};
export class Centries__subscribe_on extends AlanNode {
	public readonly properties:{
		readonly key_filter:Centries__subscribe_on.Dkey_filter,
		readonly subscription:Cnode_subscription
	};
	constructor(init:Tentries__subscribe_on, public parent:Ccollection__type__property__type__attributes) {
		super();
		const $this = this;
		this.properties = {
			key_filter: new Centries__subscribe_on.Dkey_filter(init['key filter'], $this),
			subscription: new Centries__subscribe_on.Dsubscription(init['subscription'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'subscribe on'?'entries'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__key_filter = {
};
export class Cno__key_filter extends AlanNode {
	constructor(init:Tno__key_filter, public parent:Centries__subscribe_on) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'key filter'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__key_filter = {
	'keys':Record<string, {}>;
};
export class Cyes__key_filter extends AlanNode {
	public readonly properties:{
		readonly keys:Cyes__key_filter.Dkeys
	};
	constructor(init:Tyes__key_filter, public parent:Centries__subscribe_on) {
		super();
		const $this = this;
		this.properties = {
			keys: new Cyes__key_filter.Dkeys(init['keys'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'key filter'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tkeys__yes = {
};
export class Ckeys__yes extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	constructor(key:string, init:Tkeys__yes, public parent:Cyes__key_filter) {
		super();
		const $this = this;
		this.key = key;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'keys'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tupdates = {
};
export class Cupdates extends AlanNode {
	constructor(init:Tupdates, public parent:Ccollection__type__property__type__attributes) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'subscribe on'?'updates'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfile__type__property = {
};
export class Cfile__type__property extends AlanNode {
	public readonly inferences:{
		file: () => application.Cfile
	} = {
		file: cache((detach:boolean) => {
			const client_request__node_subscription__attributes__type__property__type__file_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.property())
				.then(context => {
					if (context?.properties.type.state.name === 'file') {
						return resolve(context.properties.type.state.node as application.Cfile).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tfile__type__property, public parent:Cproperty__type__attributes) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'file'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgroup__type__property = {
	'subscription':Tnode_subscription;
};
export class Cgroup__type__property extends AlanNode {
	public readonly properties:{
		readonly subscription:Cnode_subscription
	};
	public readonly inferences:{
		group: () => application.Cgroup__type__property
	} = {
		group: cache((detach:boolean) => {
			const client_request__node_subscription__attributes__type__property__type__group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.property())
				.then(context => {
					if (context?.properties.type.state.name === 'group') {
						return resolve(context.properties.type.state.node as application.Cgroup__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tgroup__type__property, public parent:Cproperty__type__attributes) {
		super();
		const $this = this;
		this.properties = {
			subscription: new Cgroup__type__property.Dsubscription(init['subscription'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__property__type__attributes = {
};
export class Cnumber__type__property__type__attributes extends AlanNode {
	public readonly inferences:{
		number: () => application.Cnumber__type__property
	} = {
		number: cache((detach:boolean) => {
			const client_request__node_subscription__attributes__type__property__type__number_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.property())
				.then(context => {
					if (context?.properties.type.state.name === 'number') {
						return resolve(context.properties.type.state.node as application.Cnumber__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tnumber__type__property__type__attributes, public parent:Cproperty__type__attributes) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_group__type__property__type__attributes = {
	'states':Record<string, Tstates__state_group>;
};
export class Cstate_group__type__property__type__attributes extends AlanNode {
	public readonly properties:{
		readonly states:Cstate_group__type__property__type__attributes.Dstates
	};
	public readonly inferences:{
		state_group: () => application.Cstate_group
	} = {
		state_group: cache((detach:boolean) => {
			const client_request__node_subscription__attributes__type__property__type__state_group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.property())
				.then(context => {
					if (context?.properties.type.state.name === 'state group') {
						return resolve(context.properties.type.state.node as application.Cstate_group).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tstate_group__type__property__type__attributes, public parent:Cproperty__type__attributes) {
		super();
		const $this = this;
		this.properties = {
			states: new Cstate_group__type__property__type__attributes.Dstates(init['states'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state group'`; }
	public get entity() { return this.parent.entity; }
}
export class Kstates__state_group extends Reference<application.Cstates__state_group, string> {
	constructor(key:string, $this:Cstates__state_group) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.inferences.state_group())
			.then(context => {
				const entry = context?.properties.states.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'::key`; }
}
export type Tstates__state_group = {
	'subscription':Tnode_subscription;
};
export class Cstates__state_group extends AlanDictionaryEntry {
	public key:Kstates__state_group;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly subscription:Cnode_subscription
	};
	constructor(key:string, init:Tstates__state_group, public parent:Cstate_group__type__property__type__attributes) {
		super();
		const $this = this;
		this.key = new Kstates__state_group(key, $this);
		this.properties = {
			subscription: new Cstates__state_group.Dsubscription(init['subscription'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Ttext__type__property__type__attributes = {
	'include reference':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Ctext__type__property__type__attributes extends AlanNode {
	public readonly properties:{
		readonly include_reference:Ctext__type__property__type__attributes.Dinclude_reference
	};
	public readonly inferences:{
		text: () => application.Ctext__type__property
	} = {
		text: cache((detach:boolean) => {
			const client_request__node_subscription__attributes__type__property__type__text_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.property())
				.then(context => {
					if (context?.properties.type.state.name === 'text') {
						return resolve(context.properties.type.state.node as application.Ctext__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Ttext__type__property__type__attributes, public parent:Cproperty__type__attributes) {
		super();
		const $this = this;
		this.properties = {
			include_reference: new Ctext__type__property__type__attributes.Dinclude_reference(init['include reference'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__include_reference__text = {
};
export class Cno__include_reference__text extends AlanNode {
	constructor(init:Tno__include_reference__text, public parent:Ctext__type__property__type__attributes) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'include reference'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__include_reference__text = {
};
export class Cyes__include_reference__text extends AlanNode {
	public readonly inferences:{
		linker: () => application.Cyes__has_reference
	} = {
		linker: cache((detach:boolean) => {
			const client_request__node_subscription__attributes__type__property__type__text__include_reference__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.text())
				.then(context => {
					if (context?.properties.has_reference.state.name === 'yes') {
						return resolve(context.properties.has_reference.state.node as application.Cyes__has_reference).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__include_reference__text, public parent:Ctext__type__property__type__attributes) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'include reference'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode_query = {
	'properties':Record<string, Tproperties__node_query>;
};
export class Cnode_query extends AlanNode {
	public readonly properties:{
		readonly properties:Cnode_query.Dproperties
	};
	constructor(init:Tnode_query, public location:AlanNode, public input: {
		node_type: () => application.Cnode__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			properties: new Cnode_query.Dproperties(init['properties'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tproperties__node_query = {
	'path':Tnode_path;
	'type':['collection', Tcollection__type__properties__node_query]|['file', Tfile__type__properties__node_query]|'id path'|['id path', {}]|['number', Tnumber__type__properties__node_query]|['state group', Tstate_group__type__properties__node_query]|['text', Ttext__type__properties__node_query];
};
export class Cproperties__node_query extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly path:Cnode_path,
		readonly type:Cproperties__node_query.Dtype
	};
	constructor(key:string, init:Tproperties__node_query, public parent:Cnode_query) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			path: new Cproperties__node_query.Dpath(init['path'], $this),
			type: new Cproperties__node_query.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'properties'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tcollection__type__properties__node_query = {
	'collection':string;
	'include graph endpoints':'no'|['no', {}]|'yes'|['yes', {}];
	'properties':Tnode_query;
};
export class Ccollection__type__properties__node_query extends AlanNode {
	public readonly properties:{
		readonly collection:Ccollection__type__properties__node_query.Dcollection,
		readonly include_graph_endpoints:Ccollection__type__properties__node_query.Dinclude_graph_endpoints,
		readonly properties:Cnode_query
	};
	constructor(init:Tcollection__type__properties__node_query, public parent:Cproperties__node_query) {
		super();
		const $this = this;
		this.properties = {
			collection: new Ccollection__type__properties__node_query.Dcollection(init['collection'], $this),
			include_graph_endpoints: new Ccollection__type__properties__node_query.Dinclude_graph_endpoints(init['include graph endpoints'], $this),
			properties: new Ccollection__type__properties__node_query.Dproperties(init['properties'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'collection'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__include_graph_endpoints__collection__type__properties = {
};
export class Cno__include_graph_endpoints__collection__type__properties extends AlanNode {
	constructor(init:Tno__include_graph_endpoints__collection__type__properties, public parent:Ccollection__type__properties__node_query) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'include graph endpoints'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__include_graph_endpoints__collection__type__properties = {
};
export class Cyes__include_graph_endpoints__collection__type__properties extends AlanNode {
	constructor(init:Tyes__include_graph_endpoints__collection__type__properties, public parent:Ccollection__type__properties__node_query) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'include graph endpoints'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfile__type__properties__node_query = {
	'file':string;
};
export class Cfile__type__properties__node_query extends AlanNode {
	public readonly properties:{
		readonly file:Cfile__type__properties__node_query.Dfile
	};
	constructor(init:Tfile__type__properties__node_query, public parent:Cproperties__node_query) {
		super();
		const $this = this;
		this.properties = {
			file: new Cfile__type__properties__node_query.Dfile(init['file'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'file'`; }
	public get entity() { return this.parent.entity; }
}
export type Tid_path__type = {
};
export class Cid_path__type extends AlanNode {
	constructor(init:Tid_path__type, public parent:Cproperties__node_query) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'id path'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__properties__node_query = {
	'expression':Tnumber_expression;
};
export class Cnumber__type__properties__node_query extends AlanNode {
	public readonly properties:{
		readonly expression:Cnumber_expression
	};
	constructor(init:Tnumber__type__properties__node_query, public parent:Cproperties__node_query) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cnumber__type__properties__node_query.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_group__type__properties__node_query = {
	'state group':string;
};
export class Cstate_group__type__properties__node_query extends AlanNode {
	public readonly properties:{
		readonly state_group:Cstate_group__type__properties__node_query.Dstate_group
	};
	constructor(init:Tstate_group__type__properties__node_query, public parent:Cproperties__node_query) {
		super();
		const $this = this;
		this.properties = {
			state_group: new Cstate_group__type__properties__node_query.Dstate_group(init['state group'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state group'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext__type__properties__node_query = {
	'expression':Ttext_expression;
};
export class Ctext__type__properties__node_query extends AlanNode {
	public readonly properties:{
		readonly expression:Ctext_expression
	};
	constructor(init:Ttext__type__properties__node_query, public parent:Cproperties__node_query) {
		super();
		const $this = this;
		this.properties = {
			expression: new Ctext__type__properties__node_query.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode_path = {
	'has steps':'no'|['no', {}]|['yes', Tyes__has_steps__node_path];
};
export class Cnode_path extends AlanNode {
	public readonly properties:{
		readonly has_steps:Cnode_path.Dhas_steps
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.result_node()).result!)
	};
	constructor(init:Tnode_path, public location:AlanNode, public input: {
		context_node: () => application.Cnode__application,
		existence_support: () => application.Cexistence__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_steps: new Cnode_path.Dhas_steps(init['has steps'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_steps__node_path = {
};
export class Cno__has_steps__node_path extends AlanNode {
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.context_node()).result!)
	}
	constructor(init:Tno__has_steps__node_path, public parent:Cnode_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_steps__node_path = {
	'tail':Tnode_path;
	'type':['collection entry', Tcollection_entry__type__yes__has_steps__node_path]|['group', Tgroup__type__yes__has_steps__node_path]|'parent'|['parent', {}]|['reference', Treference]|['reference rule', Treference_rule]|['state', Tstate__type__yes__has_steps__node_path]|['state rule', Tstate_rule];
};
export class Cyes__has_steps__node_path extends AlanNode {
	public readonly properties:{
		readonly tail:Cnode_path,
		readonly type:Cyes__has_steps__node_path.Dtype
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.result_node()).result!)
	}
	constructor(init:Tyes__has_steps__node_path, public parent:Cnode_path) {
		super();
		const $this = this;
		this.properties = {
			tail: new Cyes__has_steps__node_path.Dtail(init['tail'], $this),
			type: new Cyes__has_steps__node_path.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection_entry__type__yes__has_steps__node_path = {
	'collection':string;
	'entry key':string;
};
export class Ccollection_entry__type__yes__has_steps__node_path extends AlanNode {
	public readonly properties:{
		readonly collection:Ccollection_entry__type__yes__has_steps__node_path.Dcollection,
		readonly entry_key:string
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.collection?.ref)
			.then(context => context?.properties.node).result!)
	}
	public readonly inferences:{
		optional_existence: () => application.Coptional__existence
	} = {
		optional_existence: cache((detach:boolean) => {
			const client_request__node_path__has_steps__yes__type__collection_entry_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.existence_support())
				.then(context => context?.variant.name === 'optional' ? context.variant.definition as application.Coptional__existence : undefined).result!;
		})

	}
	constructor(init:Tcollection_entry__type__yes__has_steps__node_path, public parent:Cyes__has_steps__node_path) {
		super();
		const $this = this;
		this.properties = {
			collection: new Ccollection_entry__type__yes__has_steps__node_path.Dcollection(init['collection'], $this),
			entry_key: init['entry key']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'collection entry'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgroup__type__yes__has_steps__node_path = {
	'group':string;
};
export class Cgroup__type__yes__has_steps__node_path extends AlanNode {
	public readonly properties:{
		readonly group:Cgroup__type__yes__has_steps__node_path.Dgroup
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.group?.ref)
			.then(context => context?.properties.node).result!)
	}
	constructor(init:Tgroup__type__yes__has_steps__node_path, public parent:Cyes__has_steps__node_path) {
		super();
		const $this = this;
		this.properties = {
			group: new Cgroup__type__yes__has_steps__node_path.Dgroup(init['group'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tparent = {
};
export class Cparent extends AlanNode {
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.parent_node()).result!)
	}
	public readonly inferences:{
		parent_node: () => application.Cnode__application
	} = {
		parent_node: cache((detach:boolean) => {
			const client_request__node_path__has_steps__yes__type__parent_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context_node())
				.then(context => context?.component_root.output.location())
				.then(context => context?.component_root.output.node())
				.then(context => context?.variant.name === 'node' ? context.variant.definition as application.Cnode__application : undefined).result!;
		})

	}
	constructor(init:Tparent, public parent:Cyes__has_steps__node_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'parent'`; }
	public get entity() { return this.parent.entity; }
}
export type Treference = {
	'text':string;
};
export class Creference extends AlanNode {
	public readonly properties:{
		readonly text:Creference.Dtext
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.text?.inferences.reference())
			.then(context => context?.properties.expression)
			.then(context => context?.component_root.output.referenced_node_type()).result!)
	}
	constructor(init:Treference, public parent:Cyes__has_steps__node_path) {
		super();
		const $this = this;
		this.properties = {
			text: new Creference.Dtext(init['text'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'reference'`; }
	public get entity() { return this.parent.entity; }
}
export type Treference_rule = {
	'property':string;
	'rule':string;
};
export class Creference_rule extends AlanNode {
	public readonly properties:{
		readonly property:Creference_rule.Dproperty,
		readonly rule:Creference_rule.Drule
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.rule?.ref)
			.then(context => context?.properties.type?.state.node.output.referenced_node_type()).result!)
	}
	constructor(init:Treference_rule, public parent:Cyes__has_steps__node_path) {
		super();
		const $this = this;
		this.properties = {
			property: new Creference_rule.Dproperty(init['property'], $this),
			rule: new Creference_rule.Drule(init['rule'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'reference rule'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate__type__yes__has_steps__node_path = {
	'state':string;
	'state group':string;
};
export class Cstate__type__yes__has_steps__node_path extends AlanNode {
	public readonly properties:{
		readonly state:Cstate__type__yes__has_steps__node_path.Dstate,
		readonly state_group:Cstate__type__yes__has_steps__node_path.Dstate_group
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.state?.ref)
			.then(context => context?.properties.node).result!)
	}
	public readonly inferences:{
		optional_existence: () => application.Coptional__existence
	} = {
		optional_existence: cache((detach:boolean) => {
			const client_request__node_path__has_steps__yes__type__state_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.existence_support())
				.then(context => context?.variant.name === 'optional' ? context.variant.definition as application.Coptional__existence : undefined).result!;
		})

	}
	constructor(init:Tstate__type__yes__has_steps__node_path, public parent:Cyes__has_steps__node_path) {
		super();
		const $this = this;
		this.properties = {
			state: new Cstate__type__yes__has_steps__node_path.Dstate(init['state'], $this),
			state_group: new Cstate__type__yes__has_steps__node_path.Dstate_group(init['state group'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_rule = {
	'rule':string;
};
export class Cstate_rule extends AlanNode {
	public readonly properties:{
		readonly rule:Cstate_rule.Drule
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.rule?.ref)
			.then(context => context?.properties.type?.state.node.output.referenced_node_type()).result!)
	}
	public readonly inferences:{
		state: () => application.Cstates__state_group
	} = {
		state: cache((detach:boolean) => {
			const client_request__node_path__has_steps__yes__type__state_rule_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.context_node())
				.then(context => context?.component_root.output.location())
				.then(context => context?.variant.name === 'state' ? context.variant.definition as application.Cstates__state_group : undefined).result!;
		})

	}
	constructor(init:Tstate_rule, public parent:Cyes__has_steps__node_path) {
		super();
		const $this = this;
		this.properties = {
			rule: new Cstate_rule.Drule(init['rule'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state rule'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode_initialization = {
	'properties':Record<string, Tproperties__node_initialization>;
};
export class Cnode_initialization extends AlanNode {
	public readonly properties:{
		readonly properties:Cnode_initialization.Dproperties
	};
	constructor(init:Tnode_initialization, public location:AlanNode, public input: {
		node_type: () => application.Cnode__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			properties: new Cnode_initialization.Dproperties(init['properties'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export class Kproperties__node_initialization extends Reference<application.Cproperty__type__attributes, string> {
	public readonly inferences:{
		value_source_is_user: () => application.Cexternal_source
	}
	constructor(key:string, $this:Cproperties__node_initialization) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.component_root.input.node_type())
			.then(context => {
				const entry = context?.properties.attributes.get(this.entry)!;
				return resolve(entry)
				.then(context => {
					if (context?.properties.type.state.name === 'property') {
						return context.properties.type.state.node as application.Cproperty__type__attributes;
					} else {
						return undefined;
					}
				}).result;
			}).result!), $this.parent)
		this.inferences = {
			value_source_is_user: cache((detach:boolean) => {
				const client_request__node_initialization__properties_key_nval = $this.key.ref;
				return resolve($this.key.ref)
					.then(context => client_request__node_initialization__properties_key_nval)
					.then(context => context?.properties.type?.state.node.output.value_source())
					.then(context => context?.variant.name === 'base' ? context.variant.definition as application.Cbase_value_source : undefined)
					.then(context => context?.variant.name === 'external' ? context.variant.definition as application.Cexternal_source : undefined).result!;
			})

		}
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'properties'::key`; }
}
export type Tproperties__node_initialization = {
	'type':['collection', Tcollection__type__properties__node_initialization]|['file', Tfile__type__properties__node_initialization]|['group', Tgroup__type__properties__node_initialization]|['number', Tnumber__type__properties__node_initialization]|['state group', Tstate_group__type__properties__node_initialization]|['text', Ttext__type__properties__node_initialization];
};
export class Cproperties__node_initialization extends AlanDictionaryEntry {
	public key:Kproperties__node_initialization;
	public get key_value() { return this.key.entry; }
	public get inferences() { return this.key.inferences; }
	public readonly properties:{
		readonly type:Cproperties__node_initialization.Dtype
	};
	constructor(key:string, init:Tproperties__node_initialization, public parent:Cnode_initialization) {
		super();
		const $this = this;
		this.key = new Kproperties__node_initialization(key, $this);
		this.properties = {
			type: new Cproperties__node_initialization.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'properties'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tcollection__type__properties__node_initialization = {
	'entries':Record<string, Tentries__collection__type__properties__node_initialization>;
};
export class Ccollection__type__properties__node_initialization extends AlanNode {
	public readonly properties:{
		readonly entries:Ccollection__type__properties__node_initialization.Dentries
	};
	public readonly inferences:{
		collection: () => application.Ccollection__type__property
	} = {
		collection: cache((detach:boolean) => {
			const client_request__node_initialization__properties__type__collection_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'collection') {
						return resolve(context.properties.type.state.node as application.Ccollection__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tcollection__type__properties__node_initialization, public parent:Cproperties__node_initialization) {
		super();
		const $this = this;
		this.properties = {
			entries: new Ccollection__type__properties__node_initialization.Dentries(init['entries'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'collection'`; }
	public get entity() { return this.parent.entity; }
}
export type Tentries__collection__type__properties__node_initialization = {
	'initialization':Tnode_initialization;
};
export class Centries__collection__type__properties__node_initialization extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly initialization:Cnode_initialization
	};
	constructor(key:string, init:Tentries__collection__type__properties__node_initialization, public parent:Ccollection__type__properties__node_initialization) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			initialization: new Centries__collection__type__properties__node_initialization.Dinitialization(init['initialization'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'entries'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tfile__type__properties__node_initialization = {
	'extension':string;
	'token':string;
};
export class Cfile__type__properties__node_initialization extends AlanNode {
	public readonly properties:{
		readonly extension:string,
		readonly token:string
	};
	public readonly inferences:{
		file: () => application.Cfile
	} = {
		file: cache((detach:boolean) => {
			const client_request__node_initialization__properties__type__file_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'file') {
						return resolve(context.properties.type.state.node as application.Cfile).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tfile__type__properties__node_initialization, public parent:Cproperties__node_initialization) {
		super();
		const $this = this;
		this.properties = {
			extension: init['extension'],
			token: init['token']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'file'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgroup__type__properties__node_initialization = {
	'initialization':Tnode_initialization;
};
export class Cgroup__type__properties__node_initialization extends AlanNode {
	public readonly properties:{
		readonly initialization:Cnode_initialization
	};
	public readonly inferences:{
		group: () => application.Cgroup__type__property
	} = {
		group: cache((detach:boolean) => {
			const client_request__node_initialization__properties__type__group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'group') {
						return resolve(context.properties.type.state.node as application.Cgroup__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tgroup__type__properties__node_initialization, public parent:Cproperties__node_initialization) {
		super();
		const $this = this;
		this.properties = {
			initialization: new Cgroup__type__properties__node_initialization.Dinitialization(init['initialization'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__properties__node_initialization = {
	'value':number;
};
export class Cnumber__type__properties__node_initialization extends AlanNode {
	public readonly properties:{
		readonly value:Cnumber__type__properties__node_initialization.Dvalue
	};
	public readonly inferences:{
		number: () => application.Cnumber__type__property,
		simple: () => application.Csimple__type__base
	} = {
		number: cache((detach:boolean) => {
			const client_request__node_initialization__properties__type__number_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'number') {
						return resolve(context.properties.type.state.node as application.Cnumber__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		simple: cache((detach:boolean) => {
			const client_request__node_initialization__properties__type__number_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.number())
				.then(context => {
					if (context?.properties.type.state.name === 'base') {
						return resolve(context.properties.type.state.node as application.Cbase__type__number)
						.then(context => {
							if (context?.properties.type.state.name === 'simple') {
								return context.properties.type.state.node as application.Csimple__type__base;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tnumber__type__properties__node_initialization, public parent:Cproperties__node_initialization) {
		super();
		const $this = this;
		this.properties = {
			value: new Cnumber__type__properties__node_initialization.Dvalue(init['value'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_group__type__properties__node_initialization = {
	'initialization':Tnode_initialization;
	'state':string;
};
export class Cstate_group__type__properties__node_initialization extends AlanNode {
	public readonly properties:{
		readonly initialization:Cnode_initialization,
		readonly state:Cstate_group__type__properties__node_initialization.Dstate
	};
	public readonly inferences:{
		state_group: () => application.Cstate_group
	} = {
		state_group: cache((detach:boolean) => {
			const client_request__node_initialization__properties__type__state_group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'state group') {
						return resolve(context.properties.type.state.node as application.Cstate_group).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tstate_group__type__properties__node_initialization, public parent:Cproperties__node_initialization) {
		super();
		const $this = this;
		this.properties = {
			initialization: new Cstate_group__type__properties__node_initialization.Dinitialization(init['initialization'], $this),
			state: new Cstate_group__type__properties__node_initialization.Dstate(init['state'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state group'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext__type__properties__node_initialization = {
	'value':string;
};
export class Ctext__type__properties__node_initialization extends AlanNode {
	public readonly properties:{
		readonly value:string
	};
	public readonly inferences:{
		text: () => application.Ctext__type__property
	} = {
		text: cache((detach:boolean) => {
			const client_request__node_initialization__properties__type__text_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'text') {
						return resolve(context.properties.type.state.node as application.Ctext__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Ttext__type__properties__node_initialization, public parent:Cproperties__node_initialization) {
		super();
		const $this = this;
		this.properties = {
			value: init['value']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tid_path__client_request = {
	'has steps':'no'|['no', {}]|['yes', Tyes__has_steps__id_path];
};
export class Cid_path__client_request extends AlanNode {
	public readonly properties:{
		readonly has_steps:Cid_path__client_request.Dhas_steps
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.result_node()).result!)
	};
	constructor(init:Tid_path__client_request, public location:AlanNode, public input: {
		context_node: () => application.Cnode__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_steps: new Cid_path__client_request.Dhas_steps(init['has steps'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_steps__id_path = {
};
export class Cno__has_steps__id_path extends AlanNode {
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.context_node()).result!)
	}
	constructor(init:Tno__has_steps__id_path, public parent:Cid_path__client_request) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_steps__id_path = {
	'tail':Tid_path__client_request;
	'type':['collection entry', Tcollection_entry__type__yes__has_steps__id_path]|['group', Tgroup__type__yes__has_steps__id_path]|['state', Tstate__type__yes__has_steps__id_path];
};
export class Cyes__has_steps__id_path extends AlanNode {
	public readonly properties:{
		readonly tail:Cid_path__client_request,
		readonly type:Cyes__has_steps__id_path.Dtype
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.result_node()).result!)
	}
	constructor(init:Tyes__has_steps__id_path, public parent:Cid_path__client_request) {
		super();
		const $this = this;
		this.properties = {
			tail: new Cyes__has_steps__id_path.Dtail(init['tail'], $this),
			type: new Cyes__has_steps__id_path.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection_entry__type__yes__has_steps__id_path = {
	'collection':string;
	'id':string;
};
export class Ccollection_entry__type__yes__has_steps__id_path extends AlanNode {
	public readonly properties:{
		readonly collection:Ccollection_entry__type__yes__has_steps__id_path.Dcollection,
		readonly id:string
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.collection?.ref)
			.then(context => context?.properties.node).result!)
	}
	constructor(init:Tcollection_entry__type__yes__has_steps__id_path, public parent:Cyes__has_steps__id_path) {
		super();
		const $this = this;
		this.properties = {
			collection: new Ccollection_entry__type__yes__has_steps__id_path.Dcollection(init['collection'], $this),
			id: init['id']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'collection entry'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgroup__type__yes__has_steps__id_path = {
	'group':string;
};
export class Cgroup__type__yes__has_steps__id_path extends AlanNode {
	public readonly properties:{
		readonly group:Cgroup__type__yes__has_steps__id_path.Dgroup
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.group?.ref)
			.then(context => context?.properties.node).result!)
	}
	constructor(init:Tgroup__type__yes__has_steps__id_path, public parent:Cyes__has_steps__id_path) {
		super();
		const $this = this;
		this.properties = {
			group: new Cgroup__type__yes__has_steps__id_path.Dgroup(init['group'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate__type__yes__has_steps__id_path = {
	'state':string;
	'state group':string;
};
export class Cstate__type__yes__has_steps__id_path extends AlanNode {
	public readonly properties:{
		readonly state:Cstate__type__yes__has_steps__id_path.Dstate,
		readonly state_group:Cstate__type__yes__has_steps__id_path.Dstate_group
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.state?.ref)
			.then(context => context?.properties.node).result!)
	}
	constructor(init:Tstate__type__yes__has_steps__id_path, public parent:Cyes__has_steps__id_path) {
		super();
		const $this = this;
		this.properties = {
			state: new Cstate__type__yes__has_steps__id_path.Dstate(init['state'], $this),
			state_group: new Cstate__type__yes__has_steps__id_path.Dstate_group(init['state group'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfilter_expression = {
	'has more steps':'no'|['no', {}]|['yes', Tyes__has_more_steps];
	'type':['alternatives', Talternatives__type]|'wildcard'|['wildcard', {}];
};
export class Cfilter_expression extends AlanNode {
	public readonly properties:{
		readonly has_more_steps:Cfilter_expression.Dhas_more_steps,
		readonly type:Cfilter_expression.Dtype
	};
	constructor(init:Tfilter_expression, public location:AlanNode, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_more_steps: new Cfilter_expression.Dhas_more_steps(init['has more steps'], $this),
			type: new Cfilter_expression.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_more_steps = {
};
export class Cno__has_more_steps extends AlanNode {
	constructor(init:Tno__has_more_steps, public parent:Cfilter_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has more steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_more_steps = {
	'tail':Tfilter_expression;
};
export class Cyes__has_more_steps extends AlanNode {
	public readonly properties:{
		readonly tail:Cfilter_expression
	};
	constructor(init:Tyes__has_more_steps, public parent:Cfilter_expression) {
		super();
		const $this = this;
		this.properties = {
			tail: new Cyes__has_more_steps.Dtail(init['tail'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has more steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Talternatives__type = {
	'alternatives':Record<string, {}>;
};
export class Calternatives__type extends AlanNode {
	public readonly properties:{
		readonly alternatives:Calternatives__type.Dalternatives
	};
	constructor(init:Talternatives__type, public parent:Cfilter_expression) {
		super();
		const $this = this;
		this.properties = {
			alternatives: new Calternatives__type.Dalternatives(init['alternatives'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'alternatives'`; }
	public get entity() { return this.parent.entity; }
}
export type Talternatives__alternatives = {
};
export class Calternatives__alternatives extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	constructor(key:string, init:Talternatives__alternatives, public parent:Calternatives__type) {
		super();
		const $this = this;
		this.key = key;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'alternatives'['${this.key}']`; }
	public get entity() { return this; }
}
export type Twildcard = {
};
export class Cwildcard extends AlanNode {
	constructor(init:Twildcard, public parent:Cfilter_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'wildcard'`; }
	public get entity() { return this.parent.entity; }
}
export type Tentry_point_path = {
	'has steps':['no', Tno__has_steps__entry_point_path]|['yes', Tyes__has_steps__entry_point_path];
};
export class Centry_point_path extends AlanNode {
	public readonly properties:{
		readonly has_steps:Centry_point_path.Dhas_steps
	};
	public readonly output:{
		result_collection: () => application.Ccollection__type__property;
	} = {
		result_collection: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.result_collection()).result!)
	};
	constructor(init:Tentry_point_path, public location:AlanNode, public input: {
		context_node: () => application.Cnode__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_steps: new Centry_point_path.Dhas_steps(init['has steps'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_steps__entry_point_path = {
	'text':string;
};
export class Cno__has_steps__entry_point_path extends AlanNode {
	public readonly properties:{
		readonly text:Cno__has_steps__entry_point_path.Dtext
	};
	public readonly output:{
		result_collection: () => application.Ccollection__type__property;
	} = {
		result_collection: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.text?.inferences.reference())
			.then(context => context?.properties.expression)
			.then(context => context?.component_root.output.collection()).result!)
	}
	constructor(init:Tno__has_steps__entry_point_path, public parent:Centry_point_path) {
		super();
		const $this = this;
		this.properties = {
			text: new Cno__has_steps__entry_point_path.Dtext(init['text'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_steps__entry_point_path = {
	'collection':string;
	'head':Tconditional_child_path;
	'tail':Tentry_point_path;
};
export class Cyes__has_steps__entry_point_path extends AlanNode {
	public readonly properties:{
		readonly collection:Cyes__has_steps__entry_point_path.Dcollection,
		readonly head:Cconditional_child_path,
		readonly tail:Centry_point_path
	};
	public readonly output:{
		result_collection: () => application.Ccollection__type__property;
	} = {
		result_collection: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.result_collection()).result!)
	}
	constructor(init:Tyes__has_steps__entry_point_path, public parent:Centry_point_path) {
		super();
		const $this = this;
		this.properties = {
			collection: new Cyes__has_steps__entry_point_path.Dcollection(init['collection'], $this),
			head: new Cyes__has_steps__entry_point_path.Dhead(init['head'], $this),
			tail: new Cyes__has_steps__entry_point_path.Dtail(init['tail'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tconditional_child_path = {
	'has steps':'no'|['no', {}]|['yes', Tyes__has_steps__conditional_child_path];
};
export class Cconditional_child_path extends AlanNode {
	public readonly properties:{
		readonly has_steps:Cconditional_child_path.Dhas_steps
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.result_node()).result!)
	};
	constructor(init:Tconditional_child_path, public location:AlanNode, public input: {
		context_node: () => application.Cnode__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_steps: new Cconditional_child_path.Dhas_steps(init['has steps'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_steps__conditional_child_path = {
};
export class Cno__has_steps__conditional_child_path extends AlanNode {
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.context_node()).result!)
	}
	constructor(init:Tno__has_steps__conditional_child_path, public parent:Cconditional_child_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_steps__conditional_child_path = {
	'tail':Tconditional_child_path;
	'type':['group', Tgroup__type__yes__has_steps__conditional_child_path]|['state', Tstate__type__yes__has_steps__conditional_child_path];
};
export class Cyes__has_steps__conditional_child_path extends AlanNode {
	public readonly properties:{
		readonly tail:Cconditional_child_path,
		readonly type:Cyes__has_steps__conditional_child_path.Dtype
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.result_node()).result!)
	}
	constructor(init:Tyes__has_steps__conditional_child_path, public parent:Cconditional_child_path) {
		super();
		const $this = this;
		this.properties = {
			tail: new Cyes__has_steps__conditional_child_path.Dtail(init['tail'], $this),
			type: new Cyes__has_steps__conditional_child_path.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgroup__type__yes__has_steps__conditional_child_path = {
	'group':string;
};
export class Cgroup__type__yes__has_steps__conditional_child_path extends AlanNode {
	public readonly properties:{
		readonly group:Cgroup__type__yes__has_steps__conditional_child_path.Dgroup
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.group?.ref)
			.then(context => context?.properties.node).result!)
	}
	constructor(init:Tgroup__type__yes__has_steps__conditional_child_path, public parent:Cyes__has_steps__conditional_child_path) {
		super();
		const $this = this;
		this.properties = {
			group: new Cgroup__type__yes__has_steps__conditional_child_path.Dgroup(init['group'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate__type__yes__has_steps__conditional_child_path = {
	'state':string;
	'state group':string;
};
export class Cstate__type__yes__has_steps__conditional_child_path extends AlanNode {
	public readonly properties:{
		readonly state:Cstate__type__yes__has_steps__conditional_child_path.Dstate,
		readonly state_group:Cstate__type__yes__has_steps__conditional_child_path.Dstate_group
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.state?.ref)
			.then(context => context?.properties.node).result!)
	}
	constructor(init:Tstate__type__yes__has_steps__conditional_child_path, public parent:Cyes__has_steps__conditional_child_path) {
		super();
		const $this = this;
		this.properties = {
			state: new Cstate__type__yes__has_steps__conditional_child_path.Dstate(init['state'], $this),
			state_group: new Cstate__type__yes__has_steps__conditional_child_path.Dstate_group(init['state group'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection_query_path = {
	'has steps':'no'|['no', {}]|['yes', Tyes__has_steps__collection_query_path];
};
export class Ccollection_query_path extends AlanNode {
	public readonly properties:{
		readonly has_steps:Ccollection_query_path.Dhas_steps
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.result_node()).result!)
	};
	constructor(init:Tcollection_query_path, public location:AlanNode, public input: {
		context_node: () => application.Cnode__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_steps: new Ccollection_query_path.Dhas_steps(init['has steps'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_steps__collection_query_path = {
};
export class Cno__has_steps__collection_query_path extends AlanNode {
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.context_node()).result!)
	}
	constructor(init:Tno__has_steps__collection_query_path, public parent:Ccollection_query_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_steps__collection_query_path = {
	'collection':string;
	'head':Tconditional_child_path;
	'tail':Tcollection_query_path;
	'type':['candidates', Tcandidates]|'existing entries'|['existing entries', {}];
};
export class Cyes__has_steps__collection_query_path extends AlanNode {
	public readonly properties:{
		readonly collection:Cyes__has_steps__collection_query_path.Dcollection,
		readonly head:Cconditional_child_path,
		readonly tail:Ccollection_query_path,
		readonly type:Cyes__has_steps__collection_query_path.Dtype
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.result_node()).result!)
	}
	constructor(init:Tyes__has_steps__collection_query_path, public parent:Ccollection_query_path) {
		super();
		const $this = this;
		this.properties = {
			collection: new Cyes__has_steps__collection_query_path.Dcollection(init['collection'], $this),
			head: new Cyes__has_steps__collection_query_path.Dhead(init['head'], $this),
			tail: new Cyes__has_steps__collection_query_path.Dtail(init['tail'], $this),
			type: new Cyes__has_steps__collection_query_path.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcandidates = {
	'include reference':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Ccandidates extends AlanNode {
	public readonly properties:{
		readonly include_reference:Ccandidates.Dinclude_reference
	};
	public readonly output:{
		collection_node: () => application.Cnode__application;
	} = {
		collection_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.key_reference())
			.then(context => context?.properties.expression)
			.then(context => context?.component_root.output.collection())
			.then(context => context?.properties.node).result!)
	}
	public readonly inferences:{
		key_reference: () => application.Cyes__has_reference
	} = {
		key_reference: cache((detach:boolean) => {
			const client_request__collection_query_path__has_steps__yes__type__candidates_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.collection?.ref)
				.then(context => context?.properties.key_property?.ref)
				.then(context => {
					if (context?.properties.has_reference.state.name === 'yes') {
						return resolve(context.properties.has_reference.state.node as application.Cyes__has_reference).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tcandidates, public parent:Cyes__has_steps__collection_query_path) {
		super();
		const $this = this;
		this.properties = {
			include_reference: new Ccandidates.Dinclude_reference(init['include reference'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'candidates'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__include_reference__candidates = {
};
export class Cno__include_reference__candidates extends AlanNode {
	constructor(init:Tno__include_reference__candidates, public parent:Ccandidates) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'include reference'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__include_reference__candidates = {
};
export class Cyes__include_reference__candidates extends AlanNode {
	constructor(init:Tyes__include_reference__candidates, public parent:Ccandidates) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'include reference'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Texisting_entries = {
};
export class Cexisting_entries extends AlanNode {
	public readonly output:{
		collection_node: () => application.Cnode__application;
	} = {
		collection_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.properties.collection?.ref)
			.then(context => context?.properties.node).result!)
	}
	constructor(init:Texisting_entries, public parent:Cyes__has_steps__collection_query_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'existing entries'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection_path = {
	'has steps':'no'|['no', {}]|['yes', Tyes__has_steps__collection_path];
};
export class Ccollection_path extends AlanNode {
	public readonly properties:{
		readonly has_steps:Ccollection_path.Dhas_steps
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.result_node()).result!)
	};
	constructor(init:Tcollection_path, public location:AlanNode, public input: {
		context_node: () => application.Cnode__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_steps: new Ccollection_path.Dhas_steps(init['has steps'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_steps__collection_path = {
};
export class Cno__has_steps__collection_path extends AlanNode {
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.context_node()).result!)
	}
	constructor(init:Tno__has_steps__collection_path, public parent:Ccollection_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_steps__collection_path = {
	'collection':string;
	'head':Tconditional_child_path;
	'tail':Tcollection_path;
};
export class Cyes__has_steps__collection_path extends AlanNode {
	public readonly properties:{
		readonly collection:Cyes__has_steps__collection_path.Dcollection,
		readonly head:Cconditional_child_path,
		readonly tail:Ccollection_path
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.result_node()).result!)
	}
	constructor(init:Tyes__has_steps__collection_path, public parent:Ccollection_path) {
		super();
		const $this = this;
		this.properties = {
			collection: new Cyes__has_steps__collection_path.Dcollection(init['collection'], $this),
			head: new Cyes__has_steps__collection_path.Dhead(init['head'], $this),
			tail: new Cyes__has_steps__collection_path.Dtail(init['tail'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Taggregate = {
	'collection':string;
	'head':Tnode_path;
	'tail':Tnode_path;
};
export class Caggregate extends AlanNode {
	public readonly properties:{
		readonly collection:Caggregate.Dcollection,
		readonly head:Cnode_path,
		readonly tail:Cnode_path
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.result_node()).result!)
	};
	constructor(init:Taggregate, public location:AlanNode, public input: {
		context_node: () => application.Cnode__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			collection: new Caggregate.Dcollection(init['collection'], $this),
			head: new Caggregate.Dhead(init['head'], $this),
			tail: new Caggregate.Dtail(init['tail'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}

export type Tclient_request = {
	'type':['acyclic graph list query', Tacyclic_graph_list_query]|['acyclic graph tree query', Tacyclic_graph_tree_query]|['collection query', Tcollection_query]|['command execution', Tcommand_execution]|['mutation', Tmutation]|['password mutation', Tpassword_mutation]|['password reset', Tpassword_reset]|['subscription', Tsubscription]|'subscription deletion'|['subscription deletion', {}];
};
export class Cclient_request extends AlanNode {
	public key?:string;
	public get root() { return this; }
	public readonly properties:{
		readonly type:Cclient_request.Dtype
	};
	constructor(init:Tclient_request, public readonly input: {
	'application':application.Capplication}, symbols_file?: SymbolsFile) {
		super();
		const $this = this;
		this.properties = {
			type: new Cclient_request.Dtype(init['type'], $this)
		};
	}
	public get path() { return ``; }
	public get entity() { return this; }
}
export type Tacyclic_graph_list_query = {
	'context node':Tid_path__client_request;
	'entry points':Tentry_point_path;
	'graph':string;
	'query':Tquery;
};
export class Cacyclic_graph_list_query extends AlanNode {
	public readonly properties:{
		readonly context_node:Cid_path__client_request,
		readonly entry_points:Centry_point_path,
		readonly graph:Cacyclic_graph_list_query.Dgraph,
		readonly query:Cquery
	};
	constructor(init:Tacyclic_graph_list_query, public parent:Cclient_request) {
		super();
		const $this = this;
		this.properties = {
			context_node: new Cacyclic_graph_list_query.Dcontext_node(init['context node'], $this),
			entry_points: new Cacyclic_graph_list_query.Dentry_points(init['entry points'], $this),
			graph: new Cacyclic_graph_list_query.Dgraph(init['graph'], $this),
			query: new Cquery(init['query'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'acyclic graph list query'`; }
	public get entity() { return this.parent.entity; }
}
export type Tquery = {
	'collection path':Tcollection_path;
	'properties':Record<string, Tproperties__query>;
};
export class Cquery extends AlanNode {
	public readonly properties:{
		readonly collection_path:Ccollection_path,
		readonly properties:Cquery.Dproperties
	};
	constructor(init:Tquery, public parent:Cacyclic_graph_list_query) {
		super();
		const $this = this;
		this.properties = {
			collection_path: new Cquery.Dcollection_path(init['collection path'], $this),
			properties: new Cquery.Dproperties(init['properties'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'query'`; }
	public get entity() { return this.parent.entity; }
}
export type Tproperties__query = {
	'path':Tnode_path;
	'type':['number', Tnumber__type__properties__query]|['state group', Tstate_group__type__properties__query]|['text', Ttext__type__properties__query];
};
export class Cproperties__query extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly path:Cnode_path,
		readonly type:Cproperties__query.Dtype
	};
	constructor(key:string, init:Tproperties__query, public parent:Cquery) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			path: new Cproperties__query.Dpath(init['path'], $this),
			type: new Cproperties__query.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'properties'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tnumber__type__properties__query = {
	'number':string;
};
export class Cnumber__type__properties__query extends AlanNode {
	public readonly properties:{
		readonly number:Cnumber__type__properties__query.Dnumber
	};
	constructor(init:Tnumber__type__properties__query, public parent:Cproperties__query) {
		super();
		const $this = this;
		this.properties = {
			number: new Cnumber__type__properties__query.Dnumber(init['number'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_group__type__properties__query = {
	'state group':string;
};
export class Cstate_group__type__properties__query extends AlanNode {
	public readonly properties:{
		readonly state_group:Cstate_group__type__properties__query.Dstate_group
	};
	constructor(init:Tstate_group__type__properties__query, public parent:Cproperties__query) {
		super();
		const $this = this;
		this.properties = {
			state_group: new Cstate_group__type__properties__query.Dstate_group(init['state group'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state group'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext__type__properties__query = {
	'text':string;
};
export class Ctext__type__properties__query extends AlanNode {
	public readonly properties:{
		readonly text:Ctext__type__properties__query.Dtext
	};
	constructor(init:Ttext__type__properties__query, public parent:Cproperties__query) {
		super();
		const $this = this;
		this.properties = {
			text: new Ctext__type__properties__query.Dtext(init['text'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tacyclic_graph_tree_query = {
	'context node':Tid_path__client_request;
	'type':['collection', Tcollection__type__acyclic_graph_tree_query];
};
export class Cacyclic_graph_tree_query extends AlanNode {
	public readonly properties:{
		readonly context_node:Cid_path__client_request,
		readonly type:Cacyclic_graph_tree_query.Dtype
	};
	constructor(init:Tacyclic_graph_tree_query, public parent:Cclient_request) {
		super();
		const $this = this;
		this.properties = {
			context_node: new Cacyclic_graph_tree_query.Dcontext_node(init['context node'], $this),
			type: new Cacyclic_graph_tree_query.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'acyclic graph tree query'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection__type__acyclic_graph_tree_query = {
	'graph':string;
};
export class Ccollection__type__acyclic_graph_tree_query extends AlanNode {
	public readonly properties:{
		readonly graph:Ccollection__type__acyclic_graph_tree_query.Dgraph
	};
	public readonly inferences:{
		collection: () => application.Ccollection__type__property
	} = {
		collection: cache((detach:boolean) => {
			const client_request__type__acyclic_graph_tree_query__type__collection_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.context_node)
				.then(context => context?.component_root.output.result_node())
				.then(context => context?.component_root.output.location())
				.then(context => context?.variant.name === 'collection' ? context.variant.definition as application.Ccollection__type__property : undefined).result!;
		})

	}
	constructor(init:Tcollection__type__acyclic_graph_tree_query, public parent:Cacyclic_graph_tree_query) {
		super();
		const $this = this;
		this.properties = {
			graph: new Ccollection__type__acyclic_graph_tree_query.Dgraph(init['graph'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'collection'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection_query = {
	'aggregates':Record<string, Taggregates>;
	'collection path':Tcollection_query_path;
	'context node path':Tid_path__client_request;
	'filters':Tfilters;
	'select entries':'no'|['no', {}]|['yes', Tyes__select_entries];
};
export class Ccollection_query extends AlanNode {
	public readonly properties:{
		readonly aggregates:Ccollection_query.Daggregates,
		readonly collection_path:Ccollection_query_path,
		readonly context_node_path:Cid_path__client_request,
		readonly filters:Cfilters,
		readonly select_entries:Ccollection_query.Dselect_entries
	};
	constructor(init:Tcollection_query, public parent:Cclient_request) {
		super();
		const $this = this;
		this.properties = {
			aggregates: new Ccollection_query.Daggregates(init['aggregates'], $this),
			collection_path: new Ccollection_query.Dcollection_path(init['collection path'], $this),
			context_node_path: new Ccollection_query.Dcontext_node_path(init['context node path'], $this),
			filters: new Cfilters(init['filters'], $this),
			select_entries: new Ccollection_query.Dselect_entries(init['select entries'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'collection query'`; }
	public get entity() { return this.parent.entity; }
}
export type Taggregates = {
	'type':['state distribution', Tstate_distribution];
};
export class Caggregates extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly type:Caggregates.Dtype
	};
	constructor(key:string, init:Taggregates, public parent:Ccollection_query) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			type: new Caggregates.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'aggregates'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tstate_distribution = {
	'path':Tnode_path;
	'state group':string;
};
export class Cstate_distribution extends AlanNode {
	public readonly properties:{
		readonly path:Cnode_path,
		readonly state_group:Cstate_distribution.Dstate_group
	};
	constructor(init:Tstate_distribution, public parent:Caggregates) {
		super();
		const $this = this;
		this.properties = {
			path: new Cstate_distribution.Dpath(init['path'], $this),
			state_group: new Cstate_distribution.Dstate_group(init['state group'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state distribution'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfilters = {
	'todo filter':'no'|['no', {}]|['yes', Tyes__todo_filter];
	'value filters':Record<string, Tvalue_filters>;
};
export class Cfilters extends AlanNode {
	public readonly properties:{
		readonly todo_filter:Cfilters.Dtodo_filter,
		readonly value_filters:Cfilters.Dvalue_filters
	};
	constructor(init:Tfilters, public parent:Ccollection_query) {
		super();
		const $this = this;
		this.properties = {
			todo_filter: new Cfilters.Dtodo_filter(init['todo filter'], $this),
			value_filters: new Cfilters.Dvalue_filters(init['value filters'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'filters'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__todo_filter = {
};
export class Cno__todo_filter extends AlanNode {
	constructor(init:Tno__todo_filter, public parent:Cfilters) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'todo filter'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__todo_filter = {
	'path':Tnode_path;
};
export class Cyes__todo_filter extends AlanNode {
	public readonly properties:{
		readonly path:Cnode_path & { readonly inferences: {
			has_todo: () => application.Cyes__todo;
		} }
	};
	constructor(init:Tyes__todo_filter, public parent:Cfilters) {
		super();
		const $this = this;
		this.properties = {
			path: new Cyes__todo_filter.Dpath(init['path'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'todo filter'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tvalue_filters = {
	'path':Tnode_path;
	'type':['node', Tnode]|['property', Tproperty__type__value_filters];
};
export class Cvalue_filters extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly path:Cnode_path,
		readonly type:Cvalue_filters.Dtype
	};
	constructor(key:string, init:Tvalue_filters, public parent:Cfilters) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			path: new Cvalue_filters.Dpath(init['path'], $this),
			type: new Cvalue_filters.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value filters'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tnode = {
	'type':['containment', Tcontainment]|['existence', Texistence];
};
export class Cnode extends AlanNode {
	public readonly properties:{
		readonly type:Cnode.Dtype
	};
	constructor(init:Tnode, public parent:Cvalue_filters) {
		super();
		const $this = this;
		this.properties = {
			type: new Cnode.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'node'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcontainment = {
	'operator':'in'|['in', {}]|'not in'|['not in', {}];
	'references':Treference_filter_list;
};
export class Ccontainment extends AlanNode {
	public readonly properties:{
		readonly operator:Ccontainment.Doperator,
		readonly references:Creference_filter_list
	};
	constructor(init:Tcontainment, public parent:Cnode) {
		super();
		const $this = this;
		this.properties = {
			operator: new Ccontainment.Doperator(init['operator'], $this),
			references: new Ccontainment.Dreferences(init['references'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'containment'`; }
	public get entity() { return this.parent.entity; }
}
export type Tin__operator__containment = {
};
export class Cin__operator__containment extends AlanNode {
	constructor(init:Tin__operator__containment, public parent:Ccontainment) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operator'?'in'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnot_in__operator__containment = {
};
export class Cnot_in__operator__containment extends AlanNode {
	constructor(init:Tnot_in__operator__containment, public parent:Ccontainment) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operator'?'not in'`; }
	public get entity() { return this.parent.entity; }
}
export type Texistence = {
	'operator':'exists'|['exists', {}]|'not exists'|['not exists', {}];
};
export class Cexistence extends AlanNode {
	public readonly properties:{
		readonly operator:Cexistence.Doperator
	};
	constructor(init:Texistence, public parent:Cnode) {
		super();
		const $this = this;
		this.properties = {
			operator: new Cexistence.Doperator(init['operator'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'existence'`; }
	public get entity() { return this.parent.entity; }
}
export type Texists = {
};
export class Cexists extends AlanNode {
	constructor(init:Texists, public parent:Cexistence) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operator'?'exists'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnot_exists = {
};
export class Cnot_exists extends AlanNode {
	constructor(init:Tnot_exists, public parent:Cexistence) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operator'?'not exists'`; }
	public get entity() { return this.parent.entity; }
}
export type Tproperty__type__value_filters = {
	'property':string;
	'type':['collection', Tcollection__type__property__type__value_filters]|['number', Tnumber__type__property__type__value_filters]|['state group', Tstate_group__type__property__type__value_filters]|['text', Ttext__type__property__type__value_filters];
};
export class Cproperty__type__value_filters extends AlanNode {
	public readonly properties:{
		readonly property:Cproperty__type__value_filters.Dproperty,
		readonly type:Cproperty__type__value_filters.Dtype
	};
	constructor(init:Tproperty__type__value_filters, public parent:Cvalue_filters) {
		super();
		const $this = this;
		this.properties = {
			property: new Cproperty__type__value_filters.Dproperty(init['property'], $this),
			type: new Cproperty__type__value_filters.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'property'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection__type__property__type__value_filters = {
	'keys':Record<string, {}>;
	'operator':'in'|['in', {}]|'not in'|['not in', {}];
};
export class Ccollection__type__property__type__value_filters extends AlanNode {
	public readonly properties:{
		readonly keys:Ccollection__type__property__type__value_filters.Dkeys,
		readonly operator:Ccollection__type__property__type__value_filters.Doperator
	};
	public readonly inferences:{
		collection_type: () => application.Ccollection__type__property
	} = {
		collection_type: cache((detach:boolean) => {
			const client_request__type__collection_query__filters__value_filters__type__property__type__collection_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.property?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'collection') {
						return resolve(context.properties.type.state.node as application.Ccollection__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tcollection__type__property__type__value_filters, public parent:Cproperty__type__value_filters) {
		super();
		const $this = this;
		this.properties = {
			keys: new Ccollection__type__property__type__value_filters.Dkeys(init['keys'], $this),
			operator: new Ccollection__type__property__type__value_filters.Doperator(init['operator'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'collection'`; }
	public get entity() { return this.parent.entity; }
}
export type Tkeys__collection = {
};
export class Ckeys__collection extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	constructor(key:string, init:Tkeys__collection, public parent:Ccollection__type__property__type__value_filters) {
		super();
		const $this = this;
		this.key = key;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'keys'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tin__operator__collection = {
};
export class Cin__operator__collection extends AlanNode {
	constructor(init:Tin__operator__collection, public parent:Ccollection__type__property__type__value_filters) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operator'?'in'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnot_in__operator__collection = {
};
export class Cnot_in__operator__collection extends AlanNode {
	constructor(init:Tnot_in__operator__collection, public parent:Ccollection__type__property__type__value_filters) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operator'?'not in'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__property__type__value_filters = {
	'expression':Tnumber_filter_expression;
};
export class Cnumber__type__property__type__value_filters extends AlanNode {
	public readonly properties:{
		readonly expression:Cnumber_filter_expression
	};
	public readonly inferences:{
		number_type: () => application.Cnumber__type__property
	} = {
		number_type: cache((detach:boolean) => {
			const client_request__type__collection_query__filters__value_filters__type__property__type__number_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.property?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'number') {
						return resolve(context.properties.type.state.node as application.Cnumber__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tnumber__type__property__type__value_filters, public parent:Cproperty__type__value_filters) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cnumber__type__property__type__value_filters.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_group__type__property__type__value_filters = {
	'states to include':Record<string, {}>;
};
export class Cstate_group__type__property__type__value_filters extends AlanNode {
	public readonly properties:{
		readonly states_to_include:Cstate_group__type__property__type__value_filters.Dstates_to_include
	};
	public readonly inferences:{
		state_group_type: () => application.Cstate_group
	} = {
		state_group_type: cache((detach:boolean) => {
			const client_request__type__collection_query__filters__value_filters__type__property__type__state_group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.property?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'state group') {
						return resolve(context.properties.type.state.node as application.Cstate_group).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tstate_group__type__property__type__value_filters, public parent:Cproperty__type__value_filters) {
		super();
		const $this = this;
		this.properties = {
			states_to_include: new Cstate_group__type__property__type__value_filters.Dstates_to_include(init['states to include'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state group'`; }
	public get entity() { return this.parent.entity; }
}
export class Kstates_to_include extends Reference<application.Cstates__state_group, string> {
	constructor(key:string, $this:Cstates_to_include) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.inferences.state_group_type())
			.then(context => {
				const entry = context?.properties.states.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states to include'::key`; }
}
export type Tstates_to_include = {
};
export class Cstates_to_include extends AlanDictionaryEntry {
	public key:Kstates_to_include;
	public get key_value() { return this.key.entry; }
	constructor(key:string, init:Tstates_to_include, public parent:Cstate_group__type__property__type__value_filters) {
		super();
		const $this = this;
		this.key = new Kstates_to_include(key, $this);
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states to include'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Ttext__type__property__type__value_filters = {
	'expression':Tfilter_expression;
};
export class Ctext__type__property__type__value_filters extends AlanNode {
	public readonly properties:{
		readonly expression:Cfilter_expression
	};
	public readonly inferences:{
		text_type: () => application.Ctext__type__property
	} = {
		text_type: cache((detach:boolean) => {
			const client_request__type__collection_query__filters__value_filters__type__property__type__text_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.property?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'text') {
						return resolve(context.properties.type.state.node as application.Ctext__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Ttext__type__property__type__value_filters, public parent:Cproperty__type__value_filters) {
		super();
		const $this = this;
		this.properties = {
			expression: new Ctext__type__property__type__value_filters.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__select_entries = {
};
export class Cno__select_entries extends AlanNode {
	constructor(init:Tno__select_entries, public parent:Ccollection_query) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'select entries'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__select_entries = {
	'limit number of entries':number;
	'properties':Tnode_query;
};
export class Cyes__select_entries extends AlanNode {
	public readonly properties:{
		readonly limit_number_of_entries:Cyes__select_entries.Dlimit_number_of_entries,
		readonly properties:Cnode_query
	};
	constructor(init:Tyes__select_entries, public parent:Ccollection_query) {
		super();
		const $this = this;
		this.properties = {
			limit_number_of_entries: new Cyes__select_entries.Dlimit_number_of_entries(init['limit number of entries'], $this),
			properties: new Cyes__select_entries.Dproperties(init['properties'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'select entries'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcommand_execution = {
	'arguments':Tnode_initialization;
	'command':string;
	'context node':Tid_path__client_request;
};
export class Ccommand_execution extends AlanNode {
	public readonly properties:{
		readonly arguments:Cnode_initialization,
		readonly command:Ccommand_execution.Dcommand,
		readonly context_node:Cid_path__client_request
	};
	constructor(init:Tcommand_execution, public parent:Cclient_request) {
		super();
		const $this = this;
		this.properties = {
			arguments: new Ccommand_execution.Darguments(init['arguments'], $this),
			command: new Ccommand_execution.Dcommand(init['command'], $this),
			context_node: new Ccommand_execution.Dcontext_node(init['context node'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'command execution'`; }
	public get entity() { return this.parent.entity; }
}
export type Tmutation = {
	'context node':Tid_path__client_request;
	'node update':Tnode_update;
};
export class Cmutation extends AlanNode {
	public readonly properties:{
		readonly context_node:Cid_path__client_request,
		readonly node_update:Cnode_update
	};
	constructor(init:Tmutation, public parent:Cclient_request) {
		super();
		const $this = this;
		this.properties = {
			context_node: new Cmutation.Dcontext_node(init['context node'], $this),
			node_update: new Cmutation.Dnode_update(init['node update'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'mutation'`; }
	public get entity() { return this.parent.entity; }
}
export type Tpassword_mutation = {
	'new password':string;
	'old password':string;
};
export class Cpassword_mutation extends AlanNode {
	public readonly properties:{
		readonly new_password:string,
		readonly old_password:string
	};
	public readonly inferences:{
		require_user_passwords: () => application.Cyes__has_password_authentication
	} = {
		require_user_passwords: cache((detach:boolean) => {
			const client_request__type__password_mutation_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.root.input.application)
				.then(context => {
					if (context?.properties.has_dynamic_users.state.name === 'yes') {
						return resolve(context.properties.has_dynamic_users.state.node as application.Cyes__has_dynamic_users)
						.then(context => {
							if (context?.properties.has_password_authentication.state.name === 'yes') {
								return context.properties.has_password_authentication.state.node as application.Cyes__has_password_authentication;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tpassword_mutation, public parent:Cclient_request) {
		super();
		const $this = this;
		this.properties = {
			new_password: init['new password'],
			old_password: init['old password']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'password mutation'`; }
	public get entity() { return this.parent.entity; }
}
export type Tpassword_reset = {
	'username':string;
};
export class Cpassword_reset extends AlanNode {
	public readonly properties:{
		readonly username:string
	};
	public readonly inferences:{
		require_user_password: () => application.Cyes__has_password_authentication
	} = {
		require_user_password: cache((detach:boolean) => {
			const client_request__type__password_reset_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.root.input.application)
				.then(context => {
					if (context?.properties.has_dynamic_users.state.name === 'yes') {
						return resolve(context.properties.has_dynamic_users.state.node as application.Cyes__has_dynamic_users)
						.then(context => {
							if (context?.properties.has_password_authentication.state.name === 'yes') {
								return context.properties.has_password_authentication.state.node as application.Cyes__has_password_authentication;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tpassword_reset, public parent:Cclient_request) {
		super();
		const $this = this;
		this.properties = {
			username: init['username']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'password reset'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsubscription = {
	'context node':Tid_path__client_request;
	'mutation permissions':'ignore'|['ignore', {}]|'include'|['include', {}];
	'node subscription':Tnode_subscription;
};
export class Csubscription extends AlanNode {
	public readonly properties:{
		readonly context_node:Cid_path__client_request,
		readonly mutation_permissions:Csubscription.Dmutation_permissions,
		readonly node_subscription:Cnode_subscription
	};
	constructor(init:Tsubscription, public parent:Cclient_request) {
		super();
		const $this = this;
		this.properties = {
			context_node: new Csubscription.Dcontext_node(init['context node'], $this),
			mutation_permissions: new Csubscription.Dmutation_permissions(init['mutation permissions'], $this),
			node_subscription: new Csubscription.Dnode_subscription(init['node subscription'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'subscription'`; }
	public get entity() { return this.parent.entity; }
}
export type Tignore = {
};
export class Cignore extends AlanNode {
	constructor(init:Tignore, public parent:Csubscription) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'mutation permissions'?'ignore'`; }
	public get entity() { return this.parent.entity; }
}
export type Tinclude = {
};
export class Cinclude extends AlanNode {
	constructor(init:Tinclude, public parent:Csubscription) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'mutation permissions'?'include'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsubscription_deletion = {
};
export class Csubscription_deletion extends AlanNode {
	constructor(init:Tsubscription_deletion, public parent:Cclient_request) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'subscription deletion'`; }
	public get entity() { return this.parent.entity; }
}

/* property classes */
export namespace Caggregate {
	export class Dcollection extends Reference<application.Ccollection__type__property,string> {

		constructor(data:string, $this:Caggregate) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.head)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'collection') {
							return context.properties.type.state.node as application.Ccollection__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'collection'`; }
	}
	export class Dhead extends Cnode_path {
		constructor(data:Taggregate['head'], parent:Caggregate) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context_node()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Pmandatory).result!)
			}, 'head')
		}
	}
	export class Dtail extends Cnode_path {
		constructor(data:Taggregate['tail'], parent:Caggregate) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.collection?.ref)
					.then(context => context?.properties.node).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Poptional).result!)
			}, 'tail')
		}
	}
}
export namespace Ccollection_path {
	export class Dhas_steps extends StateGroup<
		{ name: 'no', node:Cno__has_steps__collection_path, init:Tno__has_steps__collection_path}|
		{ name: 'yes', node:Cyes__has_steps__collection_path, init:Tyes__has_steps__collection_path}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_steps__collection_path, parent:Ccollection_path) => new Cno__has_steps__collection_path(init, parent);
				case 'yes': return (init:Tyes__has_steps__collection_path, parent:Ccollection_path) => new Cyes__has_steps__collection_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_steps__collection_path;
				case 'yes': return finalize_yes__has_steps__collection_path;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcollection_path['has steps'], parent:Ccollection_path) {
			super(data, parent, 'has steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has steps'`; }
	}
}
export namespace Cyes__has_steps__collection_path {
	export class Dcollection extends Reference<application.Ccollection__type__property,string> {

		constructor(data:string, $this:Cyes__has_steps__collection_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.head)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'collection') {
							return context.properties.type.state.node as application.Ccollection__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'collection'`; }
	}
	export class Dhead extends Cconditional_child_path {
		constructor(data:Tyes__has_steps__collection_path['head'], parent:Cyes__has_steps__collection_path) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context_node()).result!)
			}, 'head')
		}
	}
	export class Dtail extends Ccollection_path {
		constructor(data:Tyes__has_steps__collection_path['tail'], parent:Cyes__has_steps__collection_path) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.collection?.ref)
					.then(context => context?.properties.node).result!)
			}, 'tail')
		}
	}
}
export namespace Ccollection_query_path {
	export class Dhas_steps extends StateGroup<
		{ name: 'no', node:Cno__has_steps__collection_query_path, init:Tno__has_steps__collection_query_path}|
		{ name: 'yes', node:Cyes__has_steps__collection_query_path, init:Tyes__has_steps__collection_query_path}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_steps__collection_query_path, parent:Ccollection_query_path) => new Cno__has_steps__collection_query_path(init, parent);
				case 'yes': return (init:Tyes__has_steps__collection_query_path, parent:Ccollection_query_path) => new Cyes__has_steps__collection_query_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_steps__collection_query_path;
				case 'yes': return finalize_yes__has_steps__collection_query_path;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcollection_query_path['has steps'], parent:Ccollection_query_path) {
			super(data, parent, 'has steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has steps'`; }
	}
}
export namespace Cyes__has_steps__collection_query_path {
	export class Dcollection extends Reference<application.Ccollection__type__property,string> {

		constructor(data:string, $this:Cyes__has_steps__collection_query_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.head)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'collection') {
							return context.properties.type.state.node as application.Ccollection__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'collection'`; }
	}
	export class Dhead extends Cconditional_child_path {
		constructor(data:Tyes__has_steps__collection_query_path['head'], parent:Cyes__has_steps__collection_query_path) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context_node()).result!)
			}, 'head')
		}
	}
	export class Dtail extends Ccollection_query_path {
		constructor(data:Tyes__has_steps__collection_query_path['tail'], parent:Cyes__has_steps__collection_query_path) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.type?.state.node.output.collection_node()).result!)
			}, 'tail')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'candidates', node:Ccandidates, init:Tcandidates}|
		{ name: 'existing entries', node:Cexisting_entries, init:Texisting_entries}> {
		protected initializer(state: 'candidates'|'existing entries') {
			switch (state) {
				case 'candidates': return (init:Tcandidates, parent:Cyes__has_steps__collection_query_path) => new Ccandidates(init, parent);
				case 'existing entries': return (init:Texisting_entries, parent:Cyes__has_steps__collection_query_path) => new Cexisting_entries(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'candidates'|'existing entries') {
			switch (state) {
				case 'candidates': return finalize_candidates;
				case 'existing entries': return finalize_existing_entries;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_steps__collection_query_path['type'], parent:Cyes__has_steps__collection_query_path) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccandidates {
	export class Dinclude_reference extends StateGroup<
		{ name: 'no', node:Cno__include_reference__candidates, init:Tno__include_reference__candidates}|
		{ name: 'yes', node:Cyes__include_reference__candidates, init:Tyes__include_reference__candidates}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__include_reference__candidates, parent:Ccandidates) => new Cno__include_reference__candidates(init, parent);
				case 'yes': return (init:Tyes__include_reference__candidates, parent:Ccandidates) => new Cyes__include_reference__candidates(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__include_reference__candidates;
				case 'yes': return finalize_yes__include_reference__candidates;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcandidates['include reference'], parent:Ccandidates) {
			super(data, parent, 'include reference');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'include reference'`; }
	}
}
export namespace Cconditional_child_path {
	export class Dhas_steps extends StateGroup<
		{ name: 'no', node:Cno__has_steps__conditional_child_path, init:Tno__has_steps__conditional_child_path}|
		{ name: 'yes', node:Cyes__has_steps__conditional_child_path, init:Tyes__has_steps__conditional_child_path}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_steps__conditional_child_path, parent:Cconditional_child_path) => new Cno__has_steps__conditional_child_path(init, parent);
				case 'yes': return (init:Tyes__has_steps__conditional_child_path, parent:Cconditional_child_path) => new Cyes__has_steps__conditional_child_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_steps__conditional_child_path;
				case 'yes': return finalize_yes__has_steps__conditional_child_path;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tconditional_child_path['has steps'], parent:Cconditional_child_path) {
			super(data, parent, 'has steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has steps'`; }
	}
}
export namespace Cyes__has_steps__conditional_child_path {
	export class Dtail extends Cconditional_child_path {
		constructor(data:Tyes__has_steps__conditional_child_path['tail'], parent:Cyes__has_steps__conditional_child_path) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.type?.state.node.output.result_node()).result!)
			}, 'tail')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'group', node:Cgroup__type__yes__has_steps__conditional_child_path, init:Tgroup__type__yes__has_steps__conditional_child_path}|
		{ name: 'state', node:Cstate__type__yes__has_steps__conditional_child_path, init:Tstate__type__yes__has_steps__conditional_child_path}> {
		protected initializer(state: 'group'|'state') {
			switch (state) {
				case 'group': return (init:Tgroup__type__yes__has_steps__conditional_child_path, parent:Cyes__has_steps__conditional_child_path) => new Cgroup__type__yes__has_steps__conditional_child_path(init, parent);
				case 'state': return (init:Tstate__type__yes__has_steps__conditional_child_path, parent:Cyes__has_steps__conditional_child_path) => new Cstate__type__yes__has_steps__conditional_child_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'group'|'state') {
			switch (state) {
				case 'group': return finalize_group__type__yes__has_steps__conditional_child_path;
				case 'state': return finalize_state__type__yes__has_steps__conditional_child_path;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_steps__conditional_child_path['type'], parent:Cyes__has_steps__conditional_child_path) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cgroup__type__yes__has_steps__conditional_child_path {
	export class Dgroup extends Reference<application.Cgroup__type__property,string> {

		constructor(data:string, $this:Cgroup__type__yes__has_steps__conditional_child_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.context_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'group') {
							return context.properties.type.state.node as application.Cgroup__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'group'`; }
	}
}
export namespace Cstate__type__yes__has_steps__conditional_child_path {
	export class Dstate extends Reference<application.Cstates__state_group,string> {

		constructor(data:string, $this:Cstate__type__yes__has_steps__conditional_child_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.state_group?.ref)
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
	export class Dstate_group extends Reference<application.Cstate_group,string> {

		constructor(data:string, $this:Cstate__type__yes__has_steps__conditional_child_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.context_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'state group') {
							return context.properties.type.state.node as application.Cstate_group;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state group'`; }
	}
}
export namespace Centry_point_path {
	export class Dhas_steps extends StateGroup<
		{ name: 'no', node:Cno__has_steps__entry_point_path, init:Tno__has_steps__entry_point_path}|
		{ name: 'yes', node:Cyes__has_steps__entry_point_path, init:Tyes__has_steps__entry_point_path}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_steps__entry_point_path, parent:Centry_point_path) => new Cno__has_steps__entry_point_path(init, parent);
				case 'yes': return (init:Tyes__has_steps__entry_point_path, parent:Centry_point_path) => new Cyes__has_steps__entry_point_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_steps__entry_point_path;
				case 'yes': return finalize_yes__has_steps__entry_point_path;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tentry_point_path['has steps'], parent:Centry_point_path) {
			super(data, parent, 'has steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has steps'`; }
	}
}
export namespace Cno__has_steps__entry_point_path {
	export class Dtext extends Reference<application.Ctext__type__property,string> {
		public readonly inferences:{
			reference: () => application.Cyes__has_reference
		}

		constructor(data:string, $this:Cno__has_steps__entry_point_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.context_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'text') {
							return context.properties.type.state.node as application.Ctext__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
			this.inferences = {
				reference: cache((detach:boolean) => {
					const client_request__entry_point_path__has_steps__no__text_nval = $this.properties.text.ref;
					return resolve($this.properties.text.ref)
						.then(context => client_request__entry_point_path__has_steps__no__text_nval)
						.then(context => {
							if (context?.properties.has_reference.state.name === 'yes') {
								return resolve(context.properties.has_reference.state.node as application.Cyes__has_reference).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'text'`; }
	}
}
export namespace Cyes__has_steps__entry_point_path {
	export class Dcollection extends Reference<application.Ccollection__type__property,string> {

		constructor(data:string, $this:Cyes__has_steps__entry_point_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.head)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'collection') {
							return context.properties.type.state.node as application.Ccollection__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'collection'`; }
	}
	export class Dhead extends Cconditional_child_path {
		constructor(data:Tyes__has_steps__entry_point_path['head'], parent:Cyes__has_steps__entry_point_path) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context_node()).result!)
			}, 'head')
		}
	}
	export class Dtail extends Centry_point_path {
		constructor(data:Tyes__has_steps__entry_point_path['tail'], parent:Cyes__has_steps__entry_point_path) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.collection?.ref)
					.then(context => context?.properties.node).result!)
			}, 'tail')
		}
	}
}
export namespace Cfilter_expression {
	export class Dhas_more_steps extends StateGroup<
		{ name: 'no', node:Cno__has_more_steps, init:Tno__has_more_steps}|
		{ name: 'yes', node:Cyes__has_more_steps, init:Tyes__has_more_steps}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_more_steps, parent:Cfilter_expression) => new Cno__has_more_steps(init, parent);
				case 'yes': return (init:Tyes__has_more_steps, parent:Cfilter_expression) => new Cyes__has_more_steps(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_more_steps;
				case 'yes': return finalize_yes__has_more_steps;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tfilter_expression['has more steps'], parent:Cfilter_expression) {
			super(data, parent, 'has more steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has more steps'`; }
	}
	export class Dtype extends StateGroup<
		{ name: 'alternatives', node:Calternatives__type, init:Talternatives__type}|
		{ name: 'wildcard', node:Cwildcard, init:Twildcard}> {
		protected initializer(state: 'alternatives'|'wildcard') {
			switch (state) {
				case 'alternatives': return (init:Talternatives__type, parent:Cfilter_expression) => new Calternatives__type(init, parent);
				case 'wildcard': return (init:Twildcard, parent:Cfilter_expression) => new Cwildcard(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'alternatives'|'wildcard') {
			switch (state) {
				case 'alternatives': return finalize_alternatives__type;
				case 'wildcard': return finalize_wildcard;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tfilter_expression['type'], parent:Cfilter_expression) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cyes__has_more_steps {
	export class Dtail extends Cfilter_expression {
		constructor(data:Tyes__has_more_steps['tail'], parent:Cyes__has_more_steps) {
			super(data, parent, 'tail')
		}
	}
}
export namespace Calternatives__type {
	export class Dalternatives extends AlanDictionary<{ node:Calternatives__alternatives, init:Talternatives__alternatives},Calternatives__type> {
		protected initialize(parent:Calternatives__type, key:string) { return new Calternatives__alternatives(key, {}, parent); }
		protected finalize = finalize_alternatives__alternatives
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'alternatives'`; }
		constructor(data:Talternatives__type['alternatives'], parent:Calternatives__type) {
			super(data, parent);
		}
	}
}
export namespace Cid_path__client_request {
	export class Dhas_steps extends StateGroup<
		{ name: 'no', node:Cno__has_steps__id_path, init:Tno__has_steps__id_path}|
		{ name: 'yes', node:Cyes__has_steps__id_path, init:Tyes__has_steps__id_path}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_steps__id_path, parent:Cid_path__client_request) => new Cno__has_steps__id_path(init, parent);
				case 'yes': return (init:Tyes__has_steps__id_path, parent:Cid_path__client_request) => new Cyes__has_steps__id_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_steps__id_path;
				case 'yes': return finalize_yes__has_steps__id_path;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tid_path__client_request['has steps'], parent:Cid_path__client_request) {
			super(data, parent, 'has steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has steps'`; }
	}
}
export namespace Cyes__has_steps__id_path {
	export class Dtail extends Cid_path__client_request {
		constructor(data:Tyes__has_steps__id_path['tail'], parent:Cyes__has_steps__id_path) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.type?.state.node.output.result_node()).result!)
			}, 'tail')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'collection entry', node:Ccollection_entry__type__yes__has_steps__id_path, init:Tcollection_entry__type__yes__has_steps__id_path}|
		{ name: 'group', node:Cgroup__type__yes__has_steps__id_path, init:Tgroup__type__yes__has_steps__id_path}|
		{ name: 'state', node:Cstate__type__yes__has_steps__id_path, init:Tstate__type__yes__has_steps__id_path}> {
		protected initializer(state: 'collection entry'|'group'|'state') {
			switch (state) {
				case 'collection entry': return (init:Tcollection_entry__type__yes__has_steps__id_path, parent:Cyes__has_steps__id_path) => new Ccollection_entry__type__yes__has_steps__id_path(init, parent);
				case 'group': return (init:Tgroup__type__yes__has_steps__id_path, parent:Cyes__has_steps__id_path) => new Cgroup__type__yes__has_steps__id_path(init, parent);
				case 'state': return (init:Tstate__type__yes__has_steps__id_path, parent:Cyes__has_steps__id_path) => new Cstate__type__yes__has_steps__id_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'collection entry'|'group'|'state') {
			switch (state) {
				case 'collection entry': return finalize_collection_entry__type__yes__has_steps__id_path;
				case 'group': return finalize_group__type__yes__has_steps__id_path;
				case 'state': return finalize_state__type__yes__has_steps__id_path;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_steps__id_path['type'], parent:Cyes__has_steps__id_path) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccollection_entry__type__yes__has_steps__id_path {
	export class Dcollection extends Reference<application.Ccollection__type__property,string> {

		constructor(data:string, $this:Ccollection_entry__type__yes__has_steps__id_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.context_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'collection') {
							return context.properties.type.state.node as application.Ccollection__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'collection'`; }
	}
}
export namespace Cgroup__type__yes__has_steps__id_path {
	export class Dgroup extends Reference<application.Cgroup__type__property,string> {

		constructor(data:string, $this:Cgroup__type__yes__has_steps__id_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.context_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'group') {
							return context.properties.type.state.node as application.Cgroup__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'group'`; }
	}
}
export namespace Cstate__type__yes__has_steps__id_path {
	export class Dstate extends Reference<application.Cstates__state_group,string> {

		constructor(data:string, $this:Cstate__type__yes__has_steps__id_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.state_group?.ref)
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
	export class Dstate_group extends Reference<application.Cstate_group,string> {

		constructor(data:string, $this:Cstate__type__yes__has_steps__id_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.context_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'state group') {
							return context.properties.type.state.node as application.Cstate_group;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state group'`; }
	}
}
export namespace Cnode_initialization {
	export class Dproperties extends AlanDictionary<{ node:Cproperties__node_initialization, init:Tproperties__node_initialization},Cnode_initialization> {
		protected initialize(parent:Cnode_initialization, key:string, entry_init:Tproperties__node_initialization) { return new Cproperties__node_initialization(key, entry_init, parent); }
		protected finalize = finalize_properties__node_initialization
		protected eval_required_keys(detach:boolean = false):void {
			let this_obj = this.parent;
			function do_include(client_request__node_initialization__properties_key_nval:application.Cproperty__type__attributes):boolean {
				const inferences:{
					value_source_is_user: (detach?:boolean) => application.Cexternal_source|undefined;} = {

					value_source_is_user: maybe_cache((detach:boolean) => resolve(client_request__node_initialization__properties_key_nval)
						.then(context => client_request__node_initialization__properties_key_nval)
						.then(context => context?.properties.type?.state.node.output.value_source())
						.then(context => context?.variant.name === 'base' ? context.variant.definition as application.Cbase_value_source : undefined)
						.then(context => context?.variant.name === 'external' ? context.variant.definition as application.Cexternal_source : undefined)?.result)
				}
				const $this = {
					inferences: inferences
				}
				if (inferences.value_source_is_user() === undefined)
					return false;
				return true;
			};
			resolve(this.parent)
			.then(() => this.parent)
			.then(context => context?.component_root.input.node_type())
			.then(context => {
				for (let [key,val] of context?.properties.attributes) {
					let tail_obj = resolve(val)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					}).result;
					if (tail_obj !== undefined && do_include(tail_obj)) {
						assert(this.get(key) !== undefined);
					}
				}
				return undefined;
			});
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'properties'`; }
		constructor(data:Tnode_initialization['properties'], parent:Cnode_initialization) {
			super(data, parent);
		}
	}
}
export namespace Cproperties__node_initialization {
	export class Dtype extends StateGroup<
		{ name: 'collection', node:Ccollection__type__properties__node_initialization, init:Tcollection__type__properties__node_initialization}|
		{ name: 'file', node:Cfile__type__properties__node_initialization, init:Tfile__type__properties__node_initialization}|
		{ name: 'group', node:Cgroup__type__properties__node_initialization, init:Tgroup__type__properties__node_initialization}|
		{ name: 'number', node:Cnumber__type__properties__node_initialization, init:Tnumber__type__properties__node_initialization}|
		{ name: 'state group', node:Cstate_group__type__properties__node_initialization, init:Tstate_group__type__properties__node_initialization}|
		{ name: 'text', node:Ctext__type__properties__node_initialization, init:Ttext__type__properties__node_initialization}> {
		protected initializer(state: 'collection'|'file'|'group'|'number'|'state group'|'text') {
			switch (state) {
				case 'collection': return (init:Tcollection__type__properties__node_initialization, parent:Cproperties__node_initialization) => new Ccollection__type__properties__node_initialization(init, parent);
				case 'file': return (init:Tfile__type__properties__node_initialization, parent:Cproperties__node_initialization) => new Cfile__type__properties__node_initialization(init, parent);
				case 'group': return (init:Tgroup__type__properties__node_initialization, parent:Cproperties__node_initialization) => new Cgroup__type__properties__node_initialization(init, parent);
				case 'number': return (init:Tnumber__type__properties__node_initialization, parent:Cproperties__node_initialization) => new Cnumber__type__properties__node_initialization(init, parent);
				case 'state group': return (init:Tstate_group__type__properties__node_initialization, parent:Cproperties__node_initialization) => new Cstate_group__type__properties__node_initialization(init, parent);
				case 'text': return (init:Ttext__type__properties__node_initialization, parent:Cproperties__node_initialization) => new Ctext__type__properties__node_initialization(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'collection'|'file'|'group'|'number'|'state group'|'text') {
			switch (state) {
				case 'collection': return finalize_collection__type__properties__node_initialization;
				case 'file': return finalize_file__type__properties__node_initialization;
				case 'group': return finalize_group__type__properties__node_initialization;
				case 'number': return finalize_number__type__properties__node_initialization;
				case 'state group': return finalize_state_group__type__properties__node_initialization;
				case 'text': return finalize_text__type__properties__node_initialization;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tproperties__node_initialization['type'], parent:Cproperties__node_initialization) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccollection__type__properties__node_initialization {
	export class Dentries extends AlanDictionary<{ node:Centries__collection__type__properties__node_initialization, init:Tentries__collection__type__properties__node_initialization},Ccollection__type__properties__node_initialization> {
		protected initialize(parent:Ccollection__type__properties__node_initialization, key:string, entry_init:Tentries__collection__type__properties__node_initialization) { return new Centries__collection__type__properties__node_initialization(key, entry_init, parent); }
		protected finalize = finalize_entries__collection__type__properties__node_initialization
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'entries'`; }
		constructor(data:Tcollection__type__properties__node_initialization['entries'], parent:Ccollection__type__properties__node_initialization) {
			super(data, parent);
		}
	}
}
export namespace Centries__collection__type__properties__node_initialization {
	export class Dinitialization extends Cnode_initialization {
		constructor(data:Tentries__collection__type__properties__node_initialization['initialization'], parent:Centries__collection__type__properties__node_initialization) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.collection())
					.then(context => context?.properties.node).result!)
			}, 'initialization')
		}
	}
}
export namespace Cfile__type__properties__node_initialization {
}
export namespace Cgroup__type__properties__node_initialization {
	export class Dinitialization extends Cnode_initialization {
		constructor(data:Tgroup__type__properties__node_initialization['initialization'], parent:Cgroup__type__properties__node_initialization) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.group())
					.then(context => context?.properties.node).result!)
			}, 'initialization')
		}
	}
}
export namespace Cnumber__type__properties__node_initialization {
	export class Dvalue extends AlanInteger {
		public readonly inferences:{
			set_type: () => application.Cnumber_set_type
		}
		constructor(data:Tnumber__type__properties__node_initialization['value'], parent:Cnumber__type__properties__node_initialization) {
			super(data, parent);this.inferences = {
				set_type: cache((detach:boolean) => {
					const client_request__node_initialization__properties__type__number__value_nval = this;
					return resolve(this)
						.then(switch_context => { 
							const value = resolve(switch_context)
								.then(context => client_request__node_initialization__properties__type__number__value_nval).result.value;
							if (value < 0){
								return resolve(switch_context)
									.then(() => parent)
									.then(context => context?.inferences.number())
									.then(context => context?.properties.set_type?.state.node.output.set_type())
									.then(context => context?.variant.name === 'integer' ? context.variant.definition as application.Cinteger__number_set_type : undefined).result;
							}
							else if (value > 0){
								return resolve(switch_context)
									.then(() => parent)
									.then(() => application.Cnumber_set_type.Ppositive).result;
							}
							else if (value == 0){
								return resolve(switch_context)
									.then(() => parent)
									.then(context => context?.inferences.number())
									.then(context => context?.properties.set_type?.state.node.output.set_type())
									.then(context => context?.variant.name === 'integer' ? context.variant.definition as application.Cinteger__number_set_type : undefined).result;
							}
							else {
								throw new Error(`Missing case handler.`);
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'value'`; }
	}
}
export namespace Cstate_group__type__properties__node_initialization {
	export class Dinitialization extends Cnode_initialization {
		constructor(data:Tstate_group__type__properties__node_initialization['initialization'], parent:Cstate_group__type__properties__node_initialization) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.state?.ref)
					.then(context => context?.properties.node).result!)
			}, 'initialization')
		}
	}
	export class Dstate extends Reference<application.Cstates__state_group,string> {

		constructor(data:string, $this:Cstate_group__type__properties__node_initialization) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.state_group())
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
}
export namespace Ctext__type__properties__node_initialization {
}
export namespace Cnode_path {
	export class Dhas_steps extends StateGroup<
		{ name: 'no', node:Cno__has_steps__node_path, init:Tno__has_steps__node_path}|
		{ name: 'yes', node:Cyes__has_steps__node_path, init:Tyes__has_steps__node_path}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_steps__node_path, parent:Cnode_path) => new Cno__has_steps__node_path(init, parent);
				case 'yes': return (init:Tyes__has_steps__node_path, parent:Cnode_path) => new Cyes__has_steps__node_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_steps__node_path;
				case 'yes': return finalize_yes__has_steps__node_path;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnode_path['has steps'], parent:Cnode_path) {
			super(data, parent, 'has steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has steps'`; }
	}
}
export namespace Cyes__has_steps__node_path {
	export class Dtail extends Cnode_path {
		constructor(data:Tyes__has_steps__node_path['tail'], parent:Cyes__has_steps__node_path) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.type?.state.node.output.result_node()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.existence_support()).result!)
			}, 'tail')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'collection entry', node:Ccollection_entry__type__yes__has_steps__node_path, init:Tcollection_entry__type__yes__has_steps__node_path}|
		{ name: 'group', node:Cgroup__type__yes__has_steps__node_path, init:Tgroup__type__yes__has_steps__node_path}|
		{ name: 'parent', node:Cparent, init:Tparent}|
		{ name: 'reference', node:Creference, init:Treference}|
		{ name: 'reference rule', node:Creference_rule, init:Treference_rule}|
		{ name: 'state', node:Cstate__type__yes__has_steps__node_path, init:Tstate__type__yes__has_steps__node_path}|
		{ name: 'state rule', node:Cstate_rule, init:Tstate_rule}> {
		protected initializer(state: 'collection entry'|'group'|'parent'|'reference'|'reference rule'|'state'|'state rule') {
			switch (state) {
				case 'collection entry': return (init:Tcollection_entry__type__yes__has_steps__node_path, parent:Cyes__has_steps__node_path) => new Ccollection_entry__type__yes__has_steps__node_path(init, parent);
				case 'group': return (init:Tgroup__type__yes__has_steps__node_path, parent:Cyes__has_steps__node_path) => new Cgroup__type__yes__has_steps__node_path(init, parent);
				case 'parent': return (init:Tparent, parent:Cyes__has_steps__node_path) => new Cparent(init, parent);
				case 'reference': return (init:Treference, parent:Cyes__has_steps__node_path) => new Creference(init, parent);
				case 'reference rule': return (init:Treference_rule, parent:Cyes__has_steps__node_path) => new Creference_rule(init, parent);
				case 'state': return (init:Tstate__type__yes__has_steps__node_path, parent:Cyes__has_steps__node_path) => new Cstate__type__yes__has_steps__node_path(init, parent);
				case 'state rule': return (init:Tstate_rule, parent:Cyes__has_steps__node_path) => new Cstate_rule(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'collection entry'|'group'|'parent'|'reference'|'reference rule'|'state'|'state rule') {
			switch (state) {
				case 'collection entry': return finalize_collection_entry__type__yes__has_steps__node_path;
				case 'group': return finalize_group__type__yes__has_steps__node_path;
				case 'parent': return finalize_parent;
				case 'reference': return finalize_reference;
				case 'reference rule': return finalize_reference_rule;
				case 'state': return finalize_state__type__yes__has_steps__node_path;
				case 'state rule': return finalize_state_rule;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_steps__node_path['type'], parent:Cyes__has_steps__node_path) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccollection_entry__type__yes__has_steps__node_path {
	export class Dcollection extends Reference<application.Ccollection__type__property,string> {

		constructor(data:string, $this:Ccollection_entry__type__yes__has_steps__node_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.context_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'collection') {
							return context.properties.type.state.node as application.Ccollection__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'collection'`; }
	}
}
export namespace Cgroup__type__yes__has_steps__node_path {
	export class Dgroup extends Reference<application.Cgroup__type__property,string> {

		constructor(data:string, $this:Cgroup__type__yes__has_steps__node_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.context_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'group') {
							return context.properties.type.state.node as application.Cgroup__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'group'`; }
	}
}
export namespace Creference {
	export class Dtext extends Reference<application.Ctext__type__property,string> {
		public readonly inferences:{
			behaviour: () => application.Cexistence__application,
			reference: () => application.Cyes__has_reference
		}

		constructor(data:string, $this:Creference) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.context_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'text') {
							return context.properties.type.state.node as application.Ctext__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
			this.inferences = {
				behaviour: cache((detach:boolean) => {
					const client_request__node_path__has_steps__yes__type__reference__text_nval = $this.properties.text.ref;
					return resolve($this.properties.text.ref)
						.then(context => {
							const left = resolve(context)
								.then(() => $this.properties.text.inferences.reference())
								.then(context => context?.properties.behaviour)
								.then(context => context?.component_root.output.existence()).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.component_root.input.existence_support()).result;
							return left!.variant.widening_index >= right.variant.widening_index ? left : undefined
						}).result!;
				})
				,
				reference: cache((detach:boolean) => {
					const client_request__node_path__has_steps__yes__type__reference__text_nval = $this.properties.text.ref;
					return resolve($this.properties.text.ref)
						.then(context => client_request__node_path__has_steps__yes__type__reference__text_nval)
						.then(context => {
							if (context?.properties.has_reference.state.name === 'yes') {
								return resolve(context.properties.has_reference.state.node as application.Cyes__has_reference).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'text'`; }
	}
}
export namespace Creference_rule {
	export class Dproperty extends Reference<application.Ctext__type__property,string> {
		public readonly inferences:{
			reference: () => application.Cyes__has_reference
		}

		constructor(data:string, $this:Creference_rule) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.context_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'text') {
							return context.properties.type.state.node as application.Ctext__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
			this.inferences = {
				reference: cache((detach:boolean) => {
					const client_request__node_path__has_steps__yes__type__reference_rule__property_nval = $this.properties.property.ref;
					return resolve($this.properties.property.ref)
						.then(context => client_request__node_path__has_steps__yes__type__reference_rule__property_nval)
						.then(context => {
							if (context?.properties.has_reference.state.name === 'yes') {
								return resolve(context.properties.has_reference.state.node as application.Cyes__has_reference).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'property'`; }
	}
	export class Drule extends Reference<application.Crules,string> {
		public readonly inferences:{
			behaviour: () => application.Cexistence__application
		}

		constructor(data:string, $this:Creference_rule) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.property?.inferences.reference())
				.then(context => context?.properties.rules)
				.then(context => {
					const entry = context?.properties.rules.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				behaviour: cache((detach:boolean) => {
					const client_request__node_path__has_steps__yes__type__reference_rule__rule_nval = $this.properties.rule.ref;
					return resolve($this.properties.rule.ref)
						.then(context => {
							const left = resolve(context)
								.then(context => client_request__node_path__has_steps__yes__type__reference_rule__rule_nval)
								.then(context => context?.properties.behaviour)
								.then(context => context?.component_root.output.existence()).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.component_root.input.existence_support()).result;
							return left!.variant.widening_index >= right.variant.widening_index ? left : undefined
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'rule'`; }
	}
}
export namespace Cstate__type__yes__has_steps__node_path {
	export class Dstate extends Reference<application.Cstates__state_group,string> {

		constructor(data:string, $this:Cstate__type__yes__has_steps__node_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.state_group?.ref)
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
	export class Dstate_group extends Reference<application.Cstate_group,string> {

		constructor(data:string, $this:Cstate__type__yes__has_steps__node_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.context_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'state group') {
							return context.properties.type.state.node as application.Cstate_group;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state group'`; }
	}
}
export namespace Cstate_rule {
	export class Drule extends Reference<application.Crules,string> {
		public readonly inferences:{
			behaviour: () => application.Cexistence__application,
			mandatory_reference: () => application.Cmandatory__type
		}

		constructor(data:string, $this:Cstate_rule) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.state())
				.then(context => context?.properties.rules)
				.then(context => {
					const entry = context?.properties.rules.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				behaviour: cache((detach:boolean) => {
					const client_request__node_path__has_steps__yes__type__state_rule__rule_nval = $this.properties.rule.ref;
					return resolve($this.properties.rule.ref)
						.then(context => {
							const left = resolve(context)
								.then(context => client_request__node_path__has_steps__yes__type__state_rule__rule_nval)
								.then(context => context?.properties.behaviour)
								.then(context => context?.component_root.output.existence()).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.component_root.input.existence_support()).result;
							return left!.variant.widening_index >= right.variant.widening_index ? left : undefined
						}).result!;
				})
				,
				mandatory_reference: cache((detach:boolean) => {
					const client_request__node_path__has_steps__yes__type__state_rule__rule_nval = $this.properties.rule.ref;
					return resolve($this.properties.rule.ref)
						.then(context => client_request__node_path__has_steps__yes__type__state_rule__rule_nval)
						.then(context => context?.properties.behaviour)
						.then(context => {
							if (context?.properties.type.state.name === 'mandatory') {
								return resolve(context.properties.type.state.node as application.Cmandatory__type).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'rule'`; }
	}
}
export namespace Cnode_query {
	export class Dproperties extends AlanDictionary<{ node:Cproperties__node_query, init:Tproperties__node_query},Cnode_query> {
		protected initialize(parent:Cnode_query, key:string, entry_init:Tproperties__node_query) { return new Cproperties__node_query(key, entry_init, parent); }
		protected finalize = finalize_properties__node_query
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'properties'`; }
		constructor(data:Tnode_query['properties'], parent:Cnode_query) {
			super(data, parent);
		}
	}
}
export namespace Cproperties__node_query {
	export class Dpath extends Cnode_path {
		constructor(data:Tproperties__node_query['path'], parent:Cproperties__node_query) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.node_type()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Poptional).result!)
			}, 'path')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'collection', node:Ccollection__type__properties__node_query, init:Tcollection__type__properties__node_query}|
		{ name: 'file', node:Cfile__type__properties__node_query, init:Tfile__type__properties__node_query}|
		{ name: 'id path', node:Cid_path__type, init:Tid_path__type}|
		{ name: 'number', node:Cnumber__type__properties__node_query, init:Tnumber__type__properties__node_query}|
		{ name: 'state group', node:Cstate_group__type__properties__node_query, init:Tstate_group__type__properties__node_query}|
		{ name: 'text', node:Ctext__type__properties__node_query, init:Ttext__type__properties__node_query}> {
		protected initializer(state: 'collection'|'file'|'id path'|'number'|'state group'|'text') {
			switch (state) {
				case 'collection': return (init:Tcollection__type__properties__node_query, parent:Cproperties__node_query) => new Ccollection__type__properties__node_query(init, parent);
				case 'file': return (init:Tfile__type__properties__node_query, parent:Cproperties__node_query) => new Cfile__type__properties__node_query(init, parent);
				case 'id path': return (init:Tid_path__type, parent:Cproperties__node_query) => new Cid_path__type(init, parent);
				case 'number': return (init:Tnumber__type__properties__node_query, parent:Cproperties__node_query) => new Cnumber__type__properties__node_query(init, parent);
				case 'state group': return (init:Tstate_group__type__properties__node_query, parent:Cproperties__node_query) => new Cstate_group__type__properties__node_query(init, parent);
				case 'text': return (init:Ttext__type__properties__node_query, parent:Cproperties__node_query) => new Ctext__type__properties__node_query(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'collection'|'file'|'id path'|'number'|'state group'|'text') {
			switch (state) {
				case 'collection': return finalize_collection__type__properties__node_query;
				case 'file': return finalize_file__type__properties__node_query;
				case 'id path': return finalize_id_path__type;
				case 'number': return finalize_number__type__properties__node_query;
				case 'state group': return finalize_state_group__type__properties__node_query;
				case 'text': return finalize_text__type__properties__node_query;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tproperties__node_query['type'], parent:Cproperties__node_query) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccollection__type__properties__node_query {
	export class Dcollection extends Reference<application.Ccollection__type__property,string> {

		constructor(data:string, $this:Ccollection__type__properties__node_query) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'collection') {
							return context.properties.type.state.node as application.Ccollection__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'collection'`; }
	}
	export class Dinclude_graph_endpoints extends StateGroup<
		{ name: 'no', node:Cno__include_graph_endpoints__collection__type__properties, init:Tno__include_graph_endpoints__collection__type__properties}|
		{ name: 'yes', node:Cyes__include_graph_endpoints__collection__type__properties, init:Tyes__include_graph_endpoints__collection__type__properties}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__include_graph_endpoints__collection__type__properties, parent:Ccollection__type__properties__node_query) => new Cno__include_graph_endpoints__collection__type__properties(init, parent);
				case 'yes': return (init:Tyes__include_graph_endpoints__collection__type__properties, parent:Ccollection__type__properties__node_query) => new Cyes__include_graph_endpoints__collection__type__properties(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__include_graph_endpoints__collection__type__properties;
				case 'yes': return finalize_yes__include_graph_endpoints__collection__type__properties;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcollection__type__properties__node_query['include graph endpoints'], parent:Ccollection__type__properties__node_query) {
			super(data, parent, 'include graph endpoints');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'include graph endpoints'`; }
	}
	export class Dproperties extends Cnode_query {
		constructor(data:Tcollection__type__properties__node_query['properties'], parent:Ccollection__type__properties__node_query) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.collection?.ref)
					.then(context => context?.properties.node).result!)
			}, 'properties')
		}
	}
}
export namespace Cfile__type__properties__node_query {
	export class Dfile extends Reference<application.Cfile,string> {

		constructor(data:string, $this:Cfile__type__properties__node_query) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'file') {
							return context.properties.type.state.node as application.Cfile;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'file'`; }
	}
}
export namespace Cnumber__type__properties__node_query {
	export class Dexpression extends Cnumber_expression {
		constructor(data:Tnumber__type__properties__node_query['expression'], parent:Cnumber__type__properties__node_query) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.path)
					.then(context => context?.component_root.output.result_node()).result!)
			}, 'expression')
		}
	}
}
export namespace Cstate_group__type__properties__node_query {
	export class Dstate_group extends Reference<application.Cstate_group,string> {

		constructor(data:string, $this:Cstate_group__type__properties__node_query) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'state group') {
							return context.properties.type.state.node as application.Cstate_group;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state group'`; }
	}
}
export namespace Ctext__type__properties__node_query {
	export class Dexpression extends Ctext_expression {
		constructor(data:Ttext__type__properties__node_query['expression'], parent:Ctext__type__properties__node_query) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.path)
					.then(context => context?.component_root.output.result_node()).result!)
			}, 'expression')
		}
	}
}
export namespace Cnode_subscription {
	export class Dattributes extends AlanDictionary<{ node:Cattributes, init:Tattributes},Cnode_subscription> {
		protected initialize(parent:Cnode_subscription, key:string, entry_init:Tattributes) { return new Cattributes(key, entry_init, parent); }
		protected finalize = finalize_attributes
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'attributes'`; }
		constructor(data:Tnode_subscription['attributes'], parent:Cnode_subscription) {
			super(data, parent);
		}
	}
}
export namespace Cattributes {
	export class Dtype extends StateGroup<
		{ name: 'command', node:Ccommand, init:Tcommand}|
		{ name: 'property', node:Cproperty__type__attributes, init:Tproperty__type__attributes}> {
		protected initializer(state: 'command'|'property') {
			switch (state) {
				case 'command': return (init:Tcommand, parent:Cattributes) => new Ccommand(init, parent);
				case 'property': return (init:Tproperty__type__attributes, parent:Cattributes) => new Cproperty__type__attributes(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'command'|'property') {
			switch (state) {
				case 'command': return finalize_command;
				case 'property': return finalize_property__type__attributes;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tattributes['type'], parent:Cattributes) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cproperty__type__attributes {
	export class Dtype extends StateGroup<
		{ name: 'collection', node:Ccollection__type__property__type__attributes, init:Tcollection__type__property__type__attributes}|
		{ name: 'file', node:Cfile__type__property, init:Tfile__type__property}|
		{ name: 'group', node:Cgroup__type__property, init:Tgroup__type__property}|
		{ name: 'number', node:Cnumber__type__property__type__attributes, init:Tnumber__type__property__type__attributes}|
		{ name: 'state group', node:Cstate_group__type__property__type__attributes, init:Tstate_group__type__property__type__attributes}|
		{ name: 'text', node:Ctext__type__property__type__attributes, init:Ttext__type__property__type__attributes}> {
		protected initializer(state: 'collection'|'file'|'group'|'number'|'state group'|'text') {
			switch (state) {
				case 'collection': return (init:Tcollection__type__property__type__attributes, parent:Cproperty__type__attributes) => new Ccollection__type__property__type__attributes(init, parent);
				case 'file': return (init:Tfile__type__property, parent:Cproperty__type__attributes) => new Cfile__type__property(init, parent);
				case 'group': return (init:Tgroup__type__property, parent:Cproperty__type__attributes) => new Cgroup__type__property(init, parent);
				case 'number': return (init:Tnumber__type__property__type__attributes, parent:Cproperty__type__attributes) => new Cnumber__type__property__type__attributes(init, parent);
				case 'state group': return (init:Tstate_group__type__property__type__attributes, parent:Cproperty__type__attributes) => new Cstate_group__type__property__type__attributes(init, parent);
				case 'text': return (init:Ttext__type__property__type__attributes, parent:Cproperty__type__attributes) => new Ctext__type__property__type__attributes(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'collection'|'file'|'group'|'number'|'state group'|'text') {
			switch (state) {
				case 'collection': return finalize_collection__type__property__type__attributes;
				case 'file': return finalize_file__type__property;
				case 'group': return finalize_group__type__property;
				case 'number': return finalize_number__type__property__type__attributes;
				case 'state group': return finalize_state_group__type__property__type__attributes;
				case 'text': return finalize_text__type__property__type__attributes;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tproperty__type__attributes['type'], parent:Cproperty__type__attributes) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccollection__type__property__type__attributes {
	export class Dinclude_graph_endpoints extends StateGroup<
		{ name: 'no', node:Cno__include_graph_endpoints__collection__type__property, init:Tno__include_graph_endpoints__collection__type__property}|
		{ name: 'yes', node:Cyes__include_graph_endpoints__collection__type__property, init:Tyes__include_graph_endpoints__collection__type__property}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__include_graph_endpoints__collection__type__property, parent:Ccollection__type__property__type__attributes) => new Cno__include_graph_endpoints__collection__type__property(init, parent);
				case 'yes': return (init:Tyes__include_graph_endpoints__collection__type__property, parent:Ccollection__type__property__type__attributes) => new Cyes__include_graph_endpoints__collection__type__property(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__include_graph_endpoints__collection__type__property;
				case 'yes': return finalize_yes__include_graph_endpoints__collection__type__property;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcollection__type__property__type__attributes['include graph endpoints'], parent:Ccollection__type__property__type__attributes) {
			super(data, parent, 'include graph endpoints');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'include graph endpoints'`; }
	}
	export class Dsubscribe_on extends StateGroup<
		{ name: 'entries', node:Centries__subscribe_on, init:Tentries__subscribe_on}|
		{ name: 'updates', node:Cupdates, init:Tupdates}> {
		protected initializer(state: 'entries'|'updates') {
			switch (state) {
				case 'entries': return (init:Tentries__subscribe_on, parent:Ccollection__type__property__type__attributes) => new Centries__subscribe_on(init, parent);
				case 'updates': return (init:Tupdates, parent:Ccollection__type__property__type__attributes) => new Cupdates(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'entries'|'updates') {
			switch (state) {
				case 'entries': return finalize_entries__subscribe_on;
				case 'updates': return finalize_updates;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcollection__type__property__type__attributes['subscribe on'], parent:Ccollection__type__property__type__attributes) {
			super(data, parent, 'subscribe on');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'subscribe on'`; }
	}
}
export namespace Centries__subscribe_on {
	export class Dkey_filter extends StateGroup<
		{ name: 'no', node:Cno__key_filter, init:Tno__key_filter}|
		{ name: 'yes', node:Cyes__key_filter, init:Tyes__key_filter}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__key_filter, parent:Centries__subscribe_on) => new Cno__key_filter(init, parent);
				case 'yes': return (init:Tyes__key_filter, parent:Centries__subscribe_on) => new Cyes__key_filter(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__key_filter;
				case 'yes': return finalize_yes__key_filter;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tentries__subscribe_on['key filter'], parent:Centries__subscribe_on) {
			super(data, parent, 'key filter');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'key filter'`; }
	}
	export class Dsubscription extends Cnode_subscription {
		constructor(data:Tentries__subscribe_on['subscription'], parent:Centries__subscribe_on) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.collection())
					.then(context => context?.properties.node).result!)
			}, 'subscription')
		}
	}
}
export namespace Cyes__key_filter {
	export class Dkeys extends AlanDictionary<{ node:Ckeys__yes, init:Tkeys__yes},Cyes__key_filter> {
		protected initialize(parent:Cyes__key_filter, key:string) { return new Ckeys__yes(key, {}, parent); }
		protected finalize = finalize_keys__yes
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'keys'`; }
		constructor(data:Tyes__key_filter['keys'], parent:Cyes__key_filter) {
			super(data, parent);
		}
	}
}
export namespace Cgroup__type__property {
	export class Dsubscription extends Cnode_subscription {
		constructor(data:Tgroup__type__property['subscription'], parent:Cgroup__type__property) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.group())
					.then(context => context?.properties.node).result!)
			}, 'subscription')
		}
	}
}
export namespace Cstate_group__type__property__type__attributes {
	export class Dstates extends AlanDictionary<{ node:Cstates__state_group, init:Tstates__state_group},Cstate_group__type__property__type__attributes> {
		protected initialize(parent:Cstate_group__type__property__type__attributes, key:string, entry_init:Tstates__state_group) { return new Cstates__state_group(key, entry_init, parent); }
		protected finalize = finalize_states__state_group
		protected eval_required_keys(detach:boolean = false):void {
			let this_obj = this.parent;
			function do_include(client_request__node_subscription__attributes__type__property__type__state_group__states_key_nval:application.Cstates__state_group):boolean {
				return true;
			};
			resolve(this.parent)
			.then(() => this.parent)
			.then(context => context?.inferences.state_group())
			.then(context => {
				for (let [key,val] of context?.properties.states) {
					let tail_obj = resolve(val).result;
					if (tail_obj !== undefined && do_include(tail_obj)) {
						assert(this.get(key) !== undefined);
					}
				}
				return undefined;
			});
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'states'`; }
		constructor(data:Tstate_group__type__property__type__attributes['states'], parent:Cstate_group__type__property__type__attributes) {
			super(data, parent);
		}
	}
}
export namespace Cstates__state_group {
	export class Dsubscription extends Cnode_subscription {
		constructor(data:Tstates__state_group['subscription'], parent:Cstates__state_group) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.key?.ref)
					.then(context => context?.properties.node).result!)
			}, 'subscription')
		}
	}
}
export namespace Ctext__type__property__type__attributes {
	export class Dinclude_reference extends StateGroup<
		{ name: 'no', node:Cno__include_reference__text, init:Tno__include_reference__text}|
		{ name: 'yes', node:Cyes__include_reference__text, init:Tyes__include_reference__text}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__include_reference__text, parent:Ctext__type__property__type__attributes) => new Cno__include_reference__text(init, parent);
				case 'yes': return (init:Tyes__include_reference__text, parent:Ctext__type__property__type__attributes) => new Cyes__include_reference__text(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__include_reference__text;
				case 'yes': return finalize_yes__include_reference__text;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Ttext__type__property__type__attributes['include reference'], parent:Ctext__type__property__type__attributes) {
			super(data, parent, 'include reference');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'include reference'`; }
	}
}
export namespace Cnode_update {
	export class Dproperties extends AlanDictionary<{ node:Cproperties__node_update, init:Tproperties__node_update},Cnode_update> {
		protected initialize(parent:Cnode_update, key:string, entry_init:Tproperties__node_update) { return new Cproperties__node_update(key, entry_init, parent); }
		protected finalize = finalize_properties__node_update
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'properties'`; }
		constructor(data:Tnode_update['properties'], parent:Cnode_update) {
			super(data, parent);
		}
	}
}
export namespace Cproperties__node_update {
	export class Dtype extends StateGroup<
		{ name: 'collection', node:Ccollection__type__properties__node_update, init:Tcollection__type__properties__node_update}|
		{ name: 'file', node:Cfile__type__properties__node_update, init:Tfile__type__properties__node_update}|
		{ name: 'group', node:Cgroup__type__properties__node_update, init:Tgroup__type__properties__node_update}|
		{ name: 'number', node:Cnumber__type__properties__node_update, init:Tnumber__type__properties__node_update}|
		{ name: 'state group', node:Cstate_group__type__properties__node_update, init:Tstate_group__type__properties__node_update}|
		{ name: 'text', node:Ctext__type__properties__node_update, init:Ttext__type__properties__node_update}> {
		protected initializer(state: 'collection'|'file'|'group'|'number'|'state group'|'text') {
			switch (state) {
				case 'collection': return (init:Tcollection__type__properties__node_update, parent:Cproperties__node_update) => new Ccollection__type__properties__node_update(init, parent);
				case 'file': return (init:Tfile__type__properties__node_update, parent:Cproperties__node_update) => new Cfile__type__properties__node_update(init, parent);
				case 'group': return (init:Tgroup__type__properties__node_update, parent:Cproperties__node_update) => new Cgroup__type__properties__node_update(init, parent);
				case 'number': return (init:Tnumber__type__properties__node_update, parent:Cproperties__node_update) => new Cnumber__type__properties__node_update(init, parent);
				case 'state group': return (init:Tstate_group__type__properties__node_update, parent:Cproperties__node_update) => new Cstate_group__type__properties__node_update(init, parent);
				case 'text': return (init:Ttext__type__properties__node_update, parent:Cproperties__node_update) => new Ctext__type__properties__node_update(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'collection'|'file'|'group'|'number'|'state group'|'text') {
			switch (state) {
				case 'collection': return finalize_collection__type__properties__node_update;
				case 'file': return finalize_file__type__properties__node_update;
				case 'group': return finalize_group__type__properties__node_update;
				case 'number': return finalize_number__type__properties__node_update;
				case 'state group': return finalize_state_group__type__properties__node_update;
				case 'text': return finalize_text__type__properties__node_update;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tproperties__node_update['type'], parent:Cproperties__node_update) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccollection__type__properties__node_update {
	export class Dentries extends AlanDictionary<{ node:Centries__collection__type__properties__node_update, init:Tentries__collection__type__properties__node_update},Ccollection__type__properties__node_update> {
		protected initialize(parent:Ccollection__type__properties__node_update, key:string, entry_init:Tentries__collection__type__properties__node_update) { return new Centries__collection__type__properties__node_update(key, entry_init, parent); }
		protected finalize = finalize_entries__collection__type__properties__node_update
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'entries'`; }
		constructor(data:Tcollection__type__properties__node_update['entries'], parent:Ccollection__type__properties__node_update) {
			super(data, parent);
		}
	}
}
export namespace Centries__collection__type__properties__node_update {
	export class Dtype extends StateGroup<
		{ name: 'create', node:Ccreate, init:Tcreate}|
		{ name: 'remove', node:Cremove, init:Tremove}|
		{ name: 'update', node:Cupdate__type__entries, init:Tupdate__type__entries}> {
		protected initializer(state: 'create'|'remove'|'update') {
			switch (state) {
				case 'create': return (init:Tcreate, parent:Centries__collection__type__properties__node_update) => new Ccreate(init, parent);
				case 'remove': return (init:Tremove, parent:Centries__collection__type__properties__node_update) => new Cremove(init, parent);
				case 'update': return (init:Tupdate__type__entries, parent:Centries__collection__type__properties__node_update) => new Cupdate__type__entries(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'create'|'remove'|'update') {
			switch (state) {
				case 'create': return finalize_create;
				case 'remove': return finalize_remove;
				case 'update': return finalize_update__type__entries;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tentries__collection__type__properties__node_update['type'], parent:Centries__collection__type__properties__node_update) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccreate {
	export class Dinitialization extends Cnode_initialization {
		constructor(data:Tcreate['initialization'], parent:Ccreate) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.collection())
					.then(context => context?.properties.node).result!)
			}, 'initialization')
		}
	}
}
export namespace Cupdate__type__entries {
	export class Dupdate extends Cnode_update {
		constructor(data:Tupdate__type__entries['update'], parent:Cupdate__type__entries) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.collection())
					.then(context => context?.properties.node).result!)
			}, 'update')
		}
	}
}
export namespace Cfile__type__properties__node_update {
}
export namespace Cgroup__type__properties__node_update {
	export class Dupdate extends Cnode_update {
		constructor(data:Tgroup__type__properties__node_update['update'], parent:Cgroup__type__properties__node_update) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.group())
					.then(context => context?.properties.node).result!)
			}, 'update')
		}
	}
}
export namespace Cnumber__type__properties__node_update {
	export class Dnew_value extends AlanInteger {
		public readonly inferences:{
			set_type: () => application.Cnumber_set_type
		}
		constructor(data:Tnumber__type__properties__node_update['new value'], parent:Cnumber__type__properties__node_update) {
			super(data, parent);this.inferences = {
				set_type: cache((detach:boolean) => {
					const client_request__node_update__properties__type__number__new_value_nval = this;
					return resolve(this)
						.then(switch_context => { 
							const value = resolve(switch_context)
								.then(context => client_request__node_update__properties__type__number__new_value_nval).result.value;
							if (value < 0){
								return resolve(switch_context)
									.then(() => parent)
									.then(context => context?.inferences.number())
									.then(context => context?.properties.set_type?.state.node.output.set_type())
									.then(context => context?.variant.name === 'integer' ? context.variant.definition as application.Cinteger__number_set_type : undefined).result;
							}
							else if (value > 0){
								return resolve(switch_context)
									.then(() => parent)
									.then(() => application.Cnumber_set_type.Ppositive).result;
							}
							else if (value == 0){
								return resolve(switch_context)
									.then(() => parent)
									.then(context => context?.inferences.number())
									.then(context => context?.properties.set_type?.state.node.output.set_type())
									.then(context => context?.variant.name === 'integer' ? context.variant.definition as application.Cinteger__number_set_type : undefined).result;
							}
							else {
								throw new Error(`Missing case handler.`);
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'new value'`; }
	}
}
export namespace Cstate_group__type__properties__node_update {
	export class Dstate extends Reference<application.Cstates__state_group,string> {

		constructor(data:string, $this:Cstate_group__type__properties__node_update) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.state_group())
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
	export class Dtype extends StateGroup<
		{ name: 'set', node:Cset, init:Tset}|
		{ name: 'update', node:Cupdate__type__state_group, init:Tupdate__type__state_group}> {
		protected initializer(state: 'set'|'update') {
			switch (state) {
				case 'set': return (init:Tset, parent:Cstate_group__type__properties__node_update) => new Cset(init, parent);
				case 'update': return (init:Tupdate__type__state_group, parent:Cstate_group__type__properties__node_update) => new Cupdate__type__state_group(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'set'|'update') {
			switch (state) {
				case 'set': return finalize_set;
				case 'update': return finalize_update__type__state_group;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tstate_group__type__properties__node_update['type'], parent:Cstate_group__type__properties__node_update) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cset {
	export class Dinitialization extends Cnode_initialization {
		constructor(data:Tset['initialization'], parent:Cset) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.state?.ref)
					.then(context => context?.properties.node).result!)
			}, 'initialization')
		}
	}
}
export namespace Cupdate__type__state_group {
	export class Dupdate extends Cnode_update {
		constructor(data:Tupdate__type__state_group['update'], parent:Cupdate__type__state_group) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.state?.ref)
					.then(context => context?.properties.node).result!)
			}, 'update')
		}
	}
}
export namespace Ctext__type__properties__node_update {
}
export namespace Cnumber_aggregate {
	export class Daggregate extends Caggregate {
		constructor(data:Tnumber_aggregate['aggregate'], parent:Cnumber_aggregate) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context_node()).result!)
			}, 'aggregate')
		}
	}
	export class Dproperty_name extends Reference<application.Cnumber__type__property,string> {

		constructor(data:string, $this:Cnumber_aggregate) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.aggregate)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'number') {
							return context.properties.type.state.node as application.Cnumber__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'property name'`; }
	}
}
export namespace Cnumber_expression {
	export class Dtype extends StateGroup<
		{ name: 'count', node:Ccount, init:Tcount}|
		{ name: 'division', node:Cdivision, init:Tdivision}|
		{ name: 'maximum', node:Cmaximum, init:Tmaximum}|
		{ name: 'minimum', node:Cminimum, init:Tminimum}|
		{ name: 'number property', node:Cnumber_property, init:Tnumber_property}|
		{ name: 'product', node:Cproduct, init:Tproduct}|
		{ name: 'remainder', node:Cremainder, init:Tremainder}|
		{ name: 'state based', node:Cstate_based, init:Tstate_based}|
		{ name: 'sum', node:Csum, init:Tsum}|
		{ name: 'sum list', node:Csum_list, init:Tsum_list}|
		{ name: 'value', node:Cvalue, init:Tvalue}> {
		protected initializer(state: 'count'|'division'|'maximum'|'minimum'|'number property'|'product'|'remainder'|'state based'|'sum'|'sum list'|'value') {
			switch (state) {
				case 'count': return (init:Tcount, parent:Cnumber_expression) => new Ccount(init, parent);
				case 'division': return (init:Tdivision, parent:Cnumber_expression) => new Cdivision(init, parent);
				case 'maximum': return (init:Tmaximum, parent:Cnumber_expression) => new Cmaximum(init, parent);
				case 'minimum': return (init:Tminimum, parent:Cnumber_expression) => new Cminimum(init, parent);
				case 'number property': return (init:Tnumber_property, parent:Cnumber_expression) => new Cnumber_property(init, parent);
				case 'product': return (init:Tproduct, parent:Cnumber_expression) => new Cproduct(init, parent);
				case 'remainder': return (init:Tremainder, parent:Cnumber_expression) => new Cremainder(init, parent);
				case 'state based': return (init:Tstate_based, parent:Cnumber_expression) => new Cstate_based(init, parent);
				case 'sum': return (init:Tsum, parent:Cnumber_expression) => new Csum(init, parent);
				case 'sum list': return (init:Tsum_list, parent:Cnumber_expression) => new Csum_list(init, parent);
				case 'value': return (init:Tvalue, parent:Cnumber_expression) => new Cvalue(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'count'|'division'|'maximum'|'minimum'|'number property'|'product'|'remainder'|'state based'|'sum'|'sum list'|'value') {
			switch (state) {
				case 'count': return finalize_count;
				case 'division': return finalize_division;
				case 'maximum': return finalize_maximum;
				case 'minimum': return finalize_minimum;
				case 'number property': return finalize_number_property;
				case 'product': return finalize_product;
				case 'remainder': return finalize_remainder;
				case 'state based': return finalize_state_based;
				case 'sum': return finalize_sum;
				case 'sum list': return finalize_sum_list;
				case 'value': return finalize_value;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnumber_expression['type'], parent:Cnumber_expression) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccount {
	export class Daggregate extends Caggregate {
		constructor(data:Tcount['aggregate'], parent:Ccount) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context_node()).result!)
			}, 'aggregate')
		}
	}
}
export namespace Cdivision {
	export class Ddenominator extends Csigned_number_property {
		constructor(data:Tdivision['denominator'], parent:Cdivision) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context_node()).result!)
			}, 'denominator')
		}
	}
	export class Dnumerator extends Csigned_number_property {
		constructor(data:Tdivision['numerator'], parent:Cdivision) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context_node()).result!)
			}, 'numerator')
		}
	}
	export class Drounding extends StateGroup<
		{ name: 'ceil', node:Cceil, init:Tceil}|
		{ name: 'floor', node:Cfloor, init:Tfloor}|
		{ name: 'ordinary', node:Cordinary, init:Tordinary}> {
		protected initializer(state: 'ceil'|'floor'|'ordinary') {
			switch (state) {
				case 'ceil': return (init:Tceil, parent:Cdivision) => new Cceil(init, parent);
				case 'floor': return (init:Tfloor, parent:Cdivision) => new Cfloor(init, parent);
				case 'ordinary': return (init:Tordinary, parent:Cdivision) => new Cordinary(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'ceil'|'floor'|'ordinary') {
			switch (state) {
				case 'ceil': return finalize_ceil;
				case 'floor': return finalize_floor;
				case 'ordinary': return finalize_ordinary;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tdivision['rounding'], parent:Cdivision) {
			super(data, parent, 'rounding');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'rounding'`; }
	}
}
export namespace Cmaximum {
	export class Dleft_expression extends Cnumber_expression {
		constructor(data:Tmaximum['left expression'], parent:Cmaximum) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context_node()).result!)
			}, 'left expression')
		}
	}
	export class Dright_expression extends Cnumber_expression {
		constructor(data:Tmaximum['right expression'], parent:Cmaximum) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context_node()).result!)
			}, 'right expression')
		}
	}
}
export namespace Cminimum {
	export class Dleft_expression extends Cnumber_expression {
		constructor(data:Tminimum['left expression'], parent:Cminimum) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context_node()).result!)
			}, 'left expression')
		}
	}
	export class Dright_expression extends Cnumber_expression {
		constructor(data:Tminimum['right expression'], parent:Cminimum) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context_node()).result!)
			}, 'right expression')
		}
	}
}
export namespace Cnumber_property {
	export class Dsigned_number_property extends Csigned_number_property {
		constructor(data:Tnumber_property['signed number property'], parent:Cnumber_property) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context_node()).result!)
			}, 'signed number property')
		}
	}
}
export namespace Cproduct {
	export class Dnumbers extends Csigned_number_property_list {
		constructor(data:Tproduct['numbers'], parent:Cproduct) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context_node()).result!)
			}, 'numbers')
		}
	}
}
export namespace Cremainder {
	export class Dmodulus extends Csigned_number_property {
		constructor(data:Tremainder['modulus'], parent:Cremainder) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context_node()).result!)
			}, 'modulus')
		}
	}
	export class Dnumber extends Csigned_number_property {
		constructor(data:Tremainder['number'], parent:Cremainder) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context_node()).result!)
			}, 'number')
		}
	}
}
export namespace Cstate_based {
	export class Dpath extends Cnode_path {
		constructor(data:Tstate_based['path'], parent:Cstate_based) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context_node()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Pmandatory).result!)
			}, 'path')
		}
	}
	export class Dstate_group extends Reference<application.Cstate_group,string> {

		constructor(data:string, $this:Cstate_based) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'state group') {
							return context.properties.type.state.node as application.Cstate_group;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state group'`; }
	}
	export class Dstates extends AlanDictionary<{ node:Cstates__state_based, init:Tstates__state_based},Cstate_based> {
		protected initialize(parent:Cstate_based, key:string, entry_init:Tstates__state_based) { return new Cstates__state_based(key, entry_init, parent); }
		protected finalize = finalize_states__state_based
		protected eval_required_keys(detach:boolean = false):void {
			let this_obj = this.parent;
			function do_include(client_request__number_expression__type__state_based__states_key_nval:application.Cstates__state_group):boolean {
				return true;
			};
			resolve(this.parent)
			.then(() => this.parent)
			.then(context => context?.properties.state_group?.ref)
			.then(context => {
				for (let [key,val] of context?.properties.states) {
					let tail_obj = resolve(val).result;
					if (tail_obj !== undefined && do_include(tail_obj)) {
						assert(this.get(key) !== undefined);
					}
				}
				return undefined;
			});
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'states'`; }
		constructor(data:Tstate_based['states'], parent:Cstate_based) {
			super(data, parent);
		}
	}
}
export namespace Cstates__state_based {
	export class Dvalue extends Cnumber_expression {
		constructor(data:Tstates__state_based['value'], parent:Cstates__state_based) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.key?.ref)
					.then(context => context?.properties.node).result!)
			}, 'value')
		}
	}
}
export namespace Csum {
	export class Dnumber_aggregate extends Cnumber_aggregate {
		constructor(data:Tsum['number aggregate'], parent:Csum) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context_node()).result!)
			}, 'number aggregate')
		}
	}
}
export namespace Csum_list {
	export class Dnumbers extends Csigned_number_property_list {
		constructor(data:Tsum_list['numbers'], parent:Csum_list) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context_node()).result!)
			}, 'numbers')
		}
	}
}
export namespace Cvalue {
	export class Dvalue extends AlanInteger {
		constructor(data:Tvalue['value'], parent:Cvalue) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'value'`; }
	}
}
export namespace Cnumber_filter_expression {
	export class Doperator extends StateGroup<
		{ name: 'equals', node:Cequals, init:Tequals}|
		{ name: 'range', node:Crange, init:Trange}> {
		protected initializer(state: 'equals'|'range') {
			switch (state) {
				case 'equals': return (init:Tequals, parent:Cnumber_filter_expression) => new Cequals(init, parent);
				case 'range': return (init:Trange, parent:Cnumber_filter_expression) => new Crange(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'equals'|'range') {
			switch (state) {
				case 'equals': return finalize_equals;
				case 'range': return finalize_range;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnumber_filter_expression['operator'], parent:Cnumber_filter_expression) {
			super(data, parent, 'operator');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'operator'`; }
	}
}
export namespace Cequals {
	export class Dcriterium extends AlanInteger {
		constructor(data:Tequals['criterium'], parent:Cequals) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'criterium'`; }
	}
}
export namespace Crange {
	export class Dgreater_than extends StateGroup<
		{ name: 'no', node:Cno__greater_than, init:Tno__greater_than}|
		{ name: 'yes', node:Cyes__greater_than, init:Tyes__greater_than}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__greater_than, parent:Crange) => new Cno__greater_than(init, parent);
				case 'yes': return (init:Tyes__greater_than, parent:Crange) => new Cyes__greater_than(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__greater_than;
				case 'yes': return finalize_yes__greater_than;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Trange['greater than'], parent:Crange) {
			super(data, parent, 'greater than');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'greater than'`; }
	}
	export class Dless_than extends StateGroup<
		{ name: 'no', node:Cno__less_than, init:Tno__less_than}|
		{ name: 'yes', node:Cyes__less_than, init:Tyes__less_than}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__less_than, parent:Crange) => new Cno__less_than(init, parent);
				case 'yes': return (init:Tyes__less_than, parent:Crange) => new Cyes__less_than(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__less_than;
				case 'yes': return finalize_yes__less_than;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Trange['less than'], parent:Crange) {
			super(data, parent, 'less than');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'less than'`; }
	}
}
export namespace Cyes__greater_than {
	export class Dcriterium extends AlanInteger {
		constructor(data:Tyes__greater_than['criterium'], parent:Cyes__greater_than) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'criterium'`; }
	}
}
export namespace Cyes__less_than {
	export class Dcriterium extends AlanInteger {
		constructor(data:Tyes__less_than['criterium'], parent:Cyes__less_than) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'criterium'`; }
	}
}
export namespace Creference_filter_list {
	export class Dhas_alternative extends StateGroup<
		{ name: 'no', node:Cno__has_alternative, init:Tno__has_alternative}|
		{ name: 'yes', node:Cyes__has_alternative, init:Tyes__has_alternative}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_alternative, parent:Creference_filter_list) => new Cno__has_alternative(init, parent);
				case 'yes': return (init:Tyes__has_alternative, parent:Creference_filter_list) => new Cyes__has_alternative(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_alternative;
				case 'yes': return finalize_yes__has_alternative;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Treference_filter_list['has alternative'], parent:Creference_filter_list) {
			super(data, parent, 'has alternative');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has alternative'`; }
	}
	export class Dreferenced_node extends Cid_path__client_request {
		public readonly inferences:{
			entity_constraint: () => application.Centity__application
		}
		constructor(data:Treference_filter_list['referenced node'], parent:Creference_filter_list) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root.input.application)
					.then(context => context?.properties.root).result!)
			}, 'referenced node')
			this.inferences = {
				entity_constraint: cache((detach:boolean) => {
					const client_request__reference_filter_list__referenced_node_nval = this;
					return resolve(this)
						.then(context => {
							const left = resolve(context)
								.then(context => client_request__reference_filter_list__referenced_node_nval)
								.then(context => context?.component_root.output.result_node())
								.then(context => context?.component_root.output.entity()).result;
							const right = resolve(context)
								.then(() => parent)
								.then(context => context?.component_root.input.entity_node()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
	}
}
export namespace Cyes__has_alternative {
	export class Dalternative extends Creference_filter_list {
		constructor(data:Tyes__has_alternative['alternative'], parent:Cyes__has_alternative) {
			super(data, parent, {
				entity_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.entity_node()).result!)
			}, 'alternative')
		}
	}
}
export namespace Csigned_number_property {
	export class Dpath extends Cnode_path {
		constructor(data:Tsigned_number_property['path'], parent:Csigned_number_property) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context_node()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Pmandatory).result!)
			}, 'path')
		}
	}
	export class Dproperty_name extends Reference<application.Cnumber__type__property,string> {

		constructor(data:string, $this:Csigned_number_property) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'number') {
							return context.properties.type.state.node as application.Cnumber__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'property name'`; }
	}
	export class Dsign extends StateGroup<
		{ name: 'negative', node:Cnegative, init:Tnegative}|
		{ name: 'positive', node:Cpositive, init:Tpositive}> {
		protected initializer(state: 'negative'|'positive') {
			switch (state) {
				case 'negative': return (init:Tnegative, parent:Csigned_number_property) => new Cnegative(init, parent);
				case 'positive': return (init:Tpositive, parent:Csigned_number_property) => new Cpositive(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'negative'|'positive') {
			switch (state) {
				case 'negative': return finalize_negative;
				case 'positive': return finalize_positive;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tsigned_number_property['sign'], parent:Csigned_number_property) {
			super(data, parent, 'sign');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'sign'`; }
	}
}
export namespace Csigned_number_property_list {
	export class Dhas_element extends StateGroup<
		{ name: 'no', node:Cno__has_element__signed_number_property_list, init:Tno__has_element__signed_number_property_list}|
		{ name: 'yes', node:Cyes__has_element__signed_number_property_list, init:Tyes__has_element__signed_number_property_list}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_element__signed_number_property_list, parent:Csigned_number_property_list) => new Cno__has_element__signed_number_property_list(init, parent);
				case 'yes': return (init:Tyes__has_element__signed_number_property_list, parent:Csigned_number_property_list) => new Cyes__has_element__signed_number_property_list(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_element__signed_number_property_list;
				case 'yes': return finalize_yes__has_element__signed_number_property_list;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tsigned_number_property_list['has element'], parent:Csigned_number_property_list) {
			super(data, parent, 'has element');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has element'`; }
	}
}
export namespace Cyes__has_element__signed_number_property_list {
	export class Dsigned_number_property extends Csigned_number_property {
		constructor(data:Tyes__has_element__signed_number_property_list['signed number property'], parent:Cyes__has_element__signed_number_property_list) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context_node()).result!)
			}, 'signed number property')
		}
	}
	export class Dtail extends Csigned_number_property_list {
		constructor(data:Tyes__has_element__signed_number_property_list['tail'], parent:Cyes__has_element__signed_number_property_list) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context_node()).result!)
			}, 'tail')
		}
	}
}
export namespace Csingular_text_expression {
	export class Dtype extends StateGroup<
		{ name: 'dynamic', node:Cdynamic, init:Tdynamic}|
		{ name: 'static', node:Cstatic, init:Tstatic}> {
		protected initializer(state: 'dynamic'|'static') {
			switch (state) {
				case 'dynamic': return (init:Tdynamic, parent:Csingular_text_expression) => new Cdynamic(init, parent);
				case 'static': return (init:Tstatic, parent:Csingular_text_expression) => new Cstatic(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'dynamic'|'static') {
			switch (state) {
				case 'dynamic': return finalize_dynamic;
				case 'static': return finalize_static;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tsingular_text_expression['type'], parent:Csingular_text_expression) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cdynamic {
	export class Dpath extends Cnode_path {
		constructor(data:Tdynamic['path'], parent:Cdynamic) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context_node()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Pmandatory).result!)
			}, 'path')
		}
	}
	export class Dtext extends Reference<application.Ctext__type__property,string> {

		constructor(data:string, $this:Cdynamic) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'text') {
							return context.properties.type.state.node as application.Ctext__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'text'`; }
	}
}
export namespace Cstatic {
}
export namespace Csingular_text_expression_list {
	export class Dhas_element extends StateGroup<
		{ name: 'no', node:Cno__has_element__singular_text_expression_list, init:Tno__has_element__singular_text_expression_list}|
		{ name: 'yes', node:Cyes__has_element__singular_text_expression_list, init:Tyes__has_element__singular_text_expression_list}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_element__singular_text_expression_list, parent:Csingular_text_expression_list) => new Cno__has_element__singular_text_expression_list(init, parent);
				case 'yes': return (init:Tyes__has_element__singular_text_expression_list, parent:Csingular_text_expression_list) => new Cyes__has_element__singular_text_expression_list(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_element__singular_text_expression_list;
				case 'yes': return finalize_yes__has_element__singular_text_expression_list;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tsingular_text_expression_list['has element'], parent:Csingular_text_expression_list) {
			super(data, parent, 'has element');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has element'`; }
	}
}
export namespace Cyes__has_element__singular_text_expression_list {
	export class Dsingular_text_expression extends Csingular_text_expression {
		constructor(data:Tyes__has_element__singular_text_expression_list['singular text expression'], parent:Cyes__has_element__singular_text_expression_list) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context_node()).result!)
			}, 'singular text expression')
		}
	}
	export class Dtail extends Csingular_text_expression_list {
		constructor(data:Tyes__has_element__singular_text_expression_list['tail'], parent:Cyes__has_element__singular_text_expression_list) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context_node()).result!)
			}, 'tail')
		}
	}
}
export namespace Ctext_expression {
	export class Dtype extends StateGroup<
		{ name: 'concatenation', node:Cconcatenation, init:Tconcatenation}|
		{ name: 'singular', node:Csingular, init:Tsingular}> {
		protected initializer(state: 'concatenation'|'singular') {
			switch (state) {
				case 'concatenation': return (init:Tconcatenation, parent:Ctext_expression) => new Cconcatenation(init, parent);
				case 'singular': return (init:Tsingular, parent:Ctext_expression) => new Csingular(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'concatenation'|'singular') {
			switch (state) {
				case 'concatenation': return finalize_concatenation;
				case 'singular': return finalize_singular;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Ttext_expression['type'], parent:Ctext_expression) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cconcatenation {
	export class Dparts extends Csingular_text_expression_list {
		constructor(data:Tconcatenation['parts'], parent:Cconcatenation) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context_node()).result!)
			}, 'parts')
		}
	}
}
export namespace Csingular {
	export class Dexpression extends Csingular_text_expression {
		constructor(data:Tsingular['expression'], parent:Csingular) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.context_node()).result!)
			}, 'expression')
		}
	}
}
export namespace Cclient_request {
	export class Dtype extends StateGroup<
		{ name: 'acyclic graph list query', node:Cacyclic_graph_list_query, init:Tacyclic_graph_list_query}|
		{ name: 'acyclic graph tree query', node:Cacyclic_graph_tree_query, init:Tacyclic_graph_tree_query}|
		{ name: 'collection query', node:Ccollection_query, init:Tcollection_query}|
		{ name: 'command execution', node:Ccommand_execution, init:Tcommand_execution}|
		{ name: 'mutation', node:Cmutation, init:Tmutation}|
		{ name: 'password mutation', node:Cpassword_mutation, init:Tpassword_mutation}|
		{ name: 'password reset', node:Cpassword_reset, init:Tpassword_reset}|
		{ name: 'subscription', node:Csubscription, init:Tsubscription}|
		{ name: 'subscription deletion', node:Csubscription_deletion, init:Tsubscription_deletion}> {
		protected initializer(state: 'acyclic graph list query'|'acyclic graph tree query'|'collection query'|'command execution'|'mutation'|'password mutation'|'password reset'|'subscription'|'subscription deletion') {
			switch (state) {
				case 'acyclic graph list query': return (init:Tacyclic_graph_list_query, parent:Cclient_request) => new Cacyclic_graph_list_query(init, parent);
				case 'acyclic graph tree query': return (init:Tacyclic_graph_tree_query, parent:Cclient_request) => new Cacyclic_graph_tree_query(init, parent);
				case 'collection query': return (init:Tcollection_query, parent:Cclient_request) => new Ccollection_query(init, parent);
				case 'command execution': return (init:Tcommand_execution, parent:Cclient_request) => new Ccommand_execution(init, parent);
				case 'mutation': return (init:Tmutation, parent:Cclient_request) => new Cmutation(init, parent);
				case 'password mutation': return (init:Tpassword_mutation, parent:Cclient_request) => new Cpassword_mutation(init, parent);
				case 'password reset': return (init:Tpassword_reset, parent:Cclient_request) => new Cpassword_reset(init, parent);
				case 'subscription': return (init:Tsubscription, parent:Cclient_request) => new Csubscription(init, parent);
				case 'subscription deletion': return (init:Tsubscription_deletion, parent:Cclient_request) => new Csubscription_deletion(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'acyclic graph list query'|'acyclic graph tree query'|'collection query'|'command execution'|'mutation'|'password mutation'|'password reset'|'subscription'|'subscription deletion') {
			switch (state) {
				case 'acyclic graph list query': return finalize_acyclic_graph_list_query;
				case 'acyclic graph tree query': return finalize_acyclic_graph_tree_query;
				case 'collection query': return finalize_collection_query;
				case 'command execution': return finalize_command_execution;
				case 'mutation': return finalize_mutation;
				case 'password mutation': return finalize_password_mutation;
				case 'password reset': return finalize_password_reset;
				case 'subscription': return finalize_subscription;
				case 'subscription deletion': return finalize_subscription_deletion;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tclient_request['type'], parent:Cclient_request) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cacyclic_graph_list_query {
	export class Dcontext_node extends Cid_path__client_request {
		constructor(data:Tacyclic_graph_list_query['context node'], parent:Cacyclic_graph_list_query) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root.input.application)
					.then(context => context?.properties.root).result!)
			}, 'context node')
		}
	}
	export class Dentry_points extends Centry_point_path {
		constructor(data:Tacyclic_graph_list_query['entry points'], parent:Cacyclic_graph_list_query) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.context_node)
					.then(context => context?.component_root.output.result_node()).result!)
			}, 'entry points')
		}
	}
	export class Dgraph extends Reference<application.Cgraphs,string> {

		constructor(data:string, $this:Cacyclic_graph_list_query) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.entry_points)
				.then(context => context?.component_root.output.result_collection())
				.then(context => context?.properties.graph_constraints)
				.then(context => {
					const entry = context?.properties.graphs.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'graph'`; }
	}
}
export namespace Cquery {
	export class Dcollection_path extends Ccollection_path {
		constructor(data:Tquery['collection path'], parent:Cquery) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.entry_points)
					.then(context => context?.component_root.output.result_collection())
					.then(context => context?.properties.node).result!)
			}, 'collection path')
		}
	}
	export class Dproperties extends AlanDictionary<{ node:Cproperties__query, init:Tproperties__query},Cquery> {
		protected initialize(parent:Cquery, key:string, entry_init:Tproperties__query) { return new Cproperties__query(key, entry_init, parent); }
		protected finalize = finalize_properties__query
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'properties'`; }
		constructor(data:Tquery['properties'], parent:Cquery) {
			super(data, parent);
		}
	}
}
export namespace Cproperties__query {
	export class Dpath extends Cnode_path {
		constructor(data:Tproperties__query['path'], parent:Cproperties__query) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.collection_path)
					.then(context => context?.component_root.output.result_node()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Poptional).result!)
			}, 'path')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'number', node:Cnumber__type__properties__query, init:Tnumber__type__properties__query}|
		{ name: 'state group', node:Cstate_group__type__properties__query, init:Tstate_group__type__properties__query}|
		{ name: 'text', node:Ctext__type__properties__query, init:Ttext__type__properties__query}> {
		protected initializer(state: 'number'|'state group'|'text') {
			switch (state) {
				case 'number': return (init:Tnumber__type__properties__query, parent:Cproperties__query) => new Cnumber__type__properties__query(init, parent);
				case 'state group': return (init:Tstate_group__type__properties__query, parent:Cproperties__query) => new Cstate_group__type__properties__query(init, parent);
				case 'text': return (init:Ttext__type__properties__query, parent:Cproperties__query) => new Ctext__type__properties__query(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'number'|'state group'|'text') {
			switch (state) {
				case 'number': return finalize_number__type__properties__query;
				case 'state group': return finalize_state_group__type__properties__query;
				case 'text': return finalize_text__type__properties__query;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tproperties__query['type'], parent:Cproperties__query) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cnumber__type__properties__query {
	export class Dnumber extends Reference<application.Cnumber__type__property,string> {

		constructor(data:string, $this:Cnumber__type__properties__query) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'number') {
							return context.properties.type.state.node as application.Cnumber__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'number'`; }
	}
}
export namespace Cstate_group__type__properties__query {
	export class Dstate_group extends Reference<application.Cstate_group,string> {

		constructor(data:string, $this:Cstate_group__type__properties__query) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'state group') {
							return context.properties.type.state.node as application.Cstate_group;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state group'`; }
	}
}
export namespace Ctext__type__properties__query {
	export class Dtext extends Reference<application.Ctext__type__property,string> {

		constructor(data:string, $this:Ctext__type__properties__query) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'text') {
							return context.properties.type.state.node as application.Ctext__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'text'`; }
	}
}
export namespace Cacyclic_graph_tree_query {
	export class Dcontext_node extends Cid_path__client_request {
		constructor(data:Tacyclic_graph_tree_query['context node'], parent:Cacyclic_graph_tree_query) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root.input.application)
					.then(context => context?.properties.root).result!)
			}, 'context node')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'collection', node:Ccollection__type__acyclic_graph_tree_query, init:Tcollection__type__acyclic_graph_tree_query}> {
		protected initializer(state: 'collection') {
			switch (state) {
				case 'collection': return (init:Tcollection__type__acyclic_graph_tree_query, parent:Cacyclic_graph_tree_query) => new Ccollection__type__acyclic_graph_tree_query(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'collection') {
			switch (state) {
				case 'collection': return finalize_collection__type__acyclic_graph_tree_query;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tacyclic_graph_tree_query['type'], parent:Cacyclic_graph_tree_query) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccollection__type__acyclic_graph_tree_query {
	export class Dgraph extends Reference<application.Cacyclic,string> {

		constructor(data:string, $this:Ccollection__type__acyclic_graph_tree_query) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.collection())
				.then(context => context?.properties.graph_constraints)
				.then(context => {
					const entry = context?.properties.graphs.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'acyclic') {
							return context.properties.type.state.node as application.Cacyclic;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'graph'`; }
	}
}
export namespace Ccollection_query {
	export class Daggregates extends AlanDictionary<{ node:Caggregates, init:Taggregates},Ccollection_query> {
		protected initialize(parent:Ccollection_query, key:string, entry_init:Taggregates) { return new Caggregates(key, entry_init, parent); }
		protected finalize = finalize_aggregates
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'aggregates'`; }
		constructor(data:Tcollection_query['aggregates'], parent:Ccollection_query) {
			super(data, parent);
		}
	}
	export class Dcollection_path extends Ccollection_query_path {
		constructor(data:Tcollection_query['collection path'], parent:Ccollection_query) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.context_node_path)
					.then(context => context?.component_root.output.result_node()).result!)
			}, 'collection path')
		}
	}
	export class Dcontext_node_path extends Cid_path__client_request {
		constructor(data:Tcollection_query['context node path'], parent:Ccollection_query) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root.input.application)
					.then(context => context?.properties.root).result!)
			}, 'context node path')
		}
	}
	export class Dselect_entries extends StateGroup<
		{ name: 'no', node:Cno__select_entries, init:Tno__select_entries}|
		{ name: 'yes', node:Cyes__select_entries, init:Tyes__select_entries}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__select_entries, parent:Ccollection_query) => new Cno__select_entries(init, parent);
				case 'yes': return (init:Tyes__select_entries, parent:Ccollection_query) => new Cyes__select_entries(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__select_entries;
				case 'yes': return finalize_yes__select_entries;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcollection_query['select entries'], parent:Ccollection_query) {
			super(data, parent, 'select entries');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'select entries'`; }
	}
}
export namespace Caggregates {
	export class Dtype extends StateGroup<
		{ name: 'state distribution', node:Cstate_distribution, init:Tstate_distribution}> {
		protected initializer(state: 'state distribution') {
			switch (state) {
				case 'state distribution': return (init:Tstate_distribution, parent:Caggregates) => new Cstate_distribution(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'state distribution') {
			switch (state) {
				case 'state distribution': return finalize_state_distribution;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Taggregates['type'], parent:Caggregates) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cstate_distribution {
	export class Dpath extends Cnode_path {
		constructor(data:Tstate_distribution['path'], parent:Cstate_distribution) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.collection_path)
					.then(context => context?.component_root.output.result_node()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Poptional).result!)
			}, 'path')
		}
	}
	export class Dstate_group extends Reference<application.Cstate_group,string> {

		constructor(data:string, $this:Cstate_distribution) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'state group') {
							return context.properties.type.state.node as application.Cstate_group;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state group'`; }
	}
}
export namespace Cfilters {
	export class Dtodo_filter extends StateGroup<
		{ name: 'no', node:Cno__todo_filter, init:Tno__todo_filter}|
		{ name: 'yes', node:Cyes__todo_filter, init:Tyes__todo_filter}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__todo_filter, parent:Cfilters) => new Cno__todo_filter(init, parent);
				case 'yes': return (init:Tyes__todo_filter, parent:Cfilters) => new Cyes__todo_filter(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__todo_filter;
				case 'yes': return finalize_yes__todo_filter;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tfilters['todo filter'], parent:Cfilters) {
			super(data, parent, 'todo filter');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'todo filter'`; }
	}
	export class Dvalue_filters extends AlanDictionary<{ node:Cvalue_filters, init:Tvalue_filters},Cfilters> {
		protected initialize(parent:Cfilters, key:string, entry_init:Tvalue_filters) { return new Cvalue_filters(key, entry_init, parent); }
		protected finalize = finalize_value_filters
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'value filters'`; }
		constructor(data:Tfilters['value filters'], parent:Cfilters) {
			super(data, parent);
		}
	}
}
export namespace Cyes__todo_filter {
	export class Dpath extends Cnode_path {
		public readonly inferences:{
			has_todo: () => application.Cyes__todo
		}
		constructor(data:Tyes__todo_filter['path'], parent:Cyes__todo_filter) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.collection_path)
					.then(context => context?.component_root.output.result_node()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Poptional).result!)
			}, 'path')
			this.inferences = {
				has_todo: cache((detach:boolean) => {
					const client_request__type__collection_query__filters__todo_filter__yes__path_nval = this;
					return resolve(this)
						.then(context => client_request__type__collection_query__filters__todo_filter__yes__path_nval)
						.then(context => context?.component_root.output.result_node())
						.then(context => context?.properties.todo_definition)
						.then(context => {
							if (context?.properties.todo.state.name === 'yes') {
								return resolve(context.properties.todo.state.node as application.Cyes__todo).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
	}
}
export namespace Cvalue_filters {
	export class Dpath extends Cnode_path {
		constructor(data:Tvalue_filters['path'], parent:Cvalue_filters) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.collection_path)
					.then(context => context?.component_root.output.result_node()).result!),
				existence_support: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => application.Cexistence__application.Poptional).result!)
			}, 'path')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'node', node:Cnode, init:Tnode}|
		{ name: 'property', node:Cproperty__type__value_filters, init:Tproperty__type__value_filters}> {
		protected initializer(state: 'node'|'property') {
			switch (state) {
				case 'node': return (init:Tnode, parent:Cvalue_filters) => new Cnode(init, parent);
				case 'property': return (init:Tproperty__type__value_filters, parent:Cvalue_filters) => new Cproperty__type__value_filters(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'node'|'property') {
			switch (state) {
				case 'node': return finalize_node;
				case 'property': return finalize_property__type__value_filters;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tvalue_filters['type'], parent:Cvalue_filters) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cnode {
	export class Dtype extends StateGroup<
		{ name: 'containment', node:Ccontainment, init:Tcontainment}|
		{ name: 'existence', node:Cexistence, init:Texistence}> {
		protected initializer(state: 'containment'|'existence') {
			switch (state) {
				case 'containment': return (init:Tcontainment, parent:Cnode) => new Ccontainment(init, parent);
				case 'existence': return (init:Texistence, parent:Cnode) => new Cexistence(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'containment'|'existence') {
			switch (state) {
				case 'containment': return finalize_containment;
				case 'existence': return finalize_existence;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnode['type'], parent:Cnode) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccontainment {
	export class Doperator extends StateGroup<
		{ name: 'in', node:Cin__operator__containment, init:Tin__operator__containment}|
		{ name: 'not in', node:Cnot_in__operator__containment, init:Tnot_in__operator__containment}> {
		protected initializer(state: 'in'|'not in') {
			switch (state) {
				case 'in': return (init:Tin__operator__containment, parent:Ccontainment) => new Cin__operator__containment(init, parent);
				case 'not in': return (init:Tnot_in__operator__containment, parent:Ccontainment) => new Cnot_in__operator__containment(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'in'|'not in') {
			switch (state) {
				case 'in': return finalize_in__operator__containment;
				case 'not in': return finalize_not_in__operator__containment;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcontainment['operator'], parent:Ccontainment) {
			super(data, parent, 'operator');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'operator'`; }
	}
	export class Dreferences extends Creference_filter_list {
		constructor(data:Tcontainment['references'], parent:Ccontainment) {
			super(data, parent, {
				entity_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.path)
					.then(context => context?.component_root.output.result_node())
					.then(context => context?.component_root.output.entity()).result!)
			}, 'references')
		}
	}
}
export namespace Cexistence {
	export class Doperator extends StateGroup<
		{ name: 'exists', node:Cexists, init:Texists}|
		{ name: 'not exists', node:Cnot_exists, init:Tnot_exists}> {
		protected initializer(state: 'exists'|'not exists') {
			switch (state) {
				case 'exists': return (init:Texists, parent:Cexistence) => new Cexists(init, parent);
				case 'not exists': return (init:Tnot_exists, parent:Cexistence) => new Cnot_exists(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'exists'|'not exists') {
			switch (state) {
				case 'exists': return finalize_exists;
				case 'not exists': return finalize_not_exists;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Texistence['operator'], parent:Cexistence) {
			super(data, parent, 'operator');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'operator'`; }
	}
}
export namespace Cproperty__type__value_filters {
	export class Dproperty extends Reference<application.Cproperty__type__attributes,string> {

		constructor(data:string, $this:Cproperty__type__value_filters) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'property'`; }
	}
	export class Dtype extends StateGroup<
		{ name: 'collection', node:Ccollection__type__property__type__value_filters, init:Tcollection__type__property__type__value_filters}|
		{ name: 'number', node:Cnumber__type__property__type__value_filters, init:Tnumber__type__property__type__value_filters}|
		{ name: 'state group', node:Cstate_group__type__property__type__value_filters, init:Tstate_group__type__property__type__value_filters}|
		{ name: 'text', node:Ctext__type__property__type__value_filters, init:Ttext__type__property__type__value_filters}> {
		protected initializer(state: 'collection'|'number'|'state group'|'text') {
			switch (state) {
				case 'collection': return (init:Tcollection__type__property__type__value_filters, parent:Cproperty__type__value_filters) => new Ccollection__type__property__type__value_filters(init, parent);
				case 'number': return (init:Tnumber__type__property__type__value_filters, parent:Cproperty__type__value_filters) => new Cnumber__type__property__type__value_filters(init, parent);
				case 'state group': return (init:Tstate_group__type__property__type__value_filters, parent:Cproperty__type__value_filters) => new Cstate_group__type__property__type__value_filters(init, parent);
				case 'text': return (init:Ttext__type__property__type__value_filters, parent:Cproperty__type__value_filters) => new Ctext__type__property__type__value_filters(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'collection'|'number'|'state group'|'text') {
			switch (state) {
				case 'collection': return finalize_collection__type__property__type__value_filters;
				case 'number': return finalize_number__type__property__type__value_filters;
				case 'state group': return finalize_state_group__type__property__type__value_filters;
				case 'text': return finalize_text__type__property__type__value_filters;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tproperty__type__value_filters['type'], parent:Cproperty__type__value_filters) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccollection__type__property__type__value_filters {
	export class Dkeys extends AlanDictionary<{ node:Ckeys__collection, init:Tkeys__collection},Ccollection__type__property__type__value_filters> {
		protected initialize(parent:Ccollection__type__property__type__value_filters, key:string) { return new Ckeys__collection(key, {}, parent); }
		protected finalize = finalize_keys__collection
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'keys'`; }
		constructor(data:Tcollection__type__property__type__value_filters['keys'], parent:Ccollection__type__property__type__value_filters) {
			super(data, parent);
		}
	}
	export class Doperator extends StateGroup<
		{ name: 'in', node:Cin__operator__collection, init:Tin__operator__collection}|
		{ name: 'not in', node:Cnot_in__operator__collection, init:Tnot_in__operator__collection}> {
		protected initializer(state: 'in'|'not in') {
			switch (state) {
				case 'in': return (init:Tin__operator__collection, parent:Ccollection__type__property__type__value_filters) => new Cin__operator__collection(init, parent);
				case 'not in': return (init:Tnot_in__operator__collection, parent:Ccollection__type__property__type__value_filters) => new Cnot_in__operator__collection(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'in'|'not in') {
			switch (state) {
				case 'in': return finalize_in__operator__collection;
				case 'not in': return finalize_not_in__operator__collection;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcollection__type__property__type__value_filters['operator'], parent:Ccollection__type__property__type__value_filters) {
			super(data, parent, 'operator');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'operator'`; }
	}
}
export namespace Cnumber__type__property__type__value_filters {
	export class Dexpression extends Cnumber_filter_expression {
		constructor(data:Tnumber__type__property__type__value_filters['expression'], parent:Cnumber__type__property__type__value_filters) {
			super(data, parent, 'expression')
		}
	}
}
export namespace Cstate_group__type__property__type__value_filters {
	export class Dstates_to_include extends AlanDictionary<{ node:Cstates_to_include, init:Tstates_to_include},Cstate_group__type__property__type__value_filters> {
		protected initialize(parent:Cstate_group__type__property__type__value_filters, key:string) { return new Cstates_to_include(key, {}, parent); }
		protected finalize = finalize_states_to_include
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'states to include'`; }
		constructor(data:Tstate_group__type__property__type__value_filters['states to include'], parent:Cstate_group__type__property__type__value_filters) {
			super(data, parent);
		}
	}
}
export namespace Ctext__type__property__type__value_filters {
	export class Dexpression extends Cfilter_expression {
		constructor(data:Ttext__type__property__type__value_filters['expression'], parent:Ctext__type__property__type__value_filters) {
			super(data, parent, 'expression')
		}
	}
}
export namespace Cyes__select_entries {
	export class Dlimit_number_of_entries extends AlanInteger {
		constructor(data:Tyes__select_entries['limit number of entries'], parent:Cyes__select_entries) {
			number__is_positive(data);
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'limit number of entries'`; }
	}
	export class Dproperties extends Cnode_query {
		constructor(data:Tyes__select_entries['properties'], parent:Cyes__select_entries) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.collection_path)
					.then(context => context?.component_root.output.result_node()).result!)
			}, 'properties')
		}
	}
}
export namespace Ccommand_execution {
	export class Darguments extends Cnode_initialization {
		constructor(data:Tcommand_execution['arguments'], parent:Ccommand_execution) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.command?.ref)
					.then(context => context?.properties.parameters).result!)
			}, 'arguments')
		}
	}
	export class Dcommand extends Reference<application.Ccommand__type,string> {
		public readonly inferences:{
			global_command: () => application.Cglobal
		}

		constructor(data:string, $this:Ccommand_execution) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.context_node)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'command') {
							return context.properties.type.state.node as application.Ccommand__type;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
			this.inferences = {
				global_command: cache((detach:boolean) => {
					const client_request__type__command_execution__command_nval = $this.properties.command.ref;
					return resolve($this.properties.command.ref)
						.then(context => client_request__type__command_execution__command_nval)
						.then(context => {
							if (context?.properties.type.state.name === 'global') {
								return resolve(context.properties.type.state.node as application.Cglobal).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'command'`; }
	}
	export class Dcontext_node extends Cid_path__client_request {
		constructor(data:Tcommand_execution['context node'], parent:Ccommand_execution) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root.input.application)
					.then(context => context?.properties.root).result!)
			}, 'context node')
		}
	}
}
export namespace Cmutation {
	export class Dcontext_node extends Cid_path__client_request {
		constructor(data:Tmutation['context node'], parent:Cmutation) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root.input.application)
					.then(context => context?.properties.root).result!)
			}, 'context node')
		}
	}
	export class Dnode_update extends Cnode_update {
		constructor(data:Tmutation['node update'], parent:Cmutation) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.context_node)
					.then(context => context?.component_root.output.result_node()).result!)
			}, 'node update')
		}
	}
}
export namespace Cpassword_mutation {
}
export namespace Cpassword_reset {
}
export namespace Csubscription {
	export class Dcontext_node extends Cid_path__client_request {
		constructor(data:Tsubscription['context node'], parent:Csubscription) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root.input.application)
					.then(context => context?.properties.root).result!)
			}, 'context node')
		}
	}
	export class Dmutation_permissions extends StateGroup<
		{ name: 'ignore', node:Cignore, init:Tignore}|
		{ name: 'include', node:Cinclude, init:Tinclude}> {
		protected initializer(state: 'ignore'|'include') {
			switch (state) {
				case 'ignore': return (init:Tignore, parent:Csubscription) => new Cignore(init, parent);
				case 'include': return (init:Tinclude, parent:Csubscription) => new Cinclude(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'ignore'|'include') {
			switch (state) {
				case 'ignore': return finalize_ignore;
				case 'include': return finalize_include;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tsubscription['mutation permissions'], parent:Csubscription) {
			super(data, parent, 'mutation permissions');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'mutation permissions'`; }
	}
	export class Dnode_subscription extends Cnode_subscription {
		constructor(data:Tsubscription['node subscription'], parent:Csubscription) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.context_node)
					.then(context => context?.component_root.output.result_node()).result!)
			}, 'node subscription')
		}
	}
}
function finalize_aggregate(obj:Caggregate, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.context_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccollection__type__property>(obj.properties.collection as any).resolve)(detach) !== undefined || detach);
	finalize_node_path(obj.properties.head, detach);
	finalize_node_path(obj.properties.tail, detach);
}
function finalize_no__has_steps__collection_path(obj:Cno__has_steps__collection_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
}
function finalize_yes__has_steps__collection_path(obj:Cyes__has_steps__collection_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccollection__type__property>(obj.properties.collection as any).resolve)(detach) !== undefined || detach);
	finalize_conditional_child_path(obj.properties.head, detach);
	finalize_collection_path(obj.properties.tail, detach);
}
function finalize_collection_path(obj:Ccollection_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.context_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	switch (obj.properties.has_steps.state.name) {
		case 'no': finalize_no__has_steps__collection_path(obj.properties.has_steps.state.node, detach); break;
		case 'yes': finalize_yes__has_steps__collection_path(obj.properties.has_steps.state.node, detach); break;
	}
}
function finalize_no__has_steps__collection_query_path(obj:Cno__has_steps__collection_query_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
}
function finalize_no__include_reference__candidates(obj:Cno__include_reference__candidates, detach:boolean = false) {
}
function finalize_yes__include_reference__candidates(obj:Cyes__include_reference__candidates, detach:boolean = false) {
}
function finalize_candidates(obj:Ccandidates, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cyes__has_reference>obj.inferences.key_reference)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.collection_node)(detach) !== undefined || detach);
	switch (obj.properties.include_reference.state.name) {
		case 'no': finalize_no__include_reference__candidates(obj.properties.include_reference.state.node, detach); break;
		case 'yes': finalize_yes__include_reference__candidates(obj.properties.include_reference.state.node, detach); break;
	}
}
function finalize_existing_entries(obj:Cexisting_entries, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.collection_node)(detach) !== undefined || detach);
}
function finalize_yes__has_steps__collection_query_path(obj:Cyes__has_steps__collection_query_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccollection__type__property>(obj.properties.collection as any).resolve)(detach) !== undefined || detach);
	finalize_conditional_child_path(obj.properties.head, detach);
	finalize_collection_query_path(obj.properties.tail, detach);
	switch (obj.properties.type.state.name) {
		case 'candidates': finalize_candidates(obj.properties.type.state.node, detach); break;
		case 'existing entries': finalize_existing_entries(obj.properties.type.state.node, detach); break;
	}
}
function finalize_collection_query_path(obj:Ccollection_query_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.context_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	switch (obj.properties.has_steps.state.name) {
		case 'no': finalize_no__has_steps__collection_query_path(obj.properties.has_steps.state.node, detach); break;
		case 'yes': finalize_yes__has_steps__collection_query_path(obj.properties.has_steps.state.node, detach); break;
	}
}
function finalize_no__has_steps__conditional_child_path(obj:Cno__has_steps__conditional_child_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
}
function finalize_group__type__yes__has_steps__conditional_child_path(obj:Cgroup__type__yes__has_steps__conditional_child_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cgroup__type__property>(obj.properties.group as any).resolve)(detach) !== undefined || detach);
}
function finalize_state__type__yes__has_steps__conditional_child_path(obj:Cstate__type__yes__has_steps__conditional_child_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstates__state_group>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstate_group>(obj.properties.state_group as any).resolve)(detach) !== undefined || detach);
}
function finalize_yes__has_steps__conditional_child_path(obj:Cyes__has_steps__conditional_child_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	finalize_conditional_child_path(obj.properties.tail, detach);
	switch (obj.properties.type.state.name) {
		case 'group': finalize_group__type__yes__has_steps__conditional_child_path(obj.properties.type.state.node, detach); break;
		case 'state': finalize_state__type__yes__has_steps__conditional_child_path(obj.properties.type.state.node, detach); break;
	}
}
function finalize_conditional_child_path(obj:Cconditional_child_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.context_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	switch (obj.properties.has_steps.state.name) {
		case 'no': finalize_no__has_steps__conditional_child_path(obj.properties.has_steps.state.node, detach); break;
		case 'yes': finalize_yes__has_steps__conditional_child_path(obj.properties.has_steps.state.node, detach); break;
	}
}
function finalize_no__has_steps__entry_point_path(obj:Cno__has_steps__entry_point_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.output.result_collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ctext__type__property>(obj.properties.text as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cyes__has_reference>obj.properties.text.inferences.reference)(detach) !== undefined || detach);
}
function finalize_yes__has_steps__entry_point_path(obj:Cyes__has_steps__entry_point_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.output.result_collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccollection__type__property>(obj.properties.collection as any).resolve)(detach) !== undefined || detach);
	finalize_conditional_child_path(obj.properties.head, detach);
	finalize_entry_point_path(obj.properties.tail, detach);
}
function finalize_entry_point_path(obj:Centry_point_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.context_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.output.result_collection)(detach) !== undefined || detach);
	switch (obj.properties.has_steps.state.name) {
		case 'no': finalize_no__has_steps__entry_point_path(obj.properties.has_steps.state.node, detach); break;
		case 'yes': finalize_yes__has_steps__entry_point_path(obj.properties.has_steps.state.node, detach); break;
	}
}
function finalize_no__has_more_steps(obj:Cno__has_more_steps, detach:boolean = false) {
}
function finalize_yes__has_more_steps(obj:Cyes__has_more_steps, detach:boolean = false) {
	finalize_filter_expression(obj.properties.tail, detach);
}
function finalize_alternatives__alternatives(obj:Calternatives__alternatives, detach:boolean = false) {
}
function finalize_alternatives__type(obj:Calternatives__type, detach:boolean = false) {
	for (const [_key, entry] of obj.properties.alternatives) {
		finalize_alternatives__alternatives(entry, detach);
	}
	if (!detach) {
	}
}
function finalize_wildcard(obj:Cwildcard, detach:boolean = false) {
}
function finalize_filter_expression(obj:Cfilter_expression, detach:boolean = false) {
	switch (obj.properties.has_more_steps.state.name) {
		case 'no': finalize_no__has_more_steps(obj.properties.has_more_steps.state.node, detach); break;
		case 'yes': finalize_yes__has_more_steps(obj.properties.has_more_steps.state.node, detach); break;
	}
	switch (obj.properties.type.state.name) {
		case 'alternatives': finalize_alternatives__type(obj.properties.type.state.node, detach); break;
		case 'wildcard': finalize_wildcard(obj.properties.type.state.node, detach); break;
	}
}
function finalize_no__has_steps__id_path(obj:Cno__has_steps__id_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
}
function finalize_collection_entry__type__yes__has_steps__id_path(obj:Ccollection_entry__type__yes__has_steps__id_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccollection__type__property>(obj.properties.collection as any).resolve)(detach) !== undefined || detach);
}
function finalize_group__type__yes__has_steps__id_path(obj:Cgroup__type__yes__has_steps__id_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cgroup__type__property>(obj.properties.group as any).resolve)(detach) !== undefined || detach);
}
function finalize_state__type__yes__has_steps__id_path(obj:Cstate__type__yes__has_steps__id_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstates__state_group>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstate_group>(obj.properties.state_group as any).resolve)(detach) !== undefined || detach);
}
function finalize_yes__has_steps__id_path(obj:Cyes__has_steps__id_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	finalize_id_path__client_request(obj.properties.tail, detach);
	switch (obj.properties.type.state.name) {
		case 'collection entry': finalize_collection_entry__type__yes__has_steps__id_path(obj.properties.type.state.node, detach); break;
		case 'group': finalize_group__type__yes__has_steps__id_path(obj.properties.type.state.node, detach); break;
		case 'state': finalize_state__type__yes__has_steps__id_path(obj.properties.type.state.node, detach); break;
	}
}
function finalize_id_path__client_request(obj:Cid_path__client_request, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.context_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	switch (obj.properties.has_steps.state.name) {
		case 'no': finalize_no__has_steps__id_path(obj.properties.has_steps.state.node, detach); break;
		case 'yes': finalize_yes__has_steps__id_path(obj.properties.has_steps.state.node, detach); break;
	}
}
function finalize_entries__collection__type__properties__node_initialization(obj:Centries__collection__type__properties__node_initialization, detach:boolean = false) {
	finalize_node_initialization(obj.properties.initialization, detach);
}
function finalize_collection__type__properties__node_initialization(obj:Ccollection__type__properties__node_initialization, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.inferences.collection)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.entries) {
		finalize_entries__collection__type__properties__node_initialization(entry, detach);
	}
	if (!detach) {
	}
}
function finalize_file__type__properties__node_initialization(obj:Cfile__type__properties__node_initialization, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cfile>obj.inferences.file)(detach) !== undefined || detach);
}
function finalize_group__type__properties__node_initialization(obj:Cgroup__type__properties__node_initialization, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cgroup__type__property>obj.inferences.group)(detach) !== undefined || detach);
	finalize_node_initialization(obj.properties.initialization, detach);
}
function finalize_number__type__properties__node_initialization(obj:Cnumber__type__properties__node_initialization, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnumber__type__property>obj.inferences.number)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Csimple__type__base>obj.inferences.simple)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumber_set_type>obj.properties.value.inferences.set_type)(detach) !== undefined || detach);
}
function finalize_state_group__type__properties__node_initialization(obj:Cstate_group__type__properties__node_initialization, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cstate_group>obj.inferences.state_group)(detach) !== undefined || detach);
	finalize_node_initialization(obj.properties.initialization, detach);
	assert((<(detach?:boolean) => application.Cstates__state_group>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
}
function finalize_text__type__properties__node_initialization(obj:Ctext__type__properties__node_initialization, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ctext__type__property>obj.inferences.text)(detach) !== undefined || detach);
}
function finalize_properties__node_initialization(obj:Cproperties__node_initialization, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>(obj.key as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexternal_source>obj.key.inferences.value_source_is_user)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'collection': finalize_collection__type__properties__node_initialization(obj.properties.type.state.node, detach); break;
		case 'file': finalize_file__type__properties__node_initialization(obj.properties.type.state.node, detach); break;
		case 'group': finalize_group__type__properties__node_initialization(obj.properties.type.state.node, detach); break;
		case 'number': finalize_number__type__properties__node_initialization(obj.properties.type.state.node, detach); break;
		case 'state group': finalize_state_group__type__properties__node_initialization(obj.properties.type.state.node, detach); break;
		case 'text': finalize_text__type__properties__node_initialization(obj.properties.type.state.node, detach); break;
	}
}
function finalize_node_initialization(obj:Cnode_initialization, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.node_type)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.properties) {
		finalize_properties__node_initialization(entry, detach);
	}
	if (!detach) {
		(obj.properties.properties as any).eval_required_keys(detach);
	}
}
function finalize_no__has_steps__node_path(obj:Cno__has_steps__node_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
}
function finalize_collection_entry__type__yes__has_steps__node_path(obj:Ccollection_entry__type__yes__has_steps__node_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Coptional__existence>obj.inferences.optional_existence)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ccollection__type__property>(obj.properties.collection as any).resolve)(detach) !== undefined || detach);
}
function finalize_group__type__yes__has_steps__node_path(obj:Cgroup__type__yes__has_steps__node_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cgroup__type__property>(obj.properties.group as any).resolve)(detach) !== undefined || detach);
}
function finalize_parent(obj:Cparent, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.inferences.parent_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
}
function finalize_reference(obj:Creference, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ctext__type__property>(obj.properties.text as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.properties.text.inferences.behaviour)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cyes__has_reference>obj.properties.text.inferences.reference)(detach) !== undefined || detach);
}
function finalize_reference_rule(obj:Creference_rule, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Ctext__type__property>(obj.properties.property as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cyes__has_reference>obj.properties.property.inferences.reference)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crules>(obj.properties.rule as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.properties.rule.inferences.behaviour)(detach) !== undefined || detach);
}
function finalize_state__type__yes__has_steps__node_path(obj:Cstate__type__yes__has_steps__node_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Coptional__existence>obj.inferences.optional_existence)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstates__state_group>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstate_group>(obj.properties.state_group as any).resolve)(detach) !== undefined || detach);
}
function finalize_state_rule(obj:Cstate_rule, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cstates__state_group>obj.inferences.state)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Crules>(obj.properties.rule as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.properties.rule.inferences.behaviour)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cmandatory__type>obj.properties.rule.inferences.mandatory_reference)(detach) !== undefined || detach);
}
function finalize_yes__has_steps__node_path(obj:Cyes__has_steps__node_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	finalize_node_path(obj.properties.tail, detach);
	switch (obj.properties.type.state.name) {
		case 'collection entry': finalize_collection_entry__type__yes__has_steps__node_path(obj.properties.type.state.node, detach); break;
		case 'group': finalize_group__type__yes__has_steps__node_path(obj.properties.type.state.node, detach); break;
		case 'parent': finalize_parent(obj.properties.type.state.node, detach); break;
		case 'reference': finalize_reference(obj.properties.type.state.node, detach); break;
		case 'reference rule': finalize_reference_rule(obj.properties.type.state.node, detach); break;
		case 'state': finalize_state__type__yes__has_steps__node_path(obj.properties.type.state.node, detach); break;
		case 'state rule': finalize_state_rule(obj.properties.type.state.node, detach); break;
	}
}
function finalize_node_path(obj:Cnode_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.context_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cexistence__application>obj.input.existence_support)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_node)(detach) !== undefined || detach);
	switch (obj.properties.has_steps.state.name) {
		case 'no': finalize_no__has_steps__node_path(obj.properties.has_steps.state.node, detach); break;
		case 'yes': finalize_yes__has_steps__node_path(obj.properties.has_steps.state.node, detach); break;
	}
}
function finalize_no__include_graph_endpoints__collection__type__properties(obj:Cno__include_graph_endpoints__collection__type__properties, detach:boolean = false) {
}
function finalize_yes__include_graph_endpoints__collection__type__properties(obj:Cyes__include_graph_endpoints__collection__type__properties, detach:boolean = false) {
}
function finalize_collection__type__properties__node_query(obj:Ccollection__type__properties__node_query, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccollection__type__property>(obj.properties.collection as any).resolve)(detach) !== undefined || detach);
	switch (obj.properties.include_graph_endpoints.state.name) {
		case 'no': finalize_no__include_graph_endpoints__collection__type__properties(obj.properties.include_graph_endpoints.state.node, detach); break;
		case 'yes': finalize_yes__include_graph_endpoints__collection__type__properties(obj.properties.include_graph_endpoints.state.node, detach); break;
	}
	finalize_node_query(obj.properties.properties, detach);
}
function finalize_file__type__properties__node_query(obj:Cfile__type__properties__node_query, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cfile>(obj.properties.file as any).resolve)(detach) !== undefined || detach);
}
function finalize_id_path__type(obj:Cid_path__type, detach:boolean = false) {
}
function finalize_number__type__properties__node_query(obj:Cnumber__type__properties__node_query, detach:boolean = false) {
	finalize_number_expression(obj.properties.expression, detach);
}
function finalize_state_group__type__properties__node_query(obj:Cstate_group__type__properties__node_query, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cstate_group>(obj.properties.state_group as any).resolve)(detach) !== undefined || detach);
}
function finalize_text__type__properties__node_query(obj:Ctext__type__properties__node_query, detach:boolean = false) {
	finalize_text_expression(obj.properties.expression, detach);
}
function finalize_properties__node_query(obj:Cproperties__node_query, detach:boolean = false) {
	finalize_node_path(obj.properties.path, detach);
	switch (obj.properties.type.state.name) {
		case 'collection': finalize_collection__type__properties__node_query(obj.properties.type.state.node, detach); break;
		case 'file': finalize_file__type__properties__node_query(obj.properties.type.state.node, detach); break;
		case 'id path': finalize_id_path__type(obj.properties.type.state.node, detach); break;
		case 'number': finalize_number__type__properties__node_query(obj.properties.type.state.node, detach); break;
		case 'state group': finalize_state_group__type__properties__node_query(obj.properties.type.state.node, detach); break;
		case 'text': finalize_text__type__properties__node_query(obj.properties.type.state.node, detach); break;
	}
}
function finalize_node_query(obj:Cnode_query, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.node_type)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.properties) {
		finalize_properties__node_query(entry, detach);
	}
	if (!detach) {
	}
}
function finalize_command(obj:Ccommand, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccommand__type>obj.inferences.command)(detach) !== undefined || detach);
}
function finalize_no__include_graph_endpoints__collection__type__property(obj:Cno__include_graph_endpoints__collection__type__property, detach:boolean = false) {
}
function finalize_yes__include_graph_endpoints__collection__type__property(obj:Cyes__include_graph_endpoints__collection__type__property, detach:boolean = false) {
}
function finalize_no__key_filter(obj:Cno__key_filter, detach:boolean = false) {
}
function finalize_keys__yes(obj:Ckeys__yes, detach:boolean = false) {
}
function finalize_yes__key_filter(obj:Cyes__key_filter, detach:boolean = false) {
	for (const [_key, entry] of obj.properties.keys) {
		finalize_keys__yes(entry, detach);
	}
	if (!detach) {
	}
}
function finalize_entries__subscribe_on(obj:Centries__subscribe_on, detach:boolean = false) {
	switch (obj.properties.key_filter.state.name) {
		case 'no': finalize_no__key_filter(obj.properties.key_filter.state.node, detach); break;
		case 'yes': finalize_yes__key_filter(obj.properties.key_filter.state.node, detach); break;
	}
	finalize_node_subscription(obj.properties.subscription, detach);
}
function finalize_updates(obj:Cupdates, detach:boolean = false) {
}
function finalize_collection__type__property__type__attributes(obj:Ccollection__type__property__type__attributes, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.inferences.collection)(detach) !== undefined || detach);
	switch (obj.properties.include_graph_endpoints.state.name) {
		case 'no': finalize_no__include_graph_endpoints__collection__type__property(obj.properties.include_graph_endpoints.state.node, detach); break;
		case 'yes': finalize_yes__include_graph_endpoints__collection__type__property(obj.properties.include_graph_endpoints.state.node, detach); break;
	}
	switch (obj.properties.subscribe_on.state.name) {
		case 'entries': finalize_entries__subscribe_on(obj.properties.subscribe_on.state.node, detach); break;
		case 'updates': finalize_updates(obj.properties.subscribe_on.state.node, detach); break;
	}
}
function finalize_file__type__property(obj:Cfile__type__property, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cfile>obj.inferences.file)(detach) !== undefined || detach);
}
function finalize_group__type__property(obj:Cgroup__type__property, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cgroup__type__property>obj.inferences.group)(detach) !== undefined || detach);
	finalize_node_subscription(obj.properties.subscription, detach);
}
function finalize_number__type__property__type__attributes(obj:Cnumber__type__property__type__attributes, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnumber__type__property>obj.inferences.number)(detach) !== undefined || detach);
}
function finalize_states__state_group(obj:Cstates__state_group, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cstates__state_group>(obj.key as any).resolve)(detach) !== undefined || detach);
	finalize_node_subscription(obj.properties.subscription, detach);
}
function finalize_state_group__type__property__type__attributes(obj:Cstate_group__type__property__type__attributes, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cstate_group>obj.inferences.state_group)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.states) {
		finalize_states__state_group(entry, detach);
	}
	if (!detach) {
		(obj.properties.states as any).eval_required_keys(detach);
	}
}
function finalize_no__include_reference__text(obj:Cno__include_reference__text, detach:boolean = false) {
}
function finalize_yes__include_reference__text(obj:Cyes__include_reference__text, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cyes__has_reference>obj.inferences.linker)(detach) !== undefined || detach);
}
function finalize_text__type__property__type__attributes(obj:Ctext__type__property__type__attributes, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ctext__type__property>obj.inferences.text)(detach) !== undefined || detach);
	switch (obj.properties.include_reference.state.name) {
		case 'no': finalize_no__include_reference__text(obj.properties.include_reference.state.node, detach); break;
		case 'yes': finalize_yes__include_reference__text(obj.properties.include_reference.state.node, detach); break;
	}
}
function finalize_property__type__attributes(obj:Cproperty__type__attributes, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>obj.inferences.property)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'collection': finalize_collection__type__property__type__attributes(obj.properties.type.state.node, detach); break;
		case 'file': finalize_file__type__property(obj.properties.type.state.node, detach); break;
		case 'group': finalize_group__type__property(obj.properties.type.state.node, detach); break;
		case 'number': finalize_number__type__property__type__attributes(obj.properties.type.state.node, detach); break;
		case 'state group': finalize_state_group__type__property__type__attributes(obj.properties.type.state.node, detach); break;
		case 'text': finalize_text__type__property__type__attributes(obj.properties.type.state.node, detach); break;
	}
}
function finalize_attributes(obj:Cattributes, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cattributes>(obj.key as any).resolve)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'command': finalize_command(obj.properties.type.state.node, detach); break;
		case 'property': finalize_property__type__attributes(obj.properties.type.state.node, detach); break;
	}
}
function finalize_node_subscription(obj:Cnode_subscription, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.node_type)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.attributes) {
		finalize_attributes(entry, detach);
	}
	if (!detach) {
	}
}
function finalize_create(obj:Ccreate, detach:boolean = false) {
	finalize_node_initialization(obj.properties.initialization, detach);
}
function finalize_remove(obj:Cremove, detach:boolean = false) {
}
function finalize_update__type__entries(obj:Cupdate__type__entries, detach:boolean = false) {
	finalize_node_update(obj.properties.update, detach);
}
function finalize_entries__collection__type__properties__node_update(obj:Centries__collection__type__properties__node_update, detach:boolean = false) {
	switch (obj.properties.type.state.name) {
		case 'create': finalize_create(obj.properties.type.state.node, detach); break;
		case 'remove': finalize_remove(obj.properties.type.state.node, detach); break;
		case 'update': finalize_update__type__entries(obj.properties.type.state.node, detach); break;
	}
}
function finalize_collection__type__properties__node_update(obj:Ccollection__type__properties__node_update, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.inferences.collection)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.entries) {
		finalize_entries__collection__type__properties__node_update(entry, detach);
	}
	if (!detach) {
	}
}
function finalize_file__type__properties__node_update(obj:Cfile__type__properties__node_update, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cfile>obj.inferences.file)(detach) !== undefined || detach);
}
function finalize_group__type__properties__node_update(obj:Cgroup__type__properties__node_update, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cgroup__type__property>obj.inferences.group)(detach) !== undefined || detach);
	finalize_node_update(obj.properties.update, detach);
}
function finalize_number__type__properties__node_update(obj:Cnumber__type__properties__node_update, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnumber__type__property>obj.inferences.number)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Csimple__type__base>obj.inferences.simple)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumber_set_type>obj.properties.new_value.inferences.set_type)(detach) !== undefined || detach);
}
function finalize_set(obj:Cset, detach:boolean = false) {
	finalize_node_initialization(obj.properties.initialization, detach);
}
function finalize_update__type__state_group(obj:Cupdate__type__state_group, detach:boolean = false) {
	finalize_node_update(obj.properties.update, detach);
}
function finalize_state_group__type__properties__node_update(obj:Cstate_group__type__properties__node_update, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cstate_group>obj.inferences.state_group)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cstates__state_group>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'set': finalize_set(obj.properties.type.state.node, detach); break;
		case 'update': finalize_update__type__state_group(obj.properties.type.state.node, detach); break;
	}
}
function finalize_text__type__properties__node_update(obj:Ctext__type__properties__node_update, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ctext__type__property>obj.inferences.text)(detach) !== undefined || detach);
}
function finalize_properties__node_update(obj:Cproperties__node_update, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>(obj.key as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cuser__external_source>obj.key.inferences.value_source_is_user)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'collection': finalize_collection__type__properties__node_update(obj.properties.type.state.node, detach); break;
		case 'file': finalize_file__type__properties__node_update(obj.properties.type.state.node, detach); break;
		case 'group': finalize_group__type__properties__node_update(obj.properties.type.state.node, detach); break;
		case 'number': finalize_number__type__properties__node_update(obj.properties.type.state.node, detach); break;
		case 'state group': finalize_state_group__type__properties__node_update(obj.properties.type.state.node, detach); break;
		case 'text': finalize_text__type__properties__node_update(obj.properties.type.state.node, detach); break;
	}
}
function finalize_node_update(obj:Cnode_update, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.node_type)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.properties) {
		finalize_properties__node_update(entry, detach);
	}
	if (!detach) {
	}
}
function finalize_number_aggregate(obj:Cnumber_aggregate, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.context_node)(detach) !== undefined || detach);
	finalize_aggregate(obj.properties.aggregate, detach);
	assert((<(detach?:boolean) => application.Cnumber__type__property>(obj.properties.property_name as any).resolve)(detach) !== undefined || detach);
}
function finalize_count(obj:Ccount, detach:boolean = false) {
	finalize_aggregate(obj.properties.aggregate, detach);
}
function finalize_ceil(obj:Cceil, detach:boolean = false) {
}
function finalize_floor(obj:Cfloor, detach:boolean = false) {
}
function finalize_ordinary(obj:Cordinary, detach:boolean = false) {
}
function finalize_division(obj:Cdivision, detach:boolean = false) {
	finalize_signed_number_property(obj.properties.denominator, detach);
	finalize_signed_number_property(obj.properties.numerator, detach);
	switch (obj.properties.rounding.state.name) {
		case 'ceil': finalize_ceil(obj.properties.rounding.state.node, detach); break;
		case 'floor': finalize_floor(obj.properties.rounding.state.node, detach); break;
		case 'ordinary': finalize_ordinary(obj.properties.rounding.state.node, detach); break;
	}
}
function finalize_maximum(obj:Cmaximum, detach:boolean = false) {
	finalize_number_expression(obj.properties.left_expression, detach);
	finalize_number_expression(obj.properties.right_expression, detach);
}
function finalize_minimum(obj:Cminimum, detach:boolean = false) {
	finalize_number_expression(obj.properties.left_expression, detach);
	finalize_number_expression(obj.properties.right_expression, detach);
}
function finalize_number_property(obj:Cnumber_property, detach:boolean = false) {
	finalize_signed_number_property(obj.properties.signed_number_property, detach);
}
function finalize_product(obj:Cproduct, detach:boolean = false) {
	finalize_signed_number_property_list(obj.properties.numbers, detach);
}
function finalize_remainder(obj:Cremainder, detach:boolean = false) {
	finalize_signed_number_property(obj.properties.modulus, detach);
	finalize_signed_number_property(obj.properties.number, detach);
}
function finalize_states__state_based(obj:Cstates__state_based, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cstates__state_group>(obj.key as any).resolve)(detach) !== undefined || detach);
	finalize_number_expression(obj.properties.value, detach);
}
function finalize_state_based(obj:Cstate_based, detach:boolean = false) {
	finalize_node_path(obj.properties.path, detach);
	assert((<(detach?:boolean) => application.Cstate_group>(obj.properties.state_group as any).resolve)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.states) {
		finalize_states__state_based(entry, detach);
	}
	if (!detach) {
		(obj.properties.states as any).eval_required_keys(detach);
	}
}
function finalize_sum(obj:Csum, detach:boolean = false) {
	finalize_number_aggregate(obj.properties.number_aggregate, detach);
}
function finalize_sum_list(obj:Csum_list, detach:boolean = false) {
	finalize_signed_number_property_list(obj.properties.numbers, detach);
}
function finalize_value(obj:Cvalue, detach:boolean = false) {
}
function finalize_number_expression(obj:Cnumber_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.context_node)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'count': finalize_count(obj.properties.type.state.node, detach); break;
		case 'division': finalize_division(obj.properties.type.state.node, detach); break;
		case 'maximum': finalize_maximum(obj.properties.type.state.node, detach); break;
		case 'minimum': finalize_minimum(obj.properties.type.state.node, detach); break;
		case 'number property': finalize_number_property(obj.properties.type.state.node, detach); break;
		case 'product': finalize_product(obj.properties.type.state.node, detach); break;
		case 'remainder': finalize_remainder(obj.properties.type.state.node, detach); break;
		case 'state based': finalize_state_based(obj.properties.type.state.node, detach); break;
		case 'sum': finalize_sum(obj.properties.type.state.node, detach); break;
		case 'sum list': finalize_sum_list(obj.properties.type.state.node, detach); break;
		case 'value': finalize_value(obj.properties.type.state.node, detach); break;
	}
}
function finalize_equals(obj:Cequals, detach:boolean = false) {
}
function finalize_no__greater_than(obj:Cno__greater_than, detach:boolean = false) {
}
function finalize_yes__greater_than(obj:Cyes__greater_than, detach:boolean = false) {
}
function finalize_no__less_than(obj:Cno__less_than, detach:boolean = false) {
	assert((<(detach?:boolean) => client_request.Cyes__greater_than>obj.inferences.must_be_greater_than)(detach) !== undefined || detach);
}
function finalize_yes__less_than(obj:Cyes__less_than, detach:boolean = false) {
}
function finalize_range(obj:Crange, detach:boolean = false) {
	switch (obj.properties.greater_than.state.name) {
		case 'no': finalize_no__greater_than(obj.properties.greater_than.state.node, detach); break;
		case 'yes': finalize_yes__greater_than(obj.properties.greater_than.state.node, detach); break;
	}
	switch (obj.properties.less_than.state.name) {
		case 'no': finalize_no__less_than(obj.properties.less_than.state.node, detach); break;
		case 'yes': finalize_yes__less_than(obj.properties.less_than.state.node, detach); break;
	}
}
function finalize_number_filter_expression(obj:Cnumber_filter_expression, detach:boolean = false) {
	switch (obj.properties.operator.state.name) {
		case 'equals': finalize_equals(obj.properties.operator.state.node, detach); break;
		case 'range': finalize_range(obj.properties.operator.state.node, detach); break;
	}
}
function finalize_no__has_alternative(obj:Cno__has_alternative, detach:boolean = false) {
}
function finalize_yes__has_alternative(obj:Cyes__has_alternative, detach:boolean = false) {
	finalize_reference_filter_list(obj.properties.alternative, detach);
}
function finalize_reference_filter_list(obj:Creference_filter_list, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Centity__application>obj.input.entity_node)(detach) !== undefined || detach);
	switch (obj.properties.has_alternative.state.name) {
		case 'no': finalize_no__has_alternative(obj.properties.has_alternative.state.node, detach); break;
		case 'yes': finalize_yes__has_alternative(obj.properties.has_alternative.state.node, detach); break;
	}
	finalize_id_path__client_request(obj.properties.referenced_node, detach);
	assert((<(detach?:boolean) => application.Centity__application>obj.properties.referenced_node.inferences.entity_constraint)(detach) !== undefined || detach);
}
function finalize_negative(obj:Cnegative, detach:boolean = false) {
}
function finalize_positive(obj:Cpositive, detach:boolean = false) {
}
function finalize_signed_number_property(obj:Csigned_number_property, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.context_node)(detach) !== undefined || detach);
	finalize_node_path(obj.properties.path, detach);
	assert((<(detach?:boolean) => application.Cnumber__type__property>(obj.properties.property_name as any).resolve)(detach) !== undefined || detach);
	switch (obj.properties.sign.state.name) {
		case 'negative': finalize_negative(obj.properties.sign.state.node, detach); break;
		case 'positive': finalize_positive(obj.properties.sign.state.node, detach); break;
	}
}
function finalize_no__has_element__signed_number_property_list(obj:Cno__has_element__signed_number_property_list, detach:boolean = false) {
}
function finalize_yes__has_element__signed_number_property_list(obj:Cyes__has_element__signed_number_property_list, detach:boolean = false) {
	finalize_signed_number_property(obj.properties.signed_number_property, detach);
	finalize_signed_number_property_list(obj.properties.tail, detach);
}
function finalize_signed_number_property_list(obj:Csigned_number_property_list, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.context_node)(detach) !== undefined || detach);
	switch (obj.properties.has_element.state.name) {
		case 'no': finalize_no__has_element__signed_number_property_list(obj.properties.has_element.state.node, detach); break;
		case 'yes': finalize_yes__has_element__signed_number_property_list(obj.properties.has_element.state.node, detach); break;
	}
}
function finalize_dynamic(obj:Cdynamic, detach:boolean = false) {
	finalize_node_path(obj.properties.path, detach);
	assert((<(detach?:boolean) => application.Ctext__type__property>(obj.properties.text as any).resolve)(detach) !== undefined || detach);
}
function finalize_static(obj:Cstatic, detach:boolean = false) {
}
function finalize_singular_text_expression(obj:Csingular_text_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.context_node)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'dynamic': finalize_dynamic(obj.properties.type.state.node, detach); break;
		case 'static': finalize_static(obj.properties.type.state.node, detach); break;
	}
}
function finalize_no__has_element__singular_text_expression_list(obj:Cno__has_element__singular_text_expression_list, detach:boolean = false) {
}
function finalize_yes__has_element__singular_text_expression_list(obj:Cyes__has_element__singular_text_expression_list, detach:boolean = false) {
	finalize_singular_text_expression(obj.properties.singular_text_expression, detach);
	finalize_singular_text_expression_list(obj.properties.tail, detach);
}
function finalize_singular_text_expression_list(obj:Csingular_text_expression_list, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.context_node)(detach) !== undefined || detach);
	switch (obj.properties.has_element.state.name) {
		case 'no': finalize_no__has_element__singular_text_expression_list(obj.properties.has_element.state.node, detach); break;
		case 'yes': finalize_yes__has_element__singular_text_expression_list(obj.properties.has_element.state.node, detach); break;
	}
}
function finalize_concatenation(obj:Cconcatenation, detach:boolean = false) {
	finalize_singular_text_expression_list(obj.properties.parts, detach);
}
function finalize_singular(obj:Csingular, detach:boolean = false) {
	finalize_singular_text_expression(obj.properties.expression, detach);
}
function finalize_text_expression(obj:Ctext_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.context_node)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'concatenation': finalize_concatenation(obj.properties.type.state.node, detach); break;
		case 'singular': finalize_singular(obj.properties.type.state.node, detach); break;
	}
}
function finalize_number__type__properties__query(obj:Cnumber__type__properties__query, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnumber__type__property>(obj.properties.number as any).resolve)(detach) !== undefined || detach);
}
function finalize_state_group__type__properties__query(obj:Cstate_group__type__properties__query, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cstate_group>(obj.properties.state_group as any).resolve)(detach) !== undefined || detach);
}
function finalize_text__type__properties__query(obj:Ctext__type__properties__query, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ctext__type__property>(obj.properties.text as any).resolve)(detach) !== undefined || detach);
}
function finalize_properties__query(obj:Cproperties__query, detach:boolean = false) {
	finalize_node_path(obj.properties.path, detach);
	switch (obj.properties.type.state.name) {
		case 'number': finalize_number__type__properties__query(obj.properties.type.state.node, detach); break;
		case 'state group': finalize_state_group__type__properties__query(obj.properties.type.state.node, detach); break;
		case 'text': finalize_text__type__properties__query(obj.properties.type.state.node, detach); break;
	}
}
function finalize_query(obj:Cquery, detach:boolean = false) {
	finalize_collection_path(obj.properties.collection_path, detach);
	for (const [_key, entry] of obj.properties.properties) {
		finalize_properties__query(entry, detach);
	}
	if (!detach) {
	}
}
function finalize_acyclic_graph_list_query(obj:Cacyclic_graph_list_query, detach:boolean = false) {
	finalize_id_path__client_request(obj.properties.context_node, detach);
	finalize_entry_point_path(obj.properties.entry_points, detach);
	assert((<(detach?:boolean) => application.Cgraphs>(obj.properties.graph as any).resolve)(detach) !== undefined || detach);
	finalize_query(obj.properties.query, detach);
}
function finalize_collection__type__acyclic_graph_tree_query(obj:Ccollection__type__acyclic_graph_tree_query, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.inferences.collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cacyclic>(obj.properties.graph as any).resolve)(detach) !== undefined || detach);
}
function finalize_acyclic_graph_tree_query(obj:Cacyclic_graph_tree_query, detach:boolean = false) {
	finalize_id_path__client_request(obj.properties.context_node, detach);
	switch (obj.properties.type.state.name) {
		case 'collection': finalize_collection__type__acyclic_graph_tree_query(obj.properties.type.state.node, detach); break;
	}
}
function finalize_state_distribution(obj:Cstate_distribution, detach:boolean = false) {
	finalize_node_path(obj.properties.path, detach);
	assert((<(detach?:boolean) => application.Cstate_group>(obj.properties.state_group as any).resolve)(detach) !== undefined || detach);
}
function finalize_aggregates(obj:Caggregates, detach:boolean = false) {
	switch (obj.properties.type.state.name) {
		case 'state distribution': finalize_state_distribution(obj.properties.type.state.node, detach); break;
	}
}
function finalize_no__todo_filter(obj:Cno__todo_filter, detach:boolean = false) {
}
function finalize_yes__todo_filter(obj:Cyes__todo_filter, detach:boolean = false) {
	finalize_node_path(obj.properties.path, detach);
	assert((<(detach?:boolean) => application.Cyes__todo>obj.properties.path.inferences.has_todo)(detach) !== undefined || detach);
}
function finalize_in__operator__containment(obj:Cin__operator__containment, detach:boolean = false) {
}
function finalize_not_in__operator__containment(obj:Cnot_in__operator__containment, detach:boolean = false) {
}
function finalize_containment(obj:Ccontainment, detach:boolean = false) {
	switch (obj.properties.operator.state.name) {
		case 'in': finalize_in__operator__containment(obj.properties.operator.state.node, detach); break;
		case 'not in': finalize_not_in__operator__containment(obj.properties.operator.state.node, detach); break;
	}
	finalize_reference_filter_list(obj.properties.references, detach);
}
function finalize_exists(obj:Cexists, detach:boolean = false) {
}
function finalize_not_exists(obj:Cnot_exists, detach:boolean = false) {
}
function finalize_existence(obj:Cexistence, detach:boolean = false) {
	switch (obj.properties.operator.state.name) {
		case 'exists': finalize_exists(obj.properties.operator.state.node, detach); break;
		case 'not exists': finalize_not_exists(obj.properties.operator.state.node, detach); break;
	}
}
function finalize_node(obj:Cnode, detach:boolean = false) {
	switch (obj.properties.type.state.name) {
		case 'containment': finalize_containment(obj.properties.type.state.node, detach); break;
		case 'existence': finalize_existence(obj.properties.type.state.node, detach); break;
	}
}
function finalize_keys__collection(obj:Ckeys__collection, detach:boolean = false) {
}
function finalize_in__operator__collection(obj:Cin__operator__collection, detach:boolean = false) {
}
function finalize_not_in__operator__collection(obj:Cnot_in__operator__collection, detach:boolean = false) {
}
function finalize_collection__type__property__type__value_filters(obj:Ccollection__type__property__type__value_filters, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.inferences.collection_type)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.keys) {
		finalize_keys__collection(entry, detach);
	}
	if (!detach) {
		if (obj.properties.keys.size === 0) {
			throw new Error(`Collection cannot be empty!`);
		}
	}
	switch (obj.properties.operator.state.name) {
		case 'in': finalize_in__operator__collection(obj.properties.operator.state.node, detach); break;
		case 'not in': finalize_not_in__operator__collection(obj.properties.operator.state.node, detach); break;
	}
}
function finalize_number__type__property__type__value_filters(obj:Cnumber__type__property__type__value_filters, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnumber__type__property>obj.inferences.number_type)(detach) !== undefined || detach);
	finalize_number_filter_expression(obj.properties.expression, detach);
}
function finalize_states_to_include(obj:Cstates_to_include, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cstates__state_group>(obj.key as any).resolve)(detach) !== undefined || detach);
}
function finalize_state_group__type__property__type__value_filters(obj:Cstate_group__type__property__type__value_filters, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cstate_group>obj.inferences.state_group_type)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.states_to_include) {
		finalize_states_to_include(entry, detach);
	}
	if (!detach) {
		if (obj.properties.states_to_include.size === 0) {
			throw new Error(`Collection cannot be empty!`);
		}
	}
}
function finalize_text__type__property__type__value_filters(obj:Ctext__type__property__type__value_filters, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ctext__type__property>obj.inferences.text_type)(detach) !== undefined || detach);
	finalize_filter_expression(obj.properties.expression, detach);
}
function finalize_property__type__value_filters(obj:Cproperty__type__value_filters, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>(obj.properties.property as any).resolve)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'collection': finalize_collection__type__property__type__value_filters(obj.properties.type.state.node, detach); break;
		case 'number': finalize_number__type__property__type__value_filters(obj.properties.type.state.node, detach); break;
		case 'state group': finalize_state_group__type__property__type__value_filters(obj.properties.type.state.node, detach); break;
		case 'text': finalize_text__type__property__type__value_filters(obj.properties.type.state.node, detach); break;
	}
}
function finalize_value_filters(obj:Cvalue_filters, detach:boolean = false) {
	finalize_node_path(obj.properties.path, detach);
	switch (obj.properties.type.state.name) {
		case 'node': finalize_node(obj.properties.type.state.node, detach); break;
		case 'property': finalize_property__type__value_filters(obj.properties.type.state.node, detach); break;
	}
}
function finalize_filters(obj:Cfilters, detach:boolean = false) {
	switch (obj.properties.todo_filter.state.name) {
		case 'no': finalize_no__todo_filter(obj.properties.todo_filter.state.node, detach); break;
		case 'yes': finalize_yes__todo_filter(obj.properties.todo_filter.state.node, detach); break;
	}
	for (const [_key, entry] of obj.properties.value_filters) {
		finalize_value_filters(entry, detach);
	}
	if (!detach) {
	}
}
function finalize_no__select_entries(obj:Cno__select_entries, detach:boolean = false) {
}
function finalize_yes__select_entries(obj:Cyes__select_entries, detach:boolean = false) {
	finalize_node_query(obj.properties.properties, detach);
}
function finalize_collection_query(obj:Ccollection_query, detach:boolean = false) {
	for (const [_key, entry] of obj.properties.aggregates) {
		finalize_aggregates(entry, detach);
	}
	if (!detach) {
	}
	finalize_collection_query_path(obj.properties.collection_path, detach);
	finalize_id_path__client_request(obj.properties.context_node_path, detach);
	finalize_filters(obj.properties.filters, detach);
	switch (obj.properties.select_entries.state.name) {
		case 'no': finalize_no__select_entries(obj.properties.select_entries.state.node, detach); break;
		case 'yes': finalize_yes__select_entries(obj.properties.select_entries.state.node, detach); break;
	}
}
function finalize_command_execution(obj:Ccommand_execution, detach:boolean = false) {
	finalize_node_initialization(obj.properties.arguments, detach);
	assert((<(detach?:boolean) => application.Ccommand__type>(obj.properties.command as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cglobal>obj.properties.command.inferences.global_command)(detach) !== undefined || detach);
	finalize_id_path__client_request(obj.properties.context_node, detach);
}
function finalize_mutation(obj:Cmutation, detach:boolean = false) {
	finalize_id_path__client_request(obj.properties.context_node, detach);
	finalize_node_update(obj.properties.node_update, detach);
}
function finalize_password_mutation(obj:Cpassword_mutation, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cyes__has_password_authentication>obj.inferences.require_user_passwords)(detach) !== undefined || detach);
}
function finalize_password_reset(obj:Cpassword_reset, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cyes__has_password_authentication>obj.inferences.require_user_password)(detach) !== undefined || detach);
}
function finalize_ignore(obj:Cignore, detach:boolean = false) {
}
function finalize_include(obj:Cinclude, detach:boolean = false) {
}
function finalize_subscription(obj:Csubscription, detach:boolean = false) {
	finalize_id_path__client_request(obj.properties.context_node, detach);
	switch (obj.properties.mutation_permissions.state.name) {
		case 'ignore': finalize_ignore(obj.properties.mutation_permissions.state.node, detach); break;
		case 'include': finalize_include(obj.properties.mutation_permissions.state.node, detach); break;
	}
	finalize_node_subscription(obj.properties.node_subscription, detach);
}
function finalize_subscription_deletion(obj:Csubscription_deletion, detach:boolean = false) {
}
function finalize_client_request(obj:Cclient_request, detach:boolean = false) {
	switch (obj.properties.type.state.name) {
		case 'acyclic graph list query': finalize_acyclic_graph_list_query(obj.properties.type.state.node, detach); break;
		case 'acyclic graph tree query': finalize_acyclic_graph_tree_query(obj.properties.type.state.node, detach); break;
		case 'collection query': finalize_collection_query(obj.properties.type.state.node, detach); break;
		case 'command execution': finalize_command_execution(obj.properties.type.state.node, detach); break;
		case 'mutation': finalize_mutation(obj.properties.type.state.node, detach); break;
		case 'password mutation': finalize_password_mutation(obj.properties.type.state.node, detach); break;
		case 'password reset': finalize_password_reset(obj.properties.type.state.node, detach); break;
		case 'subscription': finalize_subscription(obj.properties.type.state.node, detach); break;
		case 'subscription deletion': finalize_subscription_deletion(obj.properties.type.state.node, detach); break;
	}
}

export namespace Cclient_request {
	export function create(init:Tclient_request, input: {
	'application':application.Capplication}, symbols_file?: SymbolsFile):Cclient_request {
		const instance = new Cclient_request(init, input as any, symbols_file);
		return instance;
	};
}
